'use client';

/**
 * Widget iframe bridge — host-side terminator for the postMessage
 * envelope defined in `lib/workspaces/widget-bridge-protocol.ts`.
 *
 * Mounted inside `ContentNode` whenever a content row resolves to an
 * iframe-kind widget (via `lib/workspaces/widget-registry.ts`). Owns:
 *
 *   - the sandboxed `<iframe>` itself (CSP'd at the response-header
 *     layer; we use `sandbox="allow-scripts"` here so the widget can
 *     run JS but cannot reach into the host's localStorage / cookies)
 *   - the postMessage listener, with `event.source === iframe.contentWindow`
 *     identity check (the only meaningful boundary because sandbox
 *     widgets surface `event.origin === 'null'`)
 *   - the dispatch table for kv.* + widget.event ops (widget-author
 *     facing utilities — NOT agent-facing surfaces)
 *   - a `workspace.bus` subscription so KV writes from other tabs
 *     (or other workspace members) stream into the widget as
 *     `kv.subscribe` push frames in real time
 *   - registration with the per-workspace `WidgetCommandBus` so
 *     agents can drive this iframe through `workspace.widgets.*` —
 *     the bridge translates bus invokes into `cmd` postMessage
 *     frames and joins on the SDK's `cmd.result` reply.
 *
 * The bridge teardown is exhaustive on unmount: every pending op is
 * abandoned, every active subscription is cleared, the bus
 * registration is dropped, and the `workspace.bus` handler is
 * removed. Replaying / hot-reloading the widget is a remount, not a
 * "soft reset" — this matches how the factory function runtime
 * treats its own ctx lifecycle.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ATTRIBUTION_SIGNALS_MAX,
  ATTRIBUTION_SIGNALS_SOURCE,
  isAttributionSignalsFrame,
  isWidgetRequest,
  OpRegistry,
  type AttributionSignal,
  type WidgetResponse,
} from '@/lib/workspaces/widget-bridge-protocol';
import { tracker } from '@/lib/tracking';
import {
  validateKvGetArgs,
  validateKvSetArgs,
  validateKvDeleteArgs,
  validateKvListArgs,
  validateKvSubscribeArgs,
  validateKvUnsubscribeArgs,
  validateKvIncrArgs,
  kvRefusal,
} from '@/lib/workspaces/widget-ops/kv';
import { isUuid as isUuidId, type AppKvScope } from '@/lib/widgets/app-kv-scope';
import { ensureGuestSession } from '@/lib/auth/guest-mint-on-demand';
import {
  validateExchangeCopyArgs,
  validateExchangeResolveArgs,
} from '@/lib/workspaces/widget-ops/exchange';
import {
  validateContentPutArgs,
  validateContentGetArgs,
  validateContentSignedUrlArgs,
  validateContentListArgs,
  validateContentDeleteArgs,
  validateContentPickArgs,
  validateContentReadArgs,
  validateContentExportArgs,
  validateSetShareCardArgs,
  validatePrintRequestArgs,
  validateShareRequestArgs,
  validateShareLinkArgs,
  validateLinksMintArgs,
} from '@/lib/workspaces/widget-ops/content';
import { shareLink as openShareSheet } from '@/lib/share';
import { awaitGuestMint } from '@/lib/auth/guest-mint-gate';
import { stickyCatalogScratchCode } from '@/lib/workspaces/catalog-scratch';
import {
  validateDocumentsSaveArgs,
  validateDocumentsRevisionsArgs,
  validateDocumentsGetRevisionArgs,
  validateDocumentsRestoreArgs,
  validateDocumentsRenameRevisionArgs,
  validateDocumentsInfoArgs,
  validateDocumentsReadArgs,
  validateDocumentsBindArgs,
  validateDocumentsSaveDialogArgs,
  resolveContentScope,
  isCapturableContentFolder,
} from '@/lib/workspaces/widget-ops/documents';
import { validateNotifyCandidatesArgs, validateNotifyMentionArgs } from '@/lib/workspaces/widget-ops/notify';
import {
  validateLivePublishArgs,
  validateLiveUnpublishArgs,
  validateLiveAttachArgs,
  validateLiveReleaseArgs,
  validateLiveResolveArgs,
  validateLiveHeartbeatArgs,
} from '@/lib/workspaces/widget-ops/live';
import { validateAgentRequestArgs, validateAgentEnsureArgs, registerAgentOps } from '@/lib/workspaces/widget-ops/agent';
import { validateUiSignInArgs, runUiSignIn, signedInForUiSignIn } from '@/lib/workspaces/widget-ops/auth';
import { ITEM_ARRIVAL_EVENT, handleItemArrival } from '@/lib/workspaces/arrival-push';
import { appEntryPath, captureFragmentTarget, clearFragment, fragmentHandedByArrival, paramsForWindow } from '@/lib/workspaces/fragment-target';
import {
  runInstallRequest,
  validateInstallRequestArgs,
  validateInstallStateArgs,
  type InstallSnapshot,
} from '@/lib/workspaces/widget-ops/install';
import {
  installRemote,
  installSelf,
  selfInstallSnapshot,
  subscribeSelfInstall,
} from '@/components/app-host/install-controller';
import { lookupAppHost } from '@/components/app-host/host-lookup';
import { detectPlatform } from '@/components/use-pwa-install';
import { isConsumptionOnlySession } from '@/lib/platform/twa-referrer';
import { addLocalWidgetGrant } from '@/lib/workspaces/widget-permissions-local';
import {
  validateFilesAvailableArgs,
  validateFilesRequestAccessArgs,
  validateFilesListArgs,
  filesListScope,
  validateFilesMkdirArgs,
  validateFilesRenameArgs,
  validateFilesMoveArgs,
  validateFilesCopyArgs,
  validateFilesDeleteArgs,
  validateFilesUploadArgs,
  validateFilesReadArgs,
  validateFilesDownloadArgs,
  validateFilesOpenArgs,
  FILES_READ_DEFAULT_MAX,
  type FilesEntry,
} from '@/lib/workspaces/widget-ops/files';
import { validateKitAvailableArgs, validateKitRequestAccessArgs, validateKitConnectArgs, validateKitReadArgs, validateKitPinsArgs, validateKitLoadArgs, validateKitForgetArgs } from '@/lib/workspaces/widget-ops/kit';
import { pageKitSession, kitOp } from '@/lib/hardware/kit-session';
import { setAppUnsaved, validateUiSetUnsavedArgs } from '@/lib/workspaces/app-unsaved';
import {
  validateLocalAvailableArgs,
  validateLocalPickArgs,
  validateLocalListArgs,
  validateLocalReadArgs,
  validateLocalWriteArgs,
  validateLocalMkdirArgs,
  validateLocalRenameArgs,
  validateLocalDeleteArgs,
  LOCAL_READ_DEFAULT_MAX,
  type LocalEntry,
} from '@/lib/workspaces/widget-ops/local';
import {
  validateCasOpenArgs,
  validateCasMountsArgs,
  validateCasMountArgs,
  validateCasUnmountArgs,
  validateCasUpdateMountArgs,
  validateCasEnsurePdkMountArgs,
  validateCasOpenMountArgs,
  validateCasEvidenceArgs,
  validateCasImportBundleArgs,
  validateCasPackArgs,
  validateCasHeadArgs,
  validateCasHasArgs,
  validateCasBlobArgs,
  validateCasPutArgs,
  validateCasCommitArgs,
  validateCasHistoryArgs,
} from '@/lib/workspaces/widget-ops/cas';
import { validateProjectOpenArgs } from '@/lib/workspaces/widget-ops/project';
import { trackUpload } from '@/lib/content/transfers';
import { CasHost } from '@/lib/casfs/host';
import { validateRedaConnectionsArgs, validateRedaRelayArgs, validateRedaSignWindowArgs, validateRedaSignatureArgs } from '@/lib/workspaces/widget-ops/reda';
import { CasError } from '@/lib/casfs/core';
import { validateFnInvokeArgs } from '@/lib/workspaces/widget-ops/fn';
import { validateDiscoverArgs } from '@/lib/workspaces/widget-ops/discover';
import { validateAgentIntentsArgs, validateAgentMineArgs, validateAgentMineTurnArgs, validateAgentRailArgs, validateNamespaceResolverArgs } from '@/lib/workspaces/widget-ops/agent-rail';
import {
  AskRelay,
  INBOX_DECISIONS_NEEDING_CONSENT,
  validateAgentAskArgs,
  validateAgentAskCardArgs,
  validateAgentAskThreadArgs,
  validateAgentAskUnwatchArgs,
  validateAgentInboxDecideArgs,
  validateAgentInboxDiscardDraftArgs,
  validateAgentInboxGetArgs,
  validateAgentInboxReplyArgs,
  validateAgentInboxSendDraftArgs,
  mailCheckReason,
  validateAgentMailCheckArgs,
  validateAgentMailSendArgs,
  validateNoArgs,
  type AskPushFrame,
} from '@/lib/workspaces/widget-ops/agent-inbox';
import { INBOX_CONSENT_EVENT, type InboxConsentRequest } from '@/components/workspace/widget-inbox-consent';
import { INBOX_APPS } from '@/lib/inbox/apps';
import { useUAMP } from '@/hooks/useUAMP';
import { readWorkspaceTabId } from '@/lib/workspaces/use-workspace-tab-id';
import { validateEmailBriefArgs } from '@/lib/workspaces/widget-ops/app-email';
import { validateRoomsConfigArgs } from '@/lib/workspaces/widget-ops/rooms';
import {
  parseWidgetMetaFlags,
  buildIframeAllowAttribute,
} from '@/lib/workspaces/widget-meta-flags';
import { COI_ALLOW_FEATURE } from '@/lib/widgets/sandbox-isolation';
import {
  allowFromGrant,
  isSensitiveFeature,
  readDeniedPermissions,
  readGrantedPermissions,
  readGrantedResources,
  type ResourceRef,
} from '@/lib/workspaces/widget-permissions';
import {
  useWorkspaceBus,
  type WorkspaceBusFrame,
} from '@/lib/workspaces/use-workspace-bus';
import { useWorkspaceItem, useWorkspace, useWorkspaceActions } from '@/components/workspace/store';
import { announceContentRenamed } from '@/components/workspace/item-label';
import { useAuth } from '@/components/auth-provider';
import type { WorkspaceItemDTO } from '@/lib/workspaces/types';
import type { WidgetSpec } from '@/lib/workspaces/widget-registry';
import { useRegisterWidget } from '@/lib/workspaces/widget-command-bus-context';
import {
  captureElement,
  hasCaptureSession,
  requestCaptureGrant,
  captureGrantDismissedForever,
} from '@/lib/workspaces/host-capture';
import { BusError } from '@/lib/workspaces/widget-command-bus';
import {
  appendWidgetLog,
  markWidgetFeatureRequested,
  setWidgetLoadState,
} from '@/lib/workspaces/widget-log-buffer';
import { makeFetchWithGuestMint } from '@/lib/api/fetch-with-guest-mint';

// ---------------------------------------------------------------------------
// Attribution relay + host-side eye-time beacon (project: per-use attribution).
//
// The SDK batches `host.attribution.*` hits and posts them as one-way
// `robutler-widget-attribution` frames; the bridge buffers them and relays to
// `/api/widgets/:id/attribution` on the VIEWER's session (the sandboxed iframe
// has an opaque origin and no cookies, so it cannot reach the route itself).
// Eye-time (`_view`) is accrued HOST-side from iframe visibility + document
// focus — no widget cooperation needed, and a widget-forged eyetime signal is
// dropped so the host measurement stays authoritative.
// ---------------------------------------------------------------------------

/** At most one relay POST per widget per this window (client-side politeness;
 * the server has its own per-viewer rate limits). */
const ATTR_MIN_POST_INTERVAL_MS = 5_000;
/** Eye-time is cut into buckets of at most this much accrued visible time. */
const EYETIME_BUCKET_MS = 15_000;
/** Dedicated eye-time flush cadence when no marker traffic drains the queue. */
const EYETIME_FLUSH_MS = 60_000;
const EYETIME_TICK_MS = 1_000;
/** Ignore sub-second bucket dust (also the SDK's own eyetime floor, historically). */
const EYETIME_MIN_BUCKET_MS = 1_000;

function attributionUuid(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}-${Math.random().toString(16).slice(2, 10)}`;
  }
}

/**
 * Ops that mutate widget-owned state — the trigger set for the one-shot
 * `first_meaningful_edit` analytics event. `widget.event` is the broadest
 * inbound message a widget emits on its own actions and is included, EXCEPT
 * `cmd.result` reply payloads (agent-driven command acknowledgements, not the
 * user editing).
 */
const MUTATING_WIDGET_OPS = new Set([
  'kv.set',
  'kv.incr',
  'kv.delete',
  'content.put',
  'content.delete',
  'documents.save',
  'documents.restore',
]);

function isMutatingWidgetOp(op: string, args: unknown): boolean {
  if (MUTATING_WIDGET_OPS.has(op)) return true;
  if (op !== 'widget.event') return false;
  const payload = (args as { payload?: unknown } | null | undefined)?.payload;
  const kind =
    payload && typeof payload === 'object' ? (payload as { kind?: unknown }).kind : undefined;
  return kind !== 'cmd.result';
}

export interface WidgetBridgeProps {
  /**
   * Iframe entry. For same-origin widgets this is a path under
   * `/widgets/` (e.g. `/widgets/calculator/index.html`); for
   * external (`iframe-external`) widgets it's a fully-qualified
   * URL pointing at an agent-authored / marketplace widget served
   * from another origin. The bridge uses the SAME sandbox in both
   * cases — `allow-scripts` only — because the postMessage source
   * identity check is what enforces the trust boundary, not origin.
   */
  src: string;
  /** Workspace this widget instance belongs to — drives `host.workspace`. */
  workspaceId: string;
  /** Workspace item id — addresses KV writes + scoped pubsub. */
  itemId: string;
  /** Optional iframe title for a11y / dev tools. */
  title?: string;
  /**
   * Optional CSP allowlist. Most widgets need nothing special; the
   * `browser-embed` widget passes its `frameSrc` / `connectSrc` here
   * so the bridge can extend the iframe sandbox attribute accordingly.
   * Unused today (CSP is applied at the response-header layer); kept
   * on the interface so we can plumb it through without an API
   * change once the per-widget CSP work lands.
   */
  csp?: { frameSrc?: string[]; connectSrc?: string[] };
  /**
   * True when `src` is an absolute URL pointing at a widget hosted
   * on another origin. Surfaces in audit logs and lets the bridge
   * apply differential telemetry if needed. Sandbox + protocol
   * surface stay identical — the SDK is the contract.
   */
  external?: boolean;
  /**
   * The resolved widget spec (kind + declared interface) so the
   * bridge can register this iframe with the per-workspace
   * `WidgetCommandBus`. The spec's `interface` declaration lists the
   * commands + events the iframe answers via the SDK's
   * `host.commands.handle` registry. When `interface` is absent, the
   * bridge still mounts the iframe (KV widgets that don't expose
   * commands) but does NOT register with the bus.
   */
  widgetSpec?: WidgetSpec;
  /**
   * The widget's TYPE id (`metadata.widgetType` on the content row, e.g.
   * `'crm'`). Passed by the mount, which resolved the spec from it — the
   * spec itself only keeps the render kind. Currently load-bearing for one
   * thing: key handoff is a VISIBLE app field now (the blind
   * `secrets.pasteRequest` rail was removed 2026-08-12 — an agent opens and
   * points via the app's own `connections_open`; the person pastes into a
   * field they can see), and per-instance consent is what stops a hostile
   * bundle from catching a paste into its own fn. Absent → the op refuses.
   */
  widgetType?: string;
  /**
   * The id of the APP ROW running in this frame: the item's own row for an
   * app mount, the open-with handler's row for a folder or document mount
   * (the same fallback `widgetType` takes). Every op that acts for "this app"
   * on the server sends it, and the server refuses unless it is the app's own
   * catalog row: a remix keeps the original's `widgetType`, so the type alone
   * let anyone's copy of an app act as the app (S-173, 2026-09-18).
   */
  appContentId?: string;
  /**
   * When this widget is an AGENT's UI (rendered by `AgentNode`), the host
   * agent's id. The bridge merges `{kind:'agent', id: connectedAgentId}` into
   * `ctx.grantedResources` so `host.agents.get(connectedAgentId)` resolves
   * without a separate `state.connectedResources` write. This only relaxes the
   * client-side UX gate — every `agent.*` op still runs as the viewer against a
   * same-origin route that authorizes ownership/participation server-side.
   */
  connectedAgentId?: string;
  /**
   * When this widget is an AGENT's UI, the agent node's CURRENT chat id. Rides
   * the synthesized agent grant ref as `chatId`, so the widget reads it as
   * `agent.currentChatId` (host.agents) and binds its session to that thread
   * (e.g. voice persists turns there). Folded into the iframe `key` so
   * switching the current chat REMOUNTS the widget onto the new thread.
   */
  connectedChatId?: string;
  /**
   * Ephemeral mount — the widget is hosted OUTSIDE a real workspace (e.g. the
   * standalone full-screen share viewer at `/w/[id]`), so there is no
   * persisted workspace/item to address. Item-scoped persistence ops
   * (`content.*`, `widget.event`) become local no-ops instead of hitting
   * `/api/workspaces/:id/items/:itemId/...` with synthetic ids (which would
   * 500 on the non-UUID workspace lookup and spam the console).
   *
   * `host.kv` is NO LONGER among them (2026-09-20). It is addressed by CONTENT
   * and owned by the viewer, so it works here exactly as it does on a canvas —
   * see the `kv.*` family below. Gating it on this flag is what made an app
   * opened from a share link accept edits and lose them silently.
   *
   * `host.fn` / `host.discover` / `host.live` / `host.agents` stay LIVE — they
   * run as the viewer against same-origin routes with their own auth, so
   * `public` content functions (leaderboard-top, eda-part-search) render and
   * owner-only / visitor functions degrade with their normal 401/403.
   */
  ephemeral?: boolean;
  /**
   * The REAL workspace + item this mount came from, when `workspaceId` /
   * `itemId` are synthetic. Set by the fullscreen path (`/w/<id>?ws=&item=`),
   * which mounts under a share-surface id but is looking at a canvas tile's
   * content.
   *
   * Their ONLY use is letting the app-KV route find the pre-2026-09-20
   * item-keyed rows for its dual-read, so a person who takes an app fullscreen
   * before ever writing on the canvas still sees their data. They go away with
   * the dual-read (`lib/widgets/app-kv.ts`).
   */
  legacyKvWorkspaceId?: string | null;
  legacyKvItemId?: string | null;
  /**
   * The shared content row's `contentType` for an ephemeral (`/w/`) mount.
   * Drives the collab room identity: a real project (folder/doc) joins its
   * canonical `<id>:MAIN` room, but a widget CATALOG row (`'widget'` — the app
   * wall / marketplace preview) must NOT — `<catalog>:MAIN` is one global room
   * shared by every visitor of that app, leaking whoever-edited-last's
   * document to the world. Catalog mounts get a private per-mount scratch
   * room instead (a preview is not a persistence surface).
   */
  shareContentType?: string | null;
  /**
   * App-specific typed-link slug the standalone view was opened through
   * (server-resolved from the share code, e.g. 'player'); null everywhere
   * else. Rides the `__ready__` push as `host.workspace.linkType`.
   */
  linkType?: string | null;
  /**
   * Called when the widget sets its deep-link via `host.workspace.setDeeplink(str)`
   * — the app's current view/room encoded as URL-hash params (e.g. `room=ABCD`).
   * The node composes it into the fullscreen `/w/<id>#…` link so maximizing (and
   * `host.shareLink`) restore the exact view. Optional; unused → no behaviour change.
   */
  onDeeplinkChange?: (deeplink: string | null) => void;
  /**
   * Called when the widget asks for the "Drive with" dialog via
   * `host.ui.driveWith({ task })`, with the app-written task (or null). The
   * app proposes the words; the person still picks the tool and sends. Omit
   * where there is no drive surface — the op then reports `not_available_here`
   * and the widget falls back to its own copy-the-prompt affordance.
   */
  onDriveWith?: (task: string | null) => void;
  /**
   * The named host or custom domain serving THIS page, null on the apex and
   * on a canvas (app-hosts plan E2, 2026-09-24). Decides what `install.*`
   * means: on a host the page IS the app and installs itself; elsewhere the
   * app's own host (looked up on demand and once per page, see
   * components/app-host/host-lookup.ts) is what gets installed, through the
   * Web Install API or a new tab.
   */
  appHost?: string | null;
}

/** The host document's CURRENT resolved theme (portal uses class-based dark
 *  mode: `.dark` on `<html>`). Surfaced to widgets via the `__ready__` push
 *  (`host.workspace.theme`) and kept live via a `theme` broadcast push, so
 *  apps can match the portal chrome instead of the OS scheme. */
/**
 * `<iframe credentialless>` for third-party documents on the isolated canvas.
 * Not in React's iframe typings, so it is spread in as a plain record.
 */
const CREDENTIALLESS_ATTR: Record<string, string> = { credentialless: '' };

function hostTheme(): 'light' | 'dark' {
  return typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
    ? 'dark'
    : 'light';
}

/** A unique tag we put on every KV write originating from this tab so
 *  the workspace.bus subscriber can ignore the echo. Falsy strings
 *  would defeat the `originTag` filter; we use a per-mount UUID to be
 *  safe even when several widget instances for the same itemId render
 *  side-by-side (e.g. dock projection + canvas iframe). */
function newOriginTag(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `bridge-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** File System Access hands back a File whose `.type` is browser/OS-derived,
 *  and it is frequently BLANK for formats macOS does not register — most
 *  notably `.svg`, which then reads back as octet-stream and won't render in an
 *  <img>. Fill the gap from the extension for the media types a viewer cares
 *  about; anything unknown stays empty so the caller's own default applies. */
const LOCAL_EXT_MIME: Record<string, string> = {
  svg: 'image/svg+xml',
  avif: 'image/avif',
  webp: 'image/webp',
  bmp: 'image/bmp',
  ico: 'image/x-icon',
  heic: 'image/heic',
  heif: 'image/heif',
  flac: 'audio/flac',
  opus: 'audio/ogg',
  oga: 'audio/ogg',
  weba: 'audio/webm',
  mkv: 'video/x-matroska',
  m4v: 'video/mp4',
  mov: 'video/quicktime',
};
function guessLocalMime(name: string): string {
  const dot = name.lastIndexOf('.');
  if (dot < 0) return '';
  return LOCAL_EXT_MIME[name.slice(dot + 1).toLowerCase()] ?? '';
}

/**
 * Bound for an app-proposed "Drive with" task (`host.ui.driveWith`).
 *
 * Wide enough for a real setup brief — the CRM's runs past 800 characters
 * because it names the guide, the safe copy-and-paste rule and the check to
 * repeat after each step — while still keeping a runaway app from writing the
 * whole dialog.
 */
const DRIVE_TASK_MAX = 4000;

/**
 * Clamp a drive task to something a person can read, cutting on a WORD
 * boundary and marking the cut.
 *
 * A hard `slice()` ends the prompt mid-word ("…never read or screenshot a
 * key while it is on scr"), which reads as a broken app and — worse here —
 * can lop the tail off a safety instruction. Cutting at the last space and
 * appending an ellipsis keeps an over-long task honest about being trimmed.
 */
function clampDriveTask(raw: string): string {
  const s = raw.trim();
  if (s.length <= DRIVE_TASK_MAX) return s;
  const head = s.slice(0, DRIVE_TASK_MAX);
  const brk = Math.max(head.lastIndexOf(' '), head.lastIndexOf('\n'));
  return `${(brk > DRIVE_TASK_MAX * 0.8 ? head.slice(0, brk) : head).trimEnd()}…`;
}

export function WidgetBridge({
  src,
  workspaceId,
  itemId,
  title,
  external = false,
  widgetSpec,
  widgetType,
  appContentId,
  connectedAgentId,
  connectedChatId,
  ephemeral = false,
  legacyKvWorkspaceId = null,
  legacyKvItemId = null,
  shareContentType = null,
  linkType = null,
  onDeeplinkChange,
  onDriveWith,
  appHost = null,
}: WidgetBridgeProps) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  // [AUTOSAVE-2] ui.setUnsaved: this mount's key in the page's set. The flag dies with the frame (lib/workspaces/app-unsaved.ts).
  const unsavedInstance = useRef(`${workspaceId}:${itemId}:${Math.random().toString(36).slice(2)}`);
  useEffect(() => { const key = unsavedInstance.current; return () => setAppUnsaved(key, false); }, []);
  // The mount URL as last rendered, for handlers that must not re-subscribe
  // when it changes (a changed `src` re-keys the iframe anyway).
  const srcRef = useRef(src);
  srcRef.current = src;

  // THE SANDBOX GRANT (ADR-0044). The folder shell's own entry fetch was
  // refused, so it asks this host, which holds the viewer's session, to
  // obtain a grant from the apex and hand it back. The shell's message is
  // hostile input like any other: the folder it names must be the one THIS
  // mount points at, and the reply goes to the shell's exact origin, never
  // `*`, because it carries a token. A refusal is answered with `token: null`
  // so the shell shows its failure at once rather than waiting out a timer.
  const answerSandboxGrant = useCallback(async (iframe: HTMLIFrameElement, params: unknown) => {
    const asked = (params as { folderId?: unknown } | null)?.folderId;
    const mountFolder =
      /\/widget-sandbox\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\//i
        .exec(srcRef.current)?.[1]
        ?.toLowerCase() ?? null;
    if (typeof asked !== 'string' || !mountFolder || asked.toLowerCase() !== mountFolder) return;
    let shellOrigin: string;
    try {
      shellOrigin = new URL(srcRef.current, window.location.href).origin;
    } catch {
      return;
    }
    if (!shellOrigin || shellOrigin === 'null') return;
    let token: string | null = null;
    try {
      const res = await fetch('/api/sandbox/grant', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ folderId: mountFolder }),
      });
      if (res.ok) {
        const body = (await res.json()) as { token?: unknown };
        if (typeof body.token === 'string') token = body.token;
      }
    } catch {
      token = null;
    }
    iframe.contentWindow?.postMessage(
      {
        jsonrpc: '2.0',
        method: 'ui/notifications/sandbox-grant',
        params: { folderId: mountFolder, token },
      },
      shellOrigin,
    );
  }, []);
  // Latest deep-link the widget published (host.workspace.setDeeplink) — composed
  // into share/maximize links so fullscreen restores the app's current view.
  const deeplinkRef = useRef<string | null>(null);
  const originTag = useMemo(() => newOriginTag(), []);
  const item = useWorkspaceItem(itemId);
  // Persist the host.documents project-folder binding onto the Yjs item: the
  // documents save/bind routes no longer write `workspace_items.state` (canvas
  // state is client-owned now), so the client records `state.projectFolderId`
  // here so the binding survives reload.
  const { patchItemState, addItem } = useWorkspaceActions();
  // Stable content-row id for this widget instance — the room-key base for
  // `host.collab` `room` lobbies (collab-board / robongus). Share path: the
  // itemId is the synthetic `share-<contentId>`. Canvas path: the workspace
  // item's `refId` is the content row. `null` when neither resolves (e.g. a
  // freshly-spawned temp instance) — collab widgets simply wait for it.
  const contentIdRef = useRef<string | null>(null);
  // One ask per mount after a "not now": an app cannot re-open the email
  // dialog in a loop until the person gives in.
  const emailDeclinedRef = useRef(false);
  // INSTALL (app-hosts plan E2, 2026-09-24). What `host.install` answers,
  // pushed in the `__ready__` context and re-pushed when it changes. On a
  // host it is the page's own install state (the controller reads the
  // captured prompt, the Web Install API and `display-mode` of the TOP
  // window, which an app must never read itself because inside the frame
  // it mirrors the host). On the apex or a canvas the page is Robutler, so
  // the state is `installable` exactly when the app has a host of its own
  // (`remoteHostRef`, resolved ON DEMAND by the install ops, never at
  // mount), `unavailable` when it has none, and `unknown` until asked.
  // `installRequestedAtRef` is the per-mount latch the ops module reads.
  const installRef = useRef<InstallSnapshot>({ state: 'unavailable', platform: 'other' });
  const remoteHostRef = useRef<string | null>(null);
  const installRequestedAtRef = useRef<number | null>(null);
  // One sign-in navigation per mount (`ui.signIn`). The navigation unloads
  // this document but not instantly, so an app calling in a loop before unload
  // would otherwise get to march the person through a series of navigations.
  // See `runUiSignIn` for the rest of the reasoning.
  const signInRequestedRef = useRef(false);
  // A project folder bound at runtime via `host.documents.bind` (Open path).
  // The server persists `state.projectFolderId` for future mounts, but the
  // local `item.state` won't reflect it until a refetch — so we hold it here
  // and prefer it when computing `contentIdRef`, making the bind take effect
  // for `host.content` immediately (no reload needed). Cleared on unbind.
  const boundProjectRef = useRef<string | null>(null);
  // The lazily-minted `host.content` media folder for a NON-project widget. The
  // content route mints this on a fresh instance's first write and used to
  // persist it onto the server-side item row; in the unified model canvas state
  // is client-owned (Yjs), so the SERVER can't write it. Instead the bridge
  // captures the folder id from the PUT response, mirrors it to
  // `state.contentFolderId` (so it survives reload), and prefers it for a
  // subsequent op while the binding it was captured under still holds (P-20,
  // `contentFolderBindingRef` below), restoring per-instance media reuse for
  // media-before-save widgets. Adopted from item state below on (re)mount.
  const contentFolderRef = useRef<string | null>(null);
  // Media follows the binding (P-20, 2026-09-12): the binding this client
  // recorded `contentFolderRef` under. The captured folder is reused only
  // while that binding still holds, which is what keeps a tile's uploads with
  // the project it is bound to. Before this the memory outlived the binding
  // (`documents.bind`, a rebinding save and unbind never touched it), so every
  // later put, get, list and delete kept addressing the FIRST folder the tile
  // ever uploaded into: open A, upload, bind B, upload, and B's media landed
  // in A while B's list read A. Docs, vector, video and office all misfiled a
  // second project's uploads that way.
  //
  // A REF, and deliberately so. `state.contentFolderId` is SHARED workspace
  // state that every other open client sees and reacts to, so the first
  // attempt at this fix, which adopted, scrubbed, dropped and re-asserted that
  // key, put two tabs into an endless write loop on the most ordinary flow
  // there is (upload into a fresh tile, then save) and made a tab on the older
  // bundle re-assert forever whatever a newer tab removed. The rules live in
  // `lib/workspaces/widget-ops/documents.ts` (`resolveContentScope` /
  // `isCapturableContentFolder`) and they READ ONLY: nothing about a binding
  // change is ever written to shared state, and the pointer is never cleared.
  const contentFolderBindingRef = useRef<string | null>(null);
  // The PROJECT base id — the room-key base for `host.collab` AND the scope for
  // `host.content` (ADR-0022). It MUST be an access-checkable content id (the
  // collab token gate runs `canAccessContent` on it). For COLLAB / document
  // widgets that means the PROJECT FOLDER once it exists, else the (accessible)
  // widget catalog row:
  //   - share viewer (`share-<id>`)        → the shared content id (folder/doc);
  //   - this instance's own saved project  → `state.contentFolderId` (the folder);
  //   - a folder/doc opened onto canvas    → `refId` (a content row);
  //   - a fresh instance (refId = catalog) → the catalog row. The fresh room is
  //     a SCRATCH room; on first Save the instance rebinds to <folderId>:MAIN
  //     (so its doc + media never strand). `host.content` is scoped to the
  //     project folder regardless — the content route falls back to
  //     `state.contentFolderId` when `?project` isn't an owned folder.
  // Non-collab widgets keep the legacy `refId` base (per-instance content).
  const isProjectWidget = !!widgetSpec?.collab;
  // Read through a ref inside the op-registry memo: the registry is built once
  // per (workspace, item, origin, ...) and never re-keys on `widgetSpec`, which
  // the parent resolves after the first render, so a handler closing over the
  // bare boolean kept the mount-time `false` for the tile's whole life and a
  // runtime `documents.bind` did not move `contentIdRef` until the next render
  // (P-20).
  const isProjectWidgetRef = useRef(isProjectWidget);
  isProjectWidgetRef.current = isProjectWidget;
  contentIdRef.current = itemId.startsWith('share-')
    ? itemId.slice('share-'.length)
    : isProjectWidget && (boundProjectRef.current || item?.state?.projectFolderId)
      ? (boundProjectRef.current || item!.state!.projectFolderId!)
      : item?.refId && !item.refId.startsWith('inline:')
        ? item.refId
        : null;
  // The item's own `refId`, for the fragment hand-over below (S-378): a
  // project-bound window's `contentIdRef` is its folder, so the row the
  // window was spawned from is kept apart.
  const itemRefIdRef = useRef<string | null>(null);
  itemRefIdRef.current = typeof item?.refId === 'string' ? item.refId : null;
  // (The persisted media folder is adopted further down, where
  // `projectFolderIdNow` exists: adoption records the binding in force with
  // it, which is what P-20 decides on, and it decides on nothing else.)
  // Re-assert for the PROJECT binding. `documents.save` / `documents.bind`
  // patch `state.projectFolderId` the moment they resolve — on a fresh spawn
  // that can be INSIDE addItem's resolve-ref window, where the patch reaches
  // local state only (the item's Y map does not exist yet) and addItem's
  // final write then replaces local state AND the doc with the state it
  // captured at spawn, binding gone. Nothing retried it, so the doc — and the
  // server snapshot every fn call resolves `_folderId` from — stayed unbound
  // until the next save. Idempotent like the one above. Only an ABSENT
  // binding is re-asserted: a different folder in state is another tab's
  // Save-As, and fighting it would ping-pong.
  const stateProjectId = item?.state?.projectFolderId ?? null;
  useEffect(() => {
    if (ephemeral) return;
    if (boundProjectRef.current && item?.state && !stateProjectId) {
      patchItemState(itemId, { projectFolderId: boundProjectRef.current });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateProjectId, itemId, ephemeral]);

  // The room CODE under `contentId` (ADR-0022). A collab widget joins
  // `<contentId>:<roomCode>`. A FRESH canvas instance (refId is the widget
  // catalog — `cache.contentType === 'widget'` — with no project folder yet)
  // gets a UNIQUE per-instance scratch code derived from its item id, so two
  // fresh drafts never collide on the shared `<catalog>:MAIN` room. A saved /
  // opened / shared project (folder bound, opened folder, or `/w/` share) uses
  // the canonical `'MAIN'`. The collab gate `canAccessContent`s `contentId`
  // (the accessible catalog/folder), never the code — so the code is free to
  // make the room private without a per-instance content row.
  const cacheContentType = (item?.state?.cache as { contentType?: string } | undefined)?.contentType;
  const isFreshScratch =
    isProjectWidget &&
    !itemId.startsWith('share-') &&
    !item?.state?.projectFolderId &&
    cacheContentType === 'widget';
  // A `/w/` mount of a widget CATALOG row (not a project folder/doc): joining
  // `<catalog>:MAIN` would put every visitor of the app's preview in ONE global
  // room — whoever edited last leaks their document to the world. Mint a
  // private random scratch code instead — STICKY per browser (localStorage),
  // so a refresh rejoins the same room and the visitor's typed draft comes
  // back. A fresh code per MOUNT stranded the draft in the previous room on
  // every reload (live-reported 2026-08-27). Still never shared across
  // visitors; saving the project remains the durable path.
  const isShareCatalogScratch = itemId.startsWith('share-') && shareContentType === 'widget';
  const shareScratchCodeRef = useRef<string>('');
  if (isShareCatalogScratch && !shareScratchCodeRef.current) {
    shareScratchCodeRef.current = stickyCatalogScratchCode(contentIdRef.current ?? itemId);
  }
  // What this mount can actually SHARE. `contentIdRef` is the room base, which
  // for an UNSAVED project draft is the app's CATALOG row — sharing that hands
  // out a link to the app, not the work: recipients open a blank editor (and
  // logged-out ones fail outright, since a `/w/<catalog>` mount joins a
  // login-required scratch room). A draft's document lives in a private
  // per-instance room that no link can address, so the honest answer is null
  // until the project is saved.
  const shareableContentRef = useRef<string | null>(null);
  shareableContentRef.current =
    isProjectWidget && (isFreshScratch || isShareCatalogScratch)
      ? null
      : contentIdRef.current;
  const roomCodeRef = useRef<string>('MAIN');
  roomCodeRef.current = isFreshScratch
    ? (itemId.replace(/[^a-z0-9]/gi, '').toUpperCase().slice(0, 12) || 'MAIN')
    : isShareCatalogScratch
      ? shareScratchCodeRef.current
      : 'MAIN';
  // Workspace owner id, mirrored into a ref so the stable postMessage
  // handler can compute a REAL `isOwner` in the __ready__ push without
  // adding the workspace object to its dep array.
  const workspaceOwnerId = useWorkspace()?.ownerId ?? null;
  const ownerIdRef = useRef<string | null>(workspaceOwnerId);
  ownerIdRef.current = workspaceOwnerId;
  // The widget TYPE id, ref-synced each render so the `rooms.config` handler
  // (registered once in the op-registry memo, whose deps do not include the
  // prop) always stamps the CURRENT app id (host.rooms, ADR-0030).
  const widgetTypeRef = useRef<string | undefined>(widgetType);
  widgetTypeRef.current = widgetType;
  // The app row running in this frame (see the prop), for every op whose
  // server route must know WHICH row this is, not just its type (S-173).
  const appContentIdRef = useRef<string | undefined>(appContentId);
  appContentIdRef.current = appContentId;
  // Memoized rooms endpoints for this mount (`rooms.config`): the config
  // route is public and static per deployment, so one same-origin fetch per
  // bridge instance is plenty. Reset to null on failure so the next call
  // retries instead of pinning "multiplayer unavailable" for the mount.
  const roomsEndpointsRef = useRef<Promise<{ url: string | null; ops: string | null }> | null>(null);

  /**
   * Current-user identity surfaced to widgets via the `__ready__` push
   * payload. Backs `host.user.current()` (sdk.v2). Resolved via the
   * portal session cookie — anon visitors see `null`.
   *
   * Derived from AuthProvider (which fetched the session ONCE for the
   * whole page) instead of a per-bridge `/api/users/me` fetch — the
   * per-bridge fetch was 8 identical requests per workspace open, 18%
   * of all authed traffic. Widgets that boot before auth resolves see
   * the same small race as before (the SDK awaits `host.ready()`).
   */
  const { user: authUser } = useAuth();
  const currentUser = useMemo(() => {
    if (!authUser) return null;
    // Color is derived from a stable hash so the same user always
    // gets the same accent across pods + page reloads. Bridge-side
    // because this surface ships in the SDK contract; the SDK
    // does not have the user id at boot.
    const idStr = String(authUser.id ?? '');
    let h = 0;
    for (let i = 0; i < idStr.length; i++) h = (h * 31 + idStr.charCodeAt(i)) | 0;
    return {
      userId: idStr,
      username: authUser.username ?? null,
      // The person's name as they show it on Robutler (2026-09-19): an app
      // that names a person to somebody else (Revent's registrations, a
      // conference's "by") showed a handle like "ana178976091" without it.
      displayName: authUser.displayName || null,
      avatarUrl: authUser.avatarUrl ?? null,
      color: `hsl(${Math.abs(h) % 360}, 70%, 55%)`,
    };
  }, [authUser]);
  // Ref mirror for handlers registered in effects whose dep arrays do NOT
  // include currentUser (the big op-registration effect) — reading the state
  // there captures the mount-time null forever (stale closure).
  const currentUserRef = useRef<typeof currentUser>(null);
  useEffect(() => {
    currentUserRef.current = currentUser;
  }, [currentUser]);

  /**
   * Community widgets DECLARE the browser features they want via a
   * `<meta robutler:widget allowMic ...>` tag. Under the per-widget
   * grant model (lib/workspaces/widget-permissions.ts) declaring is
   * NOT granting — these features feed the *request* surface (Part D3
   * / the Settings permissions list), not the live `allow=`. We fetch
   * the widget HTML once at mount and parse the meta so the host knows
   * which sensitive features this instance might ask for. Same-origin
   * only — third-party (`iframe-external`) widgets can't be parsed
   * cross-origin, so their requestable set defaults to the full
   * SENSITIVE_FEATURES list (resolved downstream in D3).
   */
  const [metaRequestedAllow, setMetaRequestedAllow] = useState<string>('');

  useEffect(() => {
    if (external) return;
    if (!src.startsWith('/')) return;
    // First-party (registry `allow`) widgets are pre-trusted; their
    // requestable set is `widgetSpec.allow`, no HTML parse needed.
    if (widgetSpec?.allow) return;
    let cancelled = false;
    fetch(src, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.text() : ''))
      .then((html) => {
        if (cancelled || !html) return;
        const flags = parseWidgetMetaFlags(html);
        const allow = buildIframeAllowAttribute(flags);
        if (allow) setMetaRequestedAllow(allow);
      })
      .catch(() => {
        /* meta is best-effort — anon visitors / network blip */
      });
    return () => {
      cancelled = true;
    };
  }, [src, external, widgetSpec?.allow]);

  /**
   * The live Permissions-Policy `allow=` attribute for THIS iframe:
   *   baseline ∪ trusted(first-party registry) ∪ (granted ∩ sensitive)
   *
   * `trusted` is ONLY the registry `widgetSpec.allow` (set on
   * platform/system widgets like voice-chat, which keep their mic
   * without a prompt). Community + external widgets contribute no
   * trusted features — their sensitive features come SOLELY from the
   * user grant persisted on `state.permissions`.
   *
   * Permissions-Policy is load-time: the attribute must be present when
   * the iframe loads, and changing it on a live frame has no effect. So
   * we derive a `grantSig` from the granted set and fold it into the
   * iframe `key` — a grant or revoke remounts the iframe with the new
   * policy, after which the widget can retry the feature.
   */
  const grantedPermissions = useMemo(
    () => readGrantedPermissions(item?.state),
    [item?.state],
  );
  // DEFAULT-ON capabilities the user revoked for this instance (`!feature`
  // entries in state.permissions) — currently gates notifications.mention.
  const deniedPermissions = useMemo(
    () => readDeniedPermissions(item?.state),
    [item?.state],
  );
  // Outbound resource grants (host.get / host.agents gate). Merge the
  // server-written `state.connectedResources` with the agent-UI mount's
  // `connectedAgentId` prop. Defence-in-depth only — server routes still
  // authorize the viewer.
  const grantedResources = useMemo<ResourceRef[]>(() => {
    const fromState = readGrantedResources(item?.state);
    if (
      connectedAgentId &&
      !fromState.some((r) => r.kind === 'agent' && r.id === connectedAgentId)
    ) {
      // Carry the agent node's current chat on the grant ref so the widget can
      // read it as agent.currentChatId (resource.list returns the full refs).
      return [
        ...fromState,
        { kind: 'agent', id: connectedAgentId, ...(connectedChatId ? { chatId: connectedChatId } : {}) },
      ];
    }
    return fromState;
  }, [item?.state, connectedAgentId, connectedChatId]);
  // The postMessage handler is a stable closure (it does not depend on
  // `item.state`), so mirror the latest grants into a ref it can read.
  const grantedResourcesRef = useRef<ResourceRef[]>(grantedResources);
  grantedResourcesRef.current = grantedResources;
  // Latest item state for read-modify-write grants (e.g. appending a project
  // agent to `state.agentControl`) from the stable postMessage closure.
  const itemStateRef = useRef(item?.state);
  itemStateRef.current = item?.state;
  // The project FOLDER this mount is bound to, as the host sees it RIGHT NOW:
  // a runtime bind/save (ref) beats the persisted item state, which lags it
  // (see the re-assert effect above). A share mount of a project folder is
  // bound to that folder by construction; a catalog or document share is not
  // a project. Pushed as `host.workspace.projectFolderId` so an app can tell
  // "bound, the server snapshot has not caught up" from "genuinely unbound":
  // the CRM latched its full-screen "not linked to a project" panel on
  // exactly that difference (prod, 2026-08-23).
  const projectFolderIdNow = (): string | null =>
    itemId.startsWith('share-')
      ? shareContentType === 'folder'
        ? itemId.slice('share-'.length)
        : null
      : boundProjectRef.current || itemStateRef.current?.projectFolderId || null;
  // Adopt a persisted media folder (e.g. after reload) without clobbering a
  // fresher value the `content.put` handler captured this session, and record
  // the binding in force AT ADOPTION beside it (P-20). That pairing is the
  // whole of rule 3 in `resolveContentScope`: a stored pointer, whose origin
  // this client cannot know, is used exactly while the binding it was found
  // under still holds, and the moment the tile is bound somewhere else the ops
  // follow the binding instead. Both writes here are to REFS. The pointer in
  // shared state is read, never rewritten and never scrubbed: a scrub is what
  // another open client, or a tab on the older bundle, answers by writing it
  // straight back.
  const stateFolderId = item?.state?.contentFolderId ?? null;
  if (!contentFolderRef.current && stateFolderId) {
    contentFolderRef.current = stateFolderId;
    contentFolderBindingRef.current = projectFolderIdNow();
  }
  // Re-assert the pointer when the ref holds a folder the item state lost: the
  // `patchItemState` in the `content.put` handler no-ops silently if the
  // item's Y map wasn't in the doc yet (fresh spawn racing the first upload).
  // Idempotent, since `patchItemState` diffs against the doc, so once the
  // value lands this never writes again.
  //
  // NARROWED to the folder this mount would itself address right now (P-20).
  // Re-asserting a pointer the tile has moved away from is one half of the
  // ping-pong (the other half being a client that removes it), and a pointer
  // equal to the binding is already addressed by the binding, so neither is
  // worth a shared write. That leaves one invariant covering both write sites
  // in this file: the bridge only ever writes `state.contentFolderId` with the
  // folder its very next content op would use, and it never clears the key.
  useEffect(() => {
    if (ephemeral) return;
    const captured = contentFolderRef.current;
    if (!captured || !item?.state || stateFolderId) return;
    const boundNow = projectFolderIdNow();
    if (captured === boundNow || contentFolderBindingRef.current !== boundNow) return;
    patchItemState(itemId, { contentFolderId: captured });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateFolderId, itemId, ephemeral]);
  // ONE builder for the `__ready__` workspace-context push (what the SDK
  // exposes as `host.workspace`): the handshake reply, the proactive on-load
  // push and the re-push after a runtime bind/save all send this. Ref-backed
  // so every site reads the values of the LATEST render, never the ones an
  // effect closed over.
  const buildReadyPushRef = useRef<() => Record<string, unknown>>(() => ({}));
  // THE FRAGMENT GOES TO THE APP THE LINK NAMED (S-378, 2026-10-01). When the
  // page was opened by a link that names an app (`/?app=<id>#...`, which the
  // splash turns into `/workspace?spawn=content&contentId=<row>&resume=1`),
  // the hash is handed to the windows running THAT app and to no other
  // window on the canvas, and it is taken off the address bar on the first
  // hand-over (kept in memory for this app's later context pushes). A page
  // whose link names no app, `/w/<id>#...` included, hands every window the
  // live hash exactly as before, so `#eda=` and `#room=` keep working; the
  // wider S-200 question (which apps may read `params` at all) stays open.
  // lib/workspaces/fragment-target.ts holds the rules; the deep-link
  // consumer captures the target before it strips the query, and the
  // capture here is the belt to that brace.
  const fragmentIds = () => [appContentIdRef.current, contentIdRef.current, itemRefIdRef.current];
  // The two fields of the push that come from the page's fragment, from ONE
  // hand-over per push so `deeplink` and `params` never disagree. Spread into
  // the builder below, which stays an object literal: several tests slice it
  // out of this file by that shape.
  const fragmentFields = (): { deeplink: string | null; params: Record<string, string> } => {
    if (typeof window === 'undefined') return { deeplink: null, params: {} };
    captureFragmentTarget(window.location.search);
    const handed = paramsForWindow(fragmentIds(), window.location.hash);
    if (handed.clearHash) clearFragment(window);
    return { deeplink: handed.params.eda || null, params: handed.params };
  };
  buildReadyPushRef.current = () => ({
    workspaceId,
    itemId,
    contentId: contentIdRef.current,
    // The APP row running in this window (the S-173 `appContentId`): the app
    // itself, the app a document or folder opens with, or the copy a person
    // runs. `contentId` is the window's CONTENT and becomes the project folder
    // once the window is bound to one, so an app that built an address to
    // itself from it (`/api/app-hooks/<appId>/...`) handed out a folder id the
    // hooks route answers 404 for (2026-09-18, Revent's desk address).
    appId: appContentIdRef.current ?? null,
    projectFolderId: projectFolderIdNow(),
    roomCode: roomCodeRef.current,
    // "Open in <app>" on a raw file (import-role handler): the FILE this
    // fresh scratch instance should pull into its new project, once. The
    // app fetches `/api/content/<id>` itself — the content API enforces
    // access; this is a hint, not a grant.
    importContentId: itemStateRef.current?.importContentId ?? null,
    sdkVersion: 'v2',
    // Portal chrome theme (class-based dark mode on <html>) so widgets can
    // match the app instead of the OS scheme. Kept live via the `theme`
    // broadcast push below.
    theme: hostTheme(),
    // Real app origin — widgets build share links from this because
    // `location.origin` is the opaque string 'null' inside the cross-origin
    // sandbox (so links became "null/w/<id>").
    appUrl: typeof window !== 'undefined' ? window.location.origin : '',
    // Deeplink hash-forward: `<app>/w/<id>#eda=<entityId>` opens the workspace and the host hands the
    // `eda=` param to the widget (the sandboxed iframe can't read the parent hash itself). Widgets that
    // understand a deeplink navigate to the target; the rest ignore it. Read from the same hand-over
    // as `params`, so a link that names another app carries no `eda` into this window either.
    // Generalized deeplink (`params`): the parsed URL hash, so an app can restore its
    // view/room from `<app>/w/<id>#room=ABCD&view=x` (the sandbox can't read the
    // parent hash). Additive — apps that ignore `host.workspace.params` are unaffected.
    // On a page opened by a link that names an app, only that app's windows
    // receive either field (`fragmentFields` above, S-378).
    ...fragmentFields(),
    user: currentUser,
    // Real workspace ownership (was hardcoded false). UX hint only — never a
    // security boundary; server routes authorize the viewer independently
    // (see the `vector-stats` `session` gate).
    isOwner: !!currentUser && !!ownerIdRef.current && currentUser.userId === ownerIdRef.current,
    // Typed-link slug this share view was opened through (server-resolved
    // from the share code; NOT a URL param, so it can't be spoofed by editing
    // the address). Apps open a type-specific surface from it — e.g. 'player'
    // boots a deck straight into the presentation player. Null in workspaces
    // + default links.
    linkType: linkType ?? null,
    // Whether, and how, this app can be installed on the device (E2). Apps
    // read this instead of `display-mode`, which inside a frame answers for
    // the top window; a `{ kind: 'install' }` broadcast follows a change.
    install: { ...installRef.current },
    // A host loaded from its Android package is consumption-only (plan G4):
    // Google Play bars pointing people at another payment method from inside
    // an app it distributes, so an app hides its own credit calls to action
    // while this is true. Platform chrome on the host page does the same.
    consumptionOnly: isConsumptionOnlySession(),
  });
  // Re-hand the widget its workspace context after a runtime bind/save, so
  // `host.workspace.projectFolderId` (and `contentId`) read the new binding
  // without a reload. The SDK's `__ready__` subscription simply replaces its
  // context; a widget that already booted is unaffected unless it looks.
  const pushWorkspaceContext = () => {
    iframeRef.current?.contentWindow?.postMessage(
      { id: '__ready__', push: buildReadyPushRef.current() },
      '*',
    );
  };
  // Keep `install` current (E2). ON A HOST the controller's subscription
  // fires when the browser hands over its prompt, spends it, or reports
  // `appinstalled`: the context is re-pushed each time, and an install is
  // broadcast as `{ kind: 'install' }` so an app that read the context once
  // can look again. OFF A HOST NOTHING IS FETCHED AT MOUNT (functional
  // review, 2026-09-24): the app's own host used to be looked up on every
  // mount, canvas tiles included, and nearly every app has none, so every
  // person's console carried a red "Failed to load resource" line per app
  // and the platform one request per mount. The context now says
  // `unknown`, and `resolveRemoteInstallRef` below resolves the host on
  // demand, when an app calls `install.state` or `install.request`, through
  // the per-page memo in components/app-host/host-lookup.ts. Read through
  // refs so the handlers below, memoised with the registry, see the latest
  // values.
  const appHostRef = useRef<string | null>(appHost);
  appHostRef.current = appHost;
  const resolveRemoteInstallRef = useRef<() => Promise<InstallSnapshot>>(async () => ({ ...installRef.current }));
  resolveRemoteInstallRef.current = async () => {
    const platform = detectPlatform();
    const target = appContentIdRef.current ?? contentIdRef.current;
    if (!target) {
      installRef.current = { state: 'unavailable', platform };
      return { ...installRef.current };
    }
    try {
      const found = await lookupAppHost(target);
      remoteHostRef.current = found?.origin ?? null;
      installRef.current = { state: found ? 'installable' : 'unavailable', platform };
    } catch {
      // Offline or a failed lookup: `unavailable` for this answer only. The
      // memo forgets a failure, so the next ask tries again.
      remoteHostRef.current = null;
      installRef.current = { state: 'unavailable', platform };
    }
    if (sdkReadyRef.current) pushWorkspaceContext();
    return { ...installRef.current };
  };
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (appHost) {
      const refresh = (announce: boolean) => {
        const next = selfInstallSnapshot();
        const changed = next.state !== installRef.current.state;
        installRef.current = next;
        if (!changed || !announce || !sdkReadyRef.current) return;
        pushWorkspaceContext();
        if (next.state === 'installed') {
          iframeRef.current?.contentWindow?.postMessage({ id: '__broadcast__', push: { kind: 'install' } }, '*');
        }
      };
      refresh(false);
      return subscribeSelfInstall(() => refresh(true));
    }
    // Off a host: not resolved, and no request made. See the note above.
    installRef.current = { state: 'unknown', platform: detectPlatform() };
    remoteHostRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appHost]);
  const allowValue = useMemo(() => {
    const granted = allowFromGrant({ granted: grantedPermissions, trusted: widgetSpec?.allow });
    // [S-651] (2026-11-04) `usb` has the default allowlist `self`, so where the sandbox shares the page's origin (no SANDBOX_ORIGIN: the local
    // cluster) a frame could call navigator.usb with no grant at all. ADR-0066 says the frame never holds it, and this makes that true
    // whatever the origins are. A person's explicit grant (S-647) still reads `usb *` in `base`.
    const usbOff = /(^|;\s*)usb\b/.test(granted) ? '' : "; usb 'none'";
    const base = granted + usbOff;
    // ISOLATION (ADR-0044). Every page that hosts apps is cross-origin
    // isolated, and `crossOriginIsolated` inside a CROSS-ORIGIN frame (every
    // app on cloud, on its own origin) is gated by a Permissions-Policy
    // feature whose default allowlist is `self`. Without this delegation the
    // app loads (it sends COEP) but reports `false` and has no
    // SharedArrayBuffer, measured. Delegated on every mount: a frame cannot
    // create its own isolation and cannot opt out, so this only reveals the
    // page's own state, and every app-hosting page is isolated.
    return `${base}; ${COI_ALLOW_FEATURE} *`;
  }, [grantedPermissions, widgetSpec?.allow]);
  const grantSig = useMemo(
    () => grantedPermissions.slice().sort().join(','),
    [grantedPermissions],
  );

  /**
   * A widget can ASK for a sensitive feature two ways: explicitly via
   * `host.requestPermission(feature)`, or implicitly by tripping a
   * Permissions-Policy violation (captured by the log forwarder, which
   * tags the frame with `featureId`). Both funnel here. We POST a
   * request that raises a `widget_permission_request` notification for
   * the workspace owner — only the USER grants. Deduped per feature
   * within this mount (the server also dedupes on the unread
   * notification) so a widget violating every frame can't spam the
   * bell. The dedup set resets on `grantSig` change so a revoked
   * feature can be re-requested after reload.
   */
  const requestedFeaturesRef = useRef<Set<string>>(new Set());
  // user-library ids the user explicitly picked via content.pick — the grant
  // set that gates content.read (the widget can never read arbitrary ids)
  const grantedContent = useRef<Set<string>>(new Set());
  // Display name of a folder-SCOPED files grant root (fetched lazily once
  // for the widget's crumb label; reset when the grant signature changes).
  const filesScopeNameRef = useRef<string | null>(null);
  useEffect(() => {
    filesScopeNameRef.current = null;
  }, [grantSig]);
  // `host.local` mounts: mountId → the picked FileSystemDirectoryHandle. Held
  // PER WIDGET INSTANCE (this bridge) and PER SESSION — never persisted, so a
  // reload drops them and the user re-picks. Confinement is structural: a
  // widget can only ever act on a handle it minted via the native picker, and
  // paths are name-segment arrays walked with getDirectoryHandle/getFileHandle
  // (no `..`, no separators → can't escape the picked folder).
  const localMountsRef = useRef<Map<string, FileSystemDirectoryHandle>>(new Map());
  // `host.cas` (ADR 0063): this instance's own host service. Handles it mints
  // live here and nowhere else, so a handle from another app instance is
  // unknown to this one by construction.
  const casHostRef = useRef<CasHost | null>(null);
  useEffect(() => {
    const mounts = localMountsRef.current;
    return () => mounts.clear();
  }, []);
  useEffect(() => {
    requestedFeaturesRef.current = new Set();
  }, [grantSig]);

  const requestFeaturePermission = useCallback(
    (feature: string) => {
      if (!feature || !isSensitiveFeature(feature)) return;
      // Already granted → nothing to ask for (a still-firing violation
      // means the PORTAL origin lacks the feature, a D6 concern, not a
      // grant gap).
      if (grantedPermissions.includes(feature)) return;
      if (requestedFeaturesRef.current.has(feature)) return;
      requestedFeaturesRef.current.add(feature);
      // Surface the header shield until the user grants it.
      markWidgetFeatureRequested(itemId, feature);
      void fetch(
        `/api/workspaces/${workspaceId}/items/${itemId}/permission-request`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ feature, widgetName: title ?? undefined }),
        },
      ).catch(() => {
        // Best-effort — let a later violation retry.
        requestedFeaturesRef.current.delete(feature);
      });
    },
    [workspaceId, itemId, title, grantedPermissions],
  );

  // Anon visitors that hit a KV write / widget.event POST get a
  // structured `signup_required` 401 from the server; the wrapped
  // fetch mints an ephemeral guest (cloning this workspace) and
  // navigates to the clone, so the write lands on their own canvas
  // instead of surfacing as a broken state. Reads (kv.get, kv.list)
  // pass through unchanged.
  const fetchGated = useMemo(() => makeFetchWithGuestMint(), []);

  // -----------------------------------------------------------------
  // Attribution relay state (see the module-level note). Plain fetch
  // (NOT fetchGated): attribution is a best-effort beacon and must
  // never trigger the guest-mint navigation flow.
  // -----------------------------------------------------------------
  const attrQueueRef = useRef<AttributionSignal[]>([]);
  const attrLastPostRef = useRef(0);
  const attrTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const postAttributionBatch = useCallback(
    (_unloading: boolean) => {
      const queue = attrQueueRef.current;
      if (queue.length === 0) return;
      attrQueueRef.current = [];
      attrLastPostRef.current = Date.now();
      const url = `/api/widgets/${encodeURIComponent(itemId)}/attribution`;
      const payload = JSON.stringify({ signals: queue });
      try {
        // `keepalive: true` survives page teardown the same way sendBeacon does,
        // and unlike sendBeacon it can carry the host-trust header the ingest
        // route requires for eye-time (security review 2026-07-28, finding 1).
        // The `_unloading` arg is retained by callers to mean "we are unloading"; the
        // transport is now the same either way.
        void fetch(url, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'content-type': 'application/json', 'x-robutler-attr-host': '1' },
          body: payload,
          keepalive: true,
        }).catch(() => {});
      } catch {
        /* best-effort raw layer — drop */
      }
    },
    [itemId],
  );

  /** Flush soon, but at most one POST per ATTR_MIN_POST_INTERVAL_MS per widget. */
  const scheduleAttributionFlush = useCallback(() => {
    if (attrTimerRef.current) return;
    const wait = Math.max(0, ATTR_MIN_POST_INTERVAL_MS - (Date.now() - attrLastPostRef.current));
    attrTimerRef.current = setTimeout(() => {
      attrTimerRef.current = null;
      postAttributionBatch(false);
    }, wait);
  }, [postAttributionBatch]);

  /** Buffer signals (drop-oldest at the request cap) and schedule a flush. */
  const enqueueAttributionSignals = useCallback(
    (signals: AttributionSignal[]) => {
      const queue = attrQueueRef.current;
      for (const s of signals) {
        // Widget-supplied eyetime is dropped — the HOST beacon below is the
        // only eyetime source, so a forked SDK can't double-count view time.
        if (s.kind === 'eyetime') continue;
        queue.push(s);
      }
      while (queue.length > ATTRIBUTION_SIGNALS_MAX) queue.shift();
      if (queue.length > 0) scheduleAttributionFlush();
    },
    [scheduleAttributionFlush],
  );

  // -----------------------------------------------------------------
  // One-shot `first_meaningful_edit` per widget per browser session —
  // fired the first time the bridge observes a state-mutating widget
  // message (see MUTATING_WIDGET_OPS). Rides the same client tracker
  // as the splash funnel events.
  // -----------------------------------------------------------------
  const fmeFiredRef = useRef(false);
  const trackFirstMeaningfulEdit = useCallback(
    (op: string, args: unknown) => {
      if (fmeFiredRef.current || !isMutatingWidgetOp(op, args)) return;
      fmeFiredRef.current = true;
      const storageKey = `rb:fme:${itemId}`;
      try {
        if (sessionStorage.getItem(storageKey)) return;
        sessionStorage.setItem(storageKey, '1');
      } catch {
        /* storage unavailable — the per-mount ref still dedupes */
      }
      // The cast bridges the tracker's client-side actionType union until
      // 'first_meaningful_edit' is added to it (and to /api/track's
      // DB_ACTION_TYPES allowlist) — both live outside this file.
      tracker?.track({
        targetType: 'journey',
        targetId: itemId,
        actionType: 'first_meaningful_edit',
        metadata: { source: 'widget-bridge', op, external: !!external },
      } as unknown as Parameters<NonNullable<typeof tracker>['track']>[0]);
    },
    [itemId, external],
  );

  // -----------------------------------------------------------------
  // Host-side eye-time beacon: accrue time while the iframe is >= 50%
  // visible AND the document (or the iframe inside it) holds focus AND
  // the tab is visible. Cut into 15s buckets, ride the shared signal
  // queue, and force a flush at most once per minute when no marker
  // traffic drains it sooner. Final bucket + sendBeacon on hide,
  // pagehide, and unmount.
  // -----------------------------------------------------------------
  useEffect(() => {
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') return;
    let observed: HTMLIFrameElement | null = null;
    let intersecting = false;
    let accruedMs = 0;
    let lastTick = Date.now();
    let lastBucketAt = Date.now();

    const io = new IntersectionObserver(
      (entries) => {
        for (const en of entries) intersecting = en.isIntersecting;
      },
      { threshold: 0.5 },
    );

    const counting = () =>
      intersecting && document.visibilityState === 'visible' && document.hasFocus();

    const cutBucket = () => {
      const ms = Math.round(accruedMs);
      accruedMs = 0;
      if (ms < EYETIME_MIN_BUCKET_MS) return;
      const queue = attrQueueRef.current;
      queue.push({ markerId: '_view', kind: 'eyetime', raw: ms, eventId: attributionUuid() });
      while (queue.length > ATTRIBUTION_SIGNALS_MAX) queue.shift();
    };

    const tick = () => {
      const now = Date.now();
      // The iframe element is re-keyed on grant / chat changes — re-acquire
      // lazily so the observer always tracks the LIVE element.
      const el = iframeRef.current;
      if (el !== observed) {
        if (observed) io.unobserve(observed);
        observed = el;
        intersecting = false;
        if (el) io.observe(el);
      }
      // Clamp the delta so a suspended timer (background tab thaw) can't
      // credit the whole gap as viewing time.
      const delta = Math.min(now - lastTick, EYETIME_TICK_MS * 5);
      lastTick = now;
      if (counting()) accruedMs += delta;
      if (now - lastBucketAt >= EYETIME_BUCKET_MS) {
        lastBucketAt = now;
        cutBucket();
      }
    };

    const tickTimer = setInterval(tick, EYETIME_TICK_MS);
    // Buckets enqueue WITHOUT scheduling (they piggyback on marker flushes);
    // this dedicated timer bounds eyetime-only traffic to one POST per minute.
    const flushTimer = setInterval(() => {
      if (attrQueueRef.current.length > 0) scheduleAttributionFlush();
    }, EYETIME_FLUSH_MS);

    const onVisibility = () => {
      tick();
      if (document.visibilityState === 'hidden') {
        cutBucket();
        postAttributionBatch(true);
      }
    };
    const onPageHide = () => {
      tick();
      cutBucket();
      postAttributionBatch(true);
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);

    return () => {
      clearInterval(tickTimer);
      clearInterval(flushTimer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      if (attrTimerRef.current) {
        clearTimeout(attrTimerRef.current);
        attrTimerRef.current = null;
      }
      // Final accrual + beacon so a widget deleted from the canvas still
      // reports its last view window. Runs BEFORE io.disconnect() — tick()
      // may re-observe the live element, which would re-arm a disconnected
      // observer if the order were reversed.
      tick();
      cutBucket();
      postAttributionBatch(true);
      io.disconnect();
    };
  }, [postAttributionBatch, scheduleAttributionFlush]);

  /**
   * Active KV subscriptions keyed by `subId`. Declared BEFORE the
   * registry so the kv.subscribe handler can close over the ref
   * during registry construction. Cleared on unmount and on
   * registry rebuild.
   */
  const activeSubsRef = useRef<
    // `push` is typed as `(frame: unknown) => void` (not
    // `WidgetPush['push']`) because `OpHandler<Args, Result, Push>`
    // defaults `Push = never`, leaving `ctx.push: PushSender<never>`.
    // We forward whatever payload the kv stream sends; downstream
    // consumers narrow it via the post-message frame type.
    Map<string, { key: string; push: (frame: unknown) => void }>
  >(new Map());

  /**
   * The chat with the person's own app agent, streamed into the app
   * (2026-09-29, the round 8 dev run; lib/workspaces/widget-ops/agent-inbox.ts
   * `AskRelay`). `askWatchersRef` holds every open `agent.ask.watch` by its
   * sub id, and a frame is posted to the frame as `{ id: subId, push }`, the
   * id the SDK's `host.onPush(subId, handler)` routes on (a request-scoped
   * `ctx.push` would carry the request id, which the SDK forgets the moment
   * the watch answers). `askChatIdRef` is the DM's chat id as the ask and
   * thread ROUTES answered it, held here and never taken from the app;
   * `askRelayRef` is the one projection per mount that the UAMP effect below
   * feeds. Declared before the registry so the handlers can close over them.
   */
  const askWatchersRef = useRef<Set<string>>(new Set());
  const askChatIdRef = useRef<string | null>(null);
  const askRelayRef = useRef<AskRelay | null>(null);

  /**
   * Per-mount cmd dispatcher state for the widget-command-bus
   * registration. `pendingCmds` correlates bus-side invocations with
   * the iframe's `cmd.result` reply frames; `eventSubs` fans out
   * widget-emitted events to bus subscribers.
   */
  const pendingCmdsRef = useRef<
    Map<string, { resolve: (value: unknown) => void; reject: (err: Error) => void; timer: ReturnType<typeof setTimeout> }>
  >(new Map());
  /**
   * Reply ids this bridge answered from its own `pendingCmds`: the command
   * came through this tab's bus and its result has reached its caller. The
   * `widget.event` handler reads this and does NOT relay such a result to
   * `/events`: nobody on the workspace bus is waiting for it, the copy cost
   * a second trip of the whole result, and a result over the route's 32 KB
   * cap drew a 413 in the console every time an agent read a full day (the
   * Revent run on dev, 2026-10-02). A result with no pending entry here was
   * asked for by ANOTHER app or tab through the bus and is still relayed.
   */
  const locallyAnsweredRef = useRef<Set<string>>(new Set());
  /**
   * SDK readiness gate for cmd relays. A `cmd` posted while the iframe is
   * BOOTING (initial load, or right after `__reload`) is silently lost —
   * the SDK's message listener doesn't exist yet — and the invoke dangles
   * until its timeout. `sdkReady` flips true on the `__ready__` handshake
   * and false when `__reload` re-navigates the iframe; `sdkReadyWaiters`
   * holds invokes parked until then (each also bounded by its own grace).
   */
  const sdkReadyRef = useRef(false);
  const sdkReadyWaitersRef = useRef<Set<() => void>>(new Set());
  // THE PERSON ARRIVES AFTER THE APP (2026-09-24, Revent's inbox browser
  // pass). `currentUser` comes from AuthProvider, which resolves the session
  // once per page, and an app whose files are cached answers the handshake
  // before it does (measured: the app ready at about 1.8 s, the session at
  // 3.0 to 3.7 s). Its context then said nobody was signed in and nothing
  // ever sent it again, so `host.user.current()` stayed null for the life of
  // the frame: Revent read its signed-in owner as signed out on 6 of 8
  // loads, with no day of their own, no code and no Inbox. The comment above
  // `currentUser` calls this race small; measured, it was lost most of the
  // time. When the signed-in user changes after the handshake the context is
  // sent again (the SDK only replaces it, nothing re-runs) and `{ kind:
  // 'user' }` is broadcast so an app that read it once can look again. The
  // broadcast carries no identity: the app reads the context it was given.
  // Before the handshake there is nothing to do, since the reply carries it.
  const pushedUserRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const id = currentUser?.userId ?? null;
    if (pushedUserRef.current === undefined) { pushedUserRef.current = id; return; }
    if (pushedUserRef.current === id) return;
    pushedUserRef.current = id;
    if (!sdkReadyRef.current) return;
    // The same helper the bind and save re-pushes use, so every site sends
    // one builder's context (tests/unit/widgets/crm-project-link-race.test.ts).
    pushWorkspaceContext();
    iframeRef.current?.contentWindow?.postMessage({ id: '__broadcast__', push: { kind: 'user' } }, '*');
  }, [currentUser]);
  /**
   * Actor of the agent command currently relayed into the widget (depth-
   * counted for overlaps). Ops the widget fires WHILE handling an agent
   * command — notably the checkpoint save — read this so the server can
   * credit the acting client (`content_versions.edited_via`), mirroring how
   * comments already attribute to the client instead of the session user.
   * Debounced autosaves that fire AFTER the command settles intentionally
   * stay user-attributed.
   */
  const agentCmdActorRef = useRef<{ name: string; depth: number } | null>(null);
  const eventSubsRef = useRef<
    Map<string, Set<(payload: unknown) => void>>
  >(new Map());

  /**
   * Per-mount op registry. Closes over `workspaceId` / `itemId` /
   * `originTag` so each widget instance gets its own dispatch surface
   * and KV writes carry the right echo-suppression tag. Recomputed
   * only when those identifiers change — typically never.
   */
  const registry = useMemo(() => {
    const reg = new OpRegistry();

    // --- kv.* family -------------------------------------------------
    // Routed through `/api/widgets/content/:contentId/kv/...`, which addresses
    // the store by CONTENT and owns the rows by the VIEWER (the whole rule
    // lives in `lib/widgets/app-kv-scope.ts`).
    //
    // Before 2026-09-20 these ops posted to
    // `/api/workspaces/:id/items/:itemId/kv` and were short-circuited to
    // silent no-ops whenever `ephemeral` was set — which is EVERY `/w/` mount,
    // because that surface has no workspace item to address. The Revent
    // first-touch runs found what that looks like from the outside: an app
    // opened from a share link shows an empty list, accepts edits, and drops
    // them without a word. There is deliberately no `ephemeral` gate in this
    // family any more. The share surface has a content id, and the content id
    // IS the address.
    //
    // `ws` / `item` are the canvas mount's real ids, sent ONLY so the server
    // can find the pre-2026-09-20 item-keyed rows for its dual-read. The APP
    // never supplies them (a sandboxed frame cannot reach this route at all)
    // and the server re-authorizes both before trusting either.
    const kvUrl = (
      key: string | null,
      scope: AppKvScope | undefined,
      extra?: Record<string, string>,
    ): string | null => {
      const contentId = contentIdRef.current;
      if (!contentId) return null;
      // `scope` rides the wire ONLY when the app supplied it. The route
      // applies the op's own default otherwise, and that difference is
      // load-bearing: a DEFAULT shared counter degrades to a private one on
      // content nobody else can see, while an EXPLICIT `shared` is refused
      // there, because an app that asked for a sequence everyone draws from
      // must not silently get a private one. Sending a resolved scope for
      // every call erased that distinction and made `kv.incr` 409 on the
      // ordinary mount.
      const params = new URLSearchParams({ ...(scope ? { scope } : {}), ...(extra ?? {}) });
      // Prefer this mount's own ids; fall back to the launching canvas tile's
      // when they are synthetic (the fullscreen path).
      const legacyWs = isUuidId(workspaceId) ? workspaceId : legacyKvWorkspaceId;
      const legacyItem = isUuidId(itemId) ? itemId : legacyKvItemId;
      if (isUuidId(legacyWs) && isUuidId(legacyItem)) {
        params.set('ws', legacyWs);
        params.set('item', legacyItem);
      }
      const path = key === null ? '' : `/${encodeURIComponent(key)}`;
      return `/api/widgets/content/${encodeURIComponent(contentId)}/kv${path}?${params}`;
    };

    /**
     * One KV request.
     *
     * Plain `fetch`, NOT `fetchGated`: that wrapper turns a `signup_required`
     * 401 into a guest mint plus a FULL NAVIGATION (a workspace clone, or a
     * reload), and reloading `/w/` out from under a visitor because their todo
     * autosaved is worse than an honest error the app can show. The route
     * answers a bare `no_session` 401 for exactly that reason.
     *
     * `/w/` mints a guest on mount (ViewerGuestMint) while the widget boots in
     * parallel, and the boot usually wins — so a first 401 is often a race
     * rather than an answer. Wait for a mint that has already been announced
     * and try once more, the same way `collab.get-token` does below.
     */
    const kvFetch = async (
      url: string,
      init?: RequestInit,
      opts?: { mintOnDemand?: boolean },
    ): Promise<Response> => {
      const res = await fetch(url, { credentials: 'same-origin', ...init });
      if (res.status !== 401) return res;
      // A mint the page already announced: this 401 is the boot race, not an
      // answer. Costs nothing when nothing is in flight.
      if (await awaitGuestMint()) {
        return fetch(url, { credentials: 'same-origin', ...init });
      }
      // A WRITE from a signed-out visitor. `/w/` only mints on mount for the
      // apps that need a session to RENDER (a collab scratch room, a private
      // share code), so timer, todo, snake, invaders and blocks arrived here
      // with no identity to own a store, every save came back 401, and the
      // apps call `host.kv.set` without awaiting it — an unhandled rejection
      // and a lost edit with nothing said to the person. Minting on the
      // gesture keeps a passive viewer (and every crawler) out of the users
      // table while giving anyone who edits somewhere to save.
      if (opts?.mintOnDemand && (await ensureGuestSession())) {
        return fetch(url, { credentials: 'same-origin', ...init });
      }
      return res;
    };

    /**
     * A read with no reachable store answers EMPTY and a write REFUSES, with a
     * code the app can branch on. Both are the honest answer to their own
     * question: an app boots on its defaults either way, but one that cannot
     * save must be able to say so rather than pretend it did.
     */
    const kvFailure = (op: string, code: string, message: string) => {
      const e = new Error(`${op}: ${message}`) as Error & { code?: string };
      e.code = code;
      return e;
    };
    /**
     * Turn a refused write into the rejection the APP sees.
     *
     * The decision lives in `kvRefusal` (`lib/workspaces/widget-ops/kv.ts`),
     * which prefers the code AND MESSAGE the server sent over anything
     * inferred from the HTTP status. Re-deriving a code here from the status
     * was a second source of truth for the same fact and it drifted twice in
     * two days; the server's message is also the only place that can say WHY
     * ("TTL exceeds the plan maximum of 30 days"), which is what an app needs
     * to tell the person something useful.
     */
    const kvWriteError = async (op: string, res: Response) => {
      let body: { error?: { code?: unknown; message?: unknown } } | null = null;
      try {
        body = (await res.json()) as typeof body;
      } catch {
        /* empty or non-JSON body: the status is all there is. */
      }
      const { code, message } = kvRefusal(res.status, body);
      return kvFailure(op, code, message);
    };
    /** No content row yet, so no store: a not-found, not a new word for one. */
    const kvNoStore = (op: string) =>
      kvFailure(op, 'not_found', 'this app has no store yet');
    /**
     * Statuses a READ reports as an empty store: no session yet, no access to
     * this content, no such store, or a scope this content cannot offer. None
     * of them is an error an app can act on, and all four mean the same thing
     * to the caller — there is nothing stored for you here.
     */
    const kvReadIsEmpty = (status: number) =>
      status === 401 || status === 403 || status === 404 || status === 409;

    // ── host.exchange (ADR-0028 v2) ─────────────────────────────────
    // Envelope stash for cross-app copy/paste: copy() parks the envelope
    // under a nonce with a short TTL; a paste in ANY tab of this browser
    // redeems it. localStorage so it survives the copying tab closing
    // before the paste; pruned on every touch.
    reg.register('exchange.copy', validateExchangeCopyArgs, async (args) => {
      const nonce =
        (crypto as { randomUUID?: () => string }).randomUUID?.() ??
        Math.random().toString(36).slice(2) + Date.now().toString(36);
      const TTL_MS = 5 * 60_000;
      try {
        const now = Date.now();
        // prune expired stashes so the quota never creeps
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const k = localStorage.key(i);
          if (!k || !k.startsWith('rbx:exchange:')) continue;
          try {
            const v = JSON.parse(localStorage.getItem(k) || '');
            if (!v || typeof v.exp !== 'number' || v.exp < now) localStorage.removeItem(k);
          } catch {
            localStorage.removeItem(k);
          }
        }
        localStorage.setItem(
          `rbx:exchange:${nonce}`,
          JSON.stringify({ exp: now + TTL_MS, envelope: args.envelope }),
        );
      } catch {
        // quota or private-mode failure: the agent rail still works; the
        // caller's clipboard write still carries the baked image.
      }
      return { nonce };
    });
    reg.register('exchange.resolve', validateExchangeResolveArgs, async (args) => {
      try {
        const k = `rbx:exchange:${args.nonce}`;
        const rawV = localStorage.getItem(k);
        if (!rawV) return { envelope: null };
        const v = JSON.parse(rawV);
        if (!v || typeof v.exp !== 'number' || v.exp < Date.now()) {
          localStorage.removeItem(k);
          return { envelope: null };
        }
        return { envelope: v.envelope ?? null };
      } catch {
        return { envelope: null };
      }
    });

    reg.register('kv.get', validateKvGetArgs, async (args) => {
      const url = kvUrl(args.key, args.scope);
      // No content row yet (a freshly spawned instance whose item has not
      // resolved its refId): an empty store, not an error.
      if (!url) return null;
      const r = await kvFetch(url);
      if (kvReadIsEmpty(r.status)) return null;
      if (!r.ok) throw new Error(`kv.get ${r.status}`);
      return (await r.json()).value ?? null;
    });

    reg.register('kv.set', validateKvSetArgs, async (args) => {
      const url = kvUrl(args.key, args.scope);
      if (!url) throw kvNoStore('kv.set');
      const r = await kvFetch(url, {
        method: 'PUT',
        headers: {
          'content-type': 'application/json',
          'x-widget-origin': originTag,
        },
        body: JSON.stringify(
          args.ttl !== undefined
            ? { value: args.value, ttl: args.ttl }
            : { value: args.value },
        ),
      }, { mintOnDemand: true });
      if (!r.ok) throw await kvWriteError('kv.set', r);
    });

    // kv.incr — atomic counter (PATCH), SHARED by default: see
    // `defaultAppKvScope`. A counter exists to coordinate actors, and a
    // per-person one would hand two people on a board the same number.
    reg.register('kv.incr', validateKvIncrArgs, async (args) => {
      const url = kvUrl(args.key, args.scope);
      if (!url) throw kvNoStore('kv.incr');
      const r = await kvFetch(url, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json', 'x-widget-origin': originTag },
        body: JSON.stringify(
          args.ttl !== undefined ? { delta: args.delta, ttl: args.ttl } : { delta: args.delta },
        ),
      }, { mintOnDemand: true });
      // On content nobody else can see (an app's own catalog row, which is
      // where an unsaved draft mounts) the DEFAULT counter is the viewer's
      // own; ask for `scope: 'shared'` there and you get `scope_unavailable`.
      // See `sharedScopeAvailable`.
      if (!r.ok) throw await kvWriteError('kv.incr', r);
      return (await r.json()) as { value: number };
    });

    reg.register('kv.delete', validateKvDeleteArgs, async (args) => {
      const url = kvUrl(args.key, args.scope);
      if (!url) throw kvNoStore('kv.delete');
      const r = await kvFetch(url, {
        method: 'DELETE',
        headers: { 'x-widget-origin': originTag },
      }, { mintOnDemand: true });
      if (!r.ok && r.status !== 404) throw await kvWriteError('kv.delete', r);
    });

    reg.register('kv.list', validateKvListArgs, async (args) => {
      const url = kvUrl(null, args.scope, args.prefix ? { prefix: args.prefix } : undefined);
      if (!url) return [];
      const r = await kvFetch(url);
      if (kvReadIsEmpty(r.status)) return [];
      if (!r.ok) throw new Error(`kv.list ${r.status}`);
      const data = (await r.json()) as { items?: Array<{ key: string; value: unknown }> };
      return data.items ?? [];
    });

    // kv.subscribe / unsubscribe don't perform an extra network
    // round-trip on subscribe — they just allocate a subId that the
    // outer `workspace.bus` handler matches against. The handler
    // streams push frames via `ctx.push`.
    reg.register('kv.subscribe', validateKvSubscribeArgs, (args, ctx) => {
      // The actual fan-out happens in the workspace.bus listener
      // outside the registry. We surface the requested key via the
      // returned `subId` so the host can wire `ctx.push` to the
      // matching frames. Allocation lives in the outer scope so the
      // useEffect closure can read it.
      const subId = `${itemId}:${args.key}:${Date.now().toString(36)}:${Math.random()
        .toString(36)
        .slice(2, 8)}`;
      // `ctx.push` is typed `PushSender<never>` because `kv.subscribe`
      // never declared a Push type. The runtime payload is whatever
      // the bus frames send (we forward verbatim), so widen the
      // contravariant `never` parameter through `unknown`.
      activeSubsRef.current.set(subId, {
        key: args.key,
        push: ctx.push as unknown as (frame: unknown) => void,
      });
      return { subId };
    });

    reg.register('kv.unsubscribe', validateKvUnsubscribeArgs, (args) => {
      activeSubsRef.current.delete(args.subId);
    });

    // --- content.* family (host.content SDK primitive) --------------
    // Per-instance binary content stored as real `content` rows under a
    // lazily-created workspace folder. Routed through
    // `/api/workspaces/:id/items/:itemId/content/[[...key]]`, which is the
    // sole ACL gate (confines every op to this instance's own folder and
    // rides `canAccessContent` for reads). We forward base64 over the wire
    // in both directions — the SDK normalizes Blob/ArrayBuffer → base64 on
    // `put` and rehydrates a Blob on `get`. Reads use plain `fetch` (a
    // public/anon viewer may legitimately read shared content); writes go
    // through `fetchGated` so an anon write pops the signup dialog.
    const contentBase = `/api/workspaces/${workspaceId}/items/${itemId}/content`;
    // Scope every content op to the OPEN PROJECT (ADR-0022): `?project=<contentId>`
    // routes the read/write to the project folder `host.content` is rooted at
    // (`host.workspace.contentId`), instead of the per-instance `itemId` folder.
    // `contentIdRef` holds the live project base (recomputed each render); read
    // it at call time so a save→rebind picks up the new folder automatically.
    // The folder every content op addresses (P-20), by the read rule in
    // `lib/workspaces/widget-ops/documents.ts`: the remembered media folder
    // while the tile is still bound where this client remembered it (a fresh
    // widget reusing the folder it just minted, before the Yjs to snapshot
    // round trip lands), and the binding itself otherwise. A rebind therefore
    // changes the answer without changing anything shared, so the op can never
    // land in a project the tile has moved away from.
    const contentProjectNow = (): string | null =>
      resolveContentScope({
        captured: contentFolderRef.current,
        capturedUnder: contentFolderBindingRef.current,
        boundNow: projectFolderIdNow(),
        base: contentIdRef.current,
      });
    const opQs = (scope?: string) => {
      const p = new URLSearchParams();
      if (scope && scope !== 'instance') p.set('scope', scope);
      const project = contentProjectNow();
      if (project) p.set('project', project);
      const s = p.toString();
      return s ? `?${s}` : '';
    };

    // Guarded JSON read for content ops. An auth race or a proxy hiccup can
    // hand back an HTML page WITH STATUS 200 (the SW notes document the same
    // trap) — blindly calling r.json() then surfaces the raw parse error
    // ("Unexpected token '<' … is not valid JSON") to the widget's toast.
    // Check the content type first and throw something a user can act on.
    const contentJson = async <T,>(r: Response, op: string): Promise<T> => {
      if (!r.ok) throw new Error(`${op} ${r.status}`);
      const ct = r.headers.get('content-type') || '';
      if (!ct.includes('json')) {
        throw new Error(`${op}: server temporarily unavailable — try again`);
      }
      return (await r.json()) as T;
    };

    reg.register('content.put', validateContentPutArgs, async (args) => {
      // Ephemeral mount (`/w/` standalone view). When the OPEN PROJECT is a
      // real content row (a saved project folder — the owner editing their own
      // project fullscreen via maximize, or a write-granted collaborator on a
      // shared one), write into it through the folder-scoped documents route:
      // media lands in the project folder exactly like a canvas write. Without
      // a project base (a widget-catalog preview) there is genuinely nowhere
      // to persist — REJECT so the widget surfaces the failure, instead of the
      // old fake `{id:'ephemeral'}` success that silently dropped uploads (the
      // "media gone after reload" bug). Matches the `documents.save` idiom.
      if (ephemeral) {
        const project = contentIdRef.current;
        if (project) {
          const r = await fetchGated(
            `/api/widgets/documents/${encodeURIComponent(project)}/content`,
            {
              method: 'PUT',
              credentials: 'same-origin',
              headers: { 'content-type': 'application/json', 'x-widget-origin': originTag },
              body: JSON.stringify({ data: args.data, name: args.name, mime: args.mime }),
            },
          );
          if (r.ok) {
            const out = await contentJson<{ id: string; key: string }>(r, 'content.put');
            return { id: out.id, key: out.key };
          }
          // 404 = not a project folder (catalog preview) → fall through to the
          // honest rejection; real permission/quota errors surface as-is.
          if (r.status !== 404) throw new Error(`content.put ${r.status}`);
        }
        throw new Error(
          'content.put unavailable in this preview — media is not saved here. Open the app from a workspace to keep uploads.',
        );
      }
      const r = await fetchGated(`${contentBase}${opQs(args.scope)}`, {
        method: 'PUT',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json', 'x-widget-origin': originTag },
        body: JSON.stringify({ data: args.data, name: args.name, mime: args.mime }),
      });
      const out = await contentJson<{ id: string; key: string; folderId?: string }>(r, 'content.put');
      // Capture the folder the route resolved or minted, TOGETHER with the
      // binding it was captured under (P-20). The server can't write the
      // client-owned Yjs item, so the bridge mirrors the folder to
      // `state.contentFolderId`: that is what a reload reuses, and the only
      // way `content.signedUrl` can name the folder of a tile that has never
      // been saved. Compare against the ref (kept fresh from state each
      // render) so this patches at most once.
      //
      // A folder equal to the BOUND project is never captured and so never
      // mirrored: it is the binding, not the tile's own media folder, and
      // writing it there is what let a plain canvas CREATE adopt it
      // (`ensureProjectFolder`) and overwrite that project's body. Nothing
      // here clears the key either, on any path: the version of this fix that
      // dropped the pointer on a binding change had two open clients writing
      // it back and forth forever.
      const boundNow = projectFolderIdNow();
      if (
        isCapturableContentFolder(out.folderId, boundNow) &&
        (out.folderId !== contentFolderRef.current || contentFolderBindingRef.current !== boundNow)
      ) {
        contentFolderRef.current = out.folderId;
        contentFolderBindingRef.current = boundNow;
        patchItemState(itemId, { contentFolderId: out.folderId });
      }
      return { id: out.id, key: out.key };
    });

    reg.register('content.get', validateContentGetArgs, async (args) => {
      // Share viewer (`/w/<folderId>`): no workspace item, but the OPEN PROJECT
      // is `contentId` (the folder). Read its media by name through the
      // folder-scoped, ACL'd documents route so a shared/published project's
      // media resolves (ADR-0022). No project context → genuinely empty.
      if (ephemeral) {
        const project = contentIdRef.current;
        if (!project) return null;
        const r = await fetch(
          `/api/widgets/documents/${encodeURIComponent(project)}/content/${encodeURIComponent(args.key)}`,
          { method: 'GET', credentials: 'same-origin' },
        );
        if (r.status === 404 || !r.ok) return null;
        return await contentJson<{ data: string; mime: string; name: string }>(r, 'content.get');
      }
      const r = await fetch(
        `${contentBase}/${encodeURIComponent(args.key)}${opQs(args.scope)}`,
        { method: 'GET', credentials: 'same-origin' },
      );
      if (r.status === 404) return null;
      return await contentJson<{ data: string; mime: string; name: string }>(r, 'content.get');
    });

    // --- content.signedUrl (host.content.signedUrl) --------------------------
    // Signed streaming URL for a project file (review-player proxy). Runs the
    // ACL'd resolve on the apex (documents content route, `?url=1`); the
    // returned HMAC `?sig&exp` URL streams cookie-less from the sandbox with
    // Range/206. Works for canvas mounts and `/w` share views alike — both
    // address the OPEN PROJECT folder.
    reg.register('content.signedUrl', validateContentSignedUrlArgs, async (args) => {
      // Same folder rule as put/get/list/delete (P-20): the captured folder only
      // while its binding holds, else the binding.
      const project = contentProjectNow();
      if (!project) throw new Error('content.signedUrl requires a saved project');
      const r = await fetch(
        `/api/widgets/documents/${encodeURIComponent(project)}/content/${encodeURIComponent(args.key)}?url=1`,
        { method: 'GET', credentials: 'same-origin' },
      );
      if (r.status === 404) return null;
      if (!r.ok) throw new Error(`content.signedUrl ${r.status}`);
      return (await r.json()) as { url: string; mime: string; name: string; id: string };
    });

    reg.register('content.list', validateContentListArgs, async (args) => {
      if (ephemeral) {
        const project = contentIdRef.current;
        if (!project) return [];
        const r = await fetch(`/api/widgets/documents/${encodeURIComponent(project)}/content`, {
          method: 'GET',
          credentials: 'same-origin',
        });
        if (!r.ok) return [];
        const data = (await r.json()) as {
          items?: Array<{ id: string; name: string; mime: string; size: number; createdAt: string }>;
        };
        return data.items ?? [];
      }
      const r = await fetch(`${contentBase}${opQs(args.scope)}`, {
        method: 'GET',
        credentials: 'same-origin',
      });
      if (!r.ok) throw new Error(`content.list ${r.status}`);
      const data = (await r.json()) as {
        items?: Array<{ id: string; name: string; mime: string; size: number; createdAt: string }>;
      };
      return data.items ?? [];
    });

    reg.register('content.delete', validateContentDeleteArgs, async (args) => {
      if (ephemeral) {
        // Folder-backed standalone mount (saved project) — real delete through
        // the documents route; a catalog preview has nothing persisted (no-op).
        const project = contentIdRef.current;
        if (project) {
          const r = await fetchGated(
            `/api/widgets/documents/${encodeURIComponent(project)}/content/${encodeURIComponent(args.key)}`,
            { method: 'DELETE', credentials: 'same-origin', headers: { 'x-widget-origin': originTag } },
          );
          if (!r.ok && r.status !== 404) throw new Error(`content.delete ${r.status}`);
        }
        return;
      }
      const r = await fetchGated(
        `${contentBase}/${encodeURIComponent(args.key)}${opQs(args.scope)}`,
        { method: 'DELETE', credentials: 'same-origin', headers: { 'x-widget-origin': originTag } },
      );
      if (!r.ok && r.status !== 404) throw new Error(`content.delete ${r.status}`);
    });

    // --- content.* USER-LIBRARY surface (pick / read / export) ---------------
    // `pick` is a HOST-SIDE op: it renders the portal-chrome picker dialog
    // (components/workspace/widget-content-picker.tsx) over the canvas. The
    // widget never sees the library — only the items the user picked, which
    // become GRANTS; `content.read` is gated on that grant set, then the
    // bridge (running on the apex with the user's session) fetches the bytes
    // and relays base64. `export` writes a top-level MY LIBRARY row through
    // the standard upload route.
    reg.register('content.pick', validateContentPickArgs, async (args) => {
      // Ephemeral (/w standalone) mounts CAN pick when a signed-in user is
      // present: the dialog is portal chrome with the user's own session, and
      // grants live in this bridge instance's memory — nothing persists. Only
      // anonymous share viewers (no library) get the null. Requires the host
      // page to mount <WidgetContentPicker/> (standalone-widget does).
      // NOTE: read via the REF — this effect's deps don't include currentUser,
      // so the state value here is the mount-time null forever.
      if (ephemeral && !currentUserRef.current) return null;
      const picked = await new Promise<
        Array<{ kind: 'file' | 'url'; id?: string; name?: string; mime?: string; size?: number; url?: string }> | null
      >((resolve) => {
        window.dispatchEvent(
          new CustomEvent('widget-content-pick', { detail: { ...args, resolve } }),
        );
      });
      if (!picked || !picked.length) return null;
      for (const it of picked) if (it.kind === 'file' && it.id) grantedContent.current.add(it.id);
      return picked;
    });

    // PUBLIC-only read: fetches WITHOUT credentials, so the server resolves
    // access as an anonymous stranger would (canAccessContent(id, null) —
    // public links / public-folder ancestry only). No pick-grant needed and
    // none consulted: a widget can read exactly what the open internet can,
    // never the viewer's library. Shipped for MMO-shared assets (elaisium
    // world assets published into a public system folder) where every
    // client must load content some OTHER player published.
    reg.register('content.readPublic', validateContentReadArgs, async (args) => {
      const r = await fetch(`/api/content/${encodeURIComponent(args.id)}`, {
        method: 'GET',
        credentials: 'omit',
      });
      if (r.status === 404 || r.status === 403) return null;
      if (!r.ok) throw new Error(`content.readPublic ${r.status}`);
      const buf = await r.arrayBuffer();
      let bin = '';
      const u8 = new Uint8Array(buf);
      for (let i = 0; i < u8.length; i += 0x8000) bin += String.fromCharCode.apply(null, Array.from(u8.subarray(i, i + 0x8000)));
      return { data: btoa(bin), mime: r.headers.get('content-type') ?? 'application/octet-stream' };
    });

    reg.register('content.read', validateContentReadArgs, async (args) => {
      if (!grantedContent.current.has(args.id)) {
        throw new Error('content.read: id was not granted via content.pick');
      }
      const r = await fetch(`/api/content/${encodeURIComponent(args.id)}`, {
        method: 'GET',
        credentials: 'same-origin',
      });
      if (r.status === 404) return null;
      if (!r.ok) throw new Error(`content.read ${r.status}`);
      const buf = await r.arrayBuffer();
      let bin = '';
      const u8 = new Uint8Array(buf);
      for (let i = 0; i < u8.length; i += 0x8000) bin += String.fromCharCode.apply(null, Array.from(u8.subarray(i, i + 0x8000)));
      return { data: btoa(bin), mime: r.headers.get('content-type') ?? 'application/octet-stream' };
    });

    reg.register('content.export', validateContentExportArgs, async (args) => {
      // Gate on the SESSION, not on the mount. `/w/` mounts ephemeral for
      // everyone, so gating on `ephemeral` alone also blocked the signed-in
      // owner running their own app from a share link (an app that uploads a
      // picture could never upload one there). An anonymous visitor still has
      // no session, and /api/content/upload re-checks server-side regardless.
      if (ephemeral && !currentUserRef.current) throw new Error('content.export requires a signed-in session');
      const bin = atob(args.data);
      const u8 = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
      // Resumable like every portal upload (CASFS-UI PF-4).
      // Exports land in the OPEN PROJECT's folder when this instance has a
      // saved project of its own (never on share mounts — that folder belongs
      // to someone else). Before this, every widget export dumped at the
      // library root, which is where the root clutter came from.
      const exportParent = !itemId.startsWith('share-')
        ? boundProjectRef.current || itemStateRef.current?.projectFolderId || null
        : null;
      let first;
      try {
        first = await trackUpload(new File([u8], args.name, { type: args.mime }), {
          filename: args.name,
          mimeType: args.mime,
          displayName: args.name,
          parentId: exportParent,
          visibility: (args.visibility as 'private' | 'unlisted' | 'public' | undefined) ?? 'private',
          fetchImpl: fetchGated as typeof fetch,
          headers: { 'x-widget-origin': originTag },
        });
      } catch (e) {
        throw new Error(`content.export ${(e as { code?: string })?.code ?? 'failed'}`);
      }
      return { id: first.id, url: first.url };
    });

    // --- links.mint (host.links.mint) ----------------------------------------
    // A SHORT public URL for one of this app's own public pages, e.g. a
    // published sign-up form: `/go/<code>` instead of the ~110-char
    // `/api/app-hooks/<appId>/<fn>?<params>`.
    //
    // It is not only cosmetic. The minted row carries the PROJECT and its
    // OWNER — stamped by the route from this mount, never by app code — so
    // `/go/<code>` can dispatch the page with `consumerOverride`, and the
    // TENANT pays for their own public traffic. On `/api/app-hooks/...` the
    // portal cannot do that: the slug -> tenant mapping lives in app KV,
    // which the portal never reads, so D4 bills the app AUTHOR instead.
    //
    // Owner-only and idempotent per (project, fn, params) — re-publishing a
    // form returns the code already printed on someone's poster.
    reg.register('links.mint', validateLinksMintArgs, async (args) => {
      const project = contentIdRef.current;
      // No saved project = no billable tenant. Say so rather than silently
      // handing back a long link the caller thinks is short.
      if (!project || !itemId) return { url: null, error: 'no_project' };
      // The APP is resolved server-side from the mounted project (the item's
      // refId is the project FOLDER for a project-opened app, not the app
      // row) — and app code should not get to name which app a public link
      // belongs to anyway.
      try {
        const r = await fetchGated('/api/workspaces/public-link', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ itemId, ...args }),
        });
        const data = (await r.json().catch(() => ({}))) as {
          url?: string; code?: string; reused?: boolean; error?: string; limit?: number;
        };
        if (!r.ok) return { url: null, error: data.error || 'mint_failed', limit: data.limit };
        return { url: data.url ?? null, code: data.code ?? null, reused: !!data.reused };
      } catch {
        return { url: null, error: 'mint_failed' };
      }
    });

    // --- content.setShareCard (host.content.setShareCard) --------------------
    // Owner-only: set the OPEN PROJECT's OG card. Targets `contentIdRef` (the
    // project content id from the mount — never a client-supplied id); the route
    // re-checks ownership, so a viewer / guest is rejected there. Needs a saved
    // project (a scratch/catalog-preview mount has no content to write).
    reg.register('content.setShareCard', validateSetShareCardArgs, async (args) => {
      const project = contentIdRef.current;
      if (!project) throw new Error('content.setShareCard requires a saved project');
      const r = await fetchGated(`/api/content/${encodeURIComponent(project)}/share-card`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json', 'x-widget-origin': originTag },
        body: JSON.stringify(args),
      });
      if (!r.ok) throw new Error(`content.setShareCard ${r.status}`);
      return (await r.json()) as { ok: boolean; hasImage: boolean };
    });

    // --- agent.request (host.agents.request) ---------------------------------
    // The agent-grant chrome (components/workspace/widget-agent-grant.tsx),
    // content.pick's twin: the HOST renders the roster dialog; the widget only
    // ever receives the refs the user confirms. The confirmed selection is
    // persisted to `state.connectedResources` HERE — host chrome is the only
    // writer besides server-side install — replacing any previous agent-kind
    // refs (re-request = re-pick; non-agent refs untouched). `grantedResources`
    // derives reactively from item state, so list/get work with no reload.
    reg.register('agent.request', validateAgentRequestArgs, async (args) => {
      if (ephemeral) return null;
      const picked = await new Promise<import('@/components/workspace/widget-agent-grant').AgentGrantSelection | null>((resolve) => {
        window.dispatchEvent(
          new CustomEvent('widget-agent-grant', {
            detail: {
              reason: args.reason,
              multi: args.multi !== false,
              start: args.start,
              widgetTitle: (widgetSpec as { title?: string } | undefined)?.title || 'This app',
              resolve,
            },
          }),
        );
      });
      if (!picked || !picked.refs.length) return null;
      const keep = grantedResourcesRef.current.filter((r) => r.kind !== 'agent');
      patchItemState(itemId, { connectedResources: [...keep, ...picked.refs] });
      return { ok: true, refs: picked.refs };
    });

    // --- agent.ensure (host.agents.ensure) -----------------------------------
    // Mint (or reuse) a USER-OWNED agent bound to THIS project and hand it a
    // standing task — the app-requested project agent. Like agent.request, the
    // HOST gates it: a confirm dialog first (apps cannot silently create
    // agents), then the server route resolves the project from itemId, checks
    // the viewer owns it, and mints via the quota-counted path. The returned
    // ref is persisted to connectedResources so agents.get()/list() see it
    // with no reload.
    reg.register('agent.ensure', validateAgentEnsureArgs, async (args) => {
      if (ephemeral) return { ok: false, error: 'not_available_here' };
      const confirmed = await new Promise<{ task?: string } | null>((resolve) => {
        window.dispatchEvent(
          new CustomEvent('widget-agent-ensure', {
            detail: {
              role: args.role,
              task: args.task,
              reason: args.reason,
              widgetTitle: (widgetSpec as { title?: string } | undefined)?.title || 'This app',
              resolve,
            },
          }),
        );
      });
      if (!confirmed) return { ok: false, error: 'declined' };
      let res: Response;
      try {
        res = await fetch('/api/workspaces/project-agent', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            itemId,
            role: args.role,
            task: confirmed.task ?? args.task,
            appId: (widgetSpec as { id?: string } | undefined)?.id,
          }),
        });
      } catch {
        return { ok: false, error: 'network' };
      }
      const data = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        agent?: { id: string; username: string; isNew: boolean };
        error?: string;
        cap?: number;
      };
      if (!res.ok || !data.ok || !data.agent) {
        return { ok: false, error: data.error ?? 'ensure_failed', cap: data.cap };
      }
      const ref = { kind: 'agent' as const, id: data.agent.id, name: data.agent.username, handle: data.agent.username };
      const keep = grantedResourcesRef.current.filter((r) => !(r.kind === 'agent' && r.id === ref.id));
      // Also grant the project agent CONTROL of this app (state.agentControl)
      // so it can file matching deals via the app's commands — it was created
      // for exactly this app, by the owner, in this confirm. Append to the
      // existing allow-list (TrustRules); never widen to 'everyone'.
      const patch: Record<string, unknown> = { connectedResources: [...keep, ref] };
      const token = `@${data.agent.id}`;
      const cur = (itemStateRef.current as { agentControl?: unknown } | undefined)?.agentControl;
      // `nobody`/`none` short-circuits to DENY in evaluateAgentTrust BEFORE the
      // explicit-handle match, so appending to a deny-default list would grant
      // nothing while reporting success. Drop the preset when adding a handle.
      const withoutDeny = (list: string[]) => list.filter((v) => v !== 'nobody' && v !== 'none');
      if (Array.isArray(cur)) {
        const base = withoutDeny(cur as string[]);
        if (!base.includes(token) && !base.includes('everyone')) base.push(token);
        patch.agentControl = base;
      } else if (cur && typeof cur === 'object') {
        const allowRaw = Array.isArray((cur as { allow?: unknown }).allow) ? (cur as { allow: string[] }).allow : [];
        const allow = withoutDeny(allowRaw);
        if (!allow.includes(token) && !allow.includes('everyone')) allow.push(token);
        patch.agentControl = { ...(cur as object), allow };
      } else {
        // No prior rule (default deny) — grant exactly this agent.
        patch.agentControl = [token];
      }
      patchItemState(itemId, patch);
      return { ok: true, agent: data.agent };
    });

    // --- ui.print (host.print) -----------------------------------------------
    // A sandboxed widget mount does not reliably get `allow-modals` (the outer
    // sandbox tier omits it, and nested sandboxes INTERSECT), so a widget's own
    // `window.print()` is silently swallowed. The HOST document has no such
    // restriction, so it prints on the widget's behalf — one implementation for
    // every app instead of per-app popup workarounds.
    //
    // SECURITY: the markup comes from the widget, so it is rendered in a frame
    // sandboxed WITHOUT `allow-scripts` — widget JS can never run, and inline
    // handlers/`<script>` are inert. `allow-same-origin` is required for us to
    // reach `contentWindow.print()`; combined with no-scripts that grants the
    // frame no executable capability. The frame is removed after printing.
    // [AUTOSAVE-2] The app holds edits it has not saved: this PAGE warns on leaving, because a frame's own beforeunload does not prompt here (lib/workspaces/app-unsaved.ts).
    reg.register('ui.setUnsaved', validateUiSetUnsavedArgs, (a) => { setAppUnsaved(unsavedInstance.current, a.unsaved); return { ok: true }; });
    reg.register('ui.print', validatePrintRequestArgs, async (args) => {
      if (typeof document === 'undefined') return { ok: false, error: 'unavailable' };
      const body =
        args.html ??
        (args.images ?? []).map((s) => `<img src="${s.replace(/"/g, '&quot;')}" alt="">`).join('');
      const css =
        args.css ??
        '@page{margin:0}html,body{margin:0}img{display:block;width:100%;break-after:page}img:last-child{break-after:auto}';
      const doc =
        `<!doctype html><html><head><meta charset="utf-8">` +
        `<title>${(args.title ?? 'Print').replace(/[<&]/g, '')}</title>` +
        `<style>${css}</style></head><body>${body}</body></html>`;
      const frame = document.createElement('iframe');
      frame.setAttribute('sandbox', 'allow-same-origin allow-modals');
      frame.setAttribute('aria-hidden', 'true');
      frame.style.cssText = 'position:fixed;right:0;bottom:0;width:1px;height:1px;opacity:0;border:0';
      frame.srcdoc = doc;
      try {
        await new Promise<void>((resolve, reject) => {
          const to = window.setTimeout(() => reject(new Error('print timed out')), 30_000);
          frame.onload = () => {
            window.clearTimeout(to);
            resolve();
          };
          frame.onerror = () => {
            window.clearTimeout(to);
            reject(new Error('print document failed to load'));
          };
          document.body.appendChild(frame);
        });
        const win = frame.contentWindow;
        if (!win) throw new Error('print frame unavailable');
        // Let images decode before the dialog samples the layout.
        await Promise.all(
          [...(frame.contentDocument?.images ?? [])].map((img) =>
            img.decode().catch(() => undefined),
          ),
        );
        win.focus();
        win.print();
        return { ok: true };
      } catch (e) {
        return { ok: false, error: e instanceof Error ? e.message : 'print failed' };
      } finally {
        // Give the dialog a beat to take its snapshot before teardown.
        window.setTimeout(() => frame.remove(), 60_000);
      }
    });

    // --- ui.signIn (ask the host to start sign-in) ---------------------------
    // 2026-09-20, the Revent first-touch runs. A stranger scans an app's code,
    // lands signed out, and the app wants them signed in so the thing they
    // scanned can be saved. It cannot do that itself: Google refuses to be
    // framed, and a sandboxed iframe has no top-level navigation. So it asks,
    // and the HOST navigates — this document, to `/login`, with a `returnTo`
    // the host computes from its own `location`.
    //
    // The app supplies ONE thing: an opaque `carry` string, validated against
    // the arrival-fragment grammar and parked in sessionStorage before the
    // navigation (`prepareSignInHandoff`). It supplies no destination and no
    // provider, and the validator refuses those keys out loud rather than
    // ignoring them, because an app-chosen destination is an open redirect in
    // the hands of untrusted user-space code and buys nothing the host does
    // not already know. No provider UI is ever rendered in the frame; the
    // person always sees our own login screen.
    //
    // BOTH SURFACES, one registration, because the bridge is mounted verbatim
    // on the canvas and on `/w/`. Signed-in (the normal canvas case) is an
    // immediate no-op answer; an anonymous canvas (a public workspace) and the
    // `/w/` share view both navigate. `runUiSignIn` holds the reasoning for
    // each refusal.
    reg.register('ui.signIn', validateUiSignInArgs, async (args) => {
      if (typeof window === 'undefined') return { ok: false, error: 'unavailable' as const };
      let storage: Storage | null = null;
      try {
        storage = window.sessionStorage;
      } catch {
        // Opaque origin or blocked storage: sign-in still works, the code
        // simply is not carried, and the answer says `carried: false`.
        storage = null;
      }
      return runUiSignIn(args, {
        // Read through the REF: this effect's deps do not include currentUser,
        // so reading the state here would capture the mount-time null forever.
        // A GUEST (the session `/w/` mints for a signed-out visitor) is not
        // signed in here (2026-09-27, `signedInForUiSignIn`).
        signedIn: signedInForUiSignIn(currentUserRef.current),
        location: { pathname: window.location.pathname, search: window.location.search },
        storage,
        requested: signInRequestedRef,
        navigate: (href) => window.location.assign(href),
      });
    });

    // --- install.* (host.install) --------------------------------------------
    // App-hosts plan E2 (2026-09-24). `install.state` is the snapshot the
    // context carries; `install.request` asks the PLATFORM for its prompt:
    // on a host the page installs itself (the browser's dialog, the Web
    // Install API, or the instruction sheet the host page draws), elsewhere
    // the app's own host is installed or opened. An app never draws a prompt.
    // The limits (one per mount per 30 s, only under a user activation) are
    // the handler's own, because the registry's rate limit is documentation;
    // `runInstallRequest` holds them so a test can drive them directly.
    // Off a host both ops resolve the app's own host ON DEMAND (functional
    // review, 2026-09-24): `install.state` never answers `unknown`, and
    // `install.request` looks the host up inside the activation window
    // before it opens anything. The memo makes the second ask free.
    reg.register('install.state', validateInstallStateArgs, async () => {
      if (appHostRef.current) return { ...installRef.current };
      return resolveRemoteInstallRef.current();
    });
    reg.register('install.request', validateInstallRequestArgs, async () => {
      if (typeof window === 'undefined') return { outcome: 'unavailable' as const };
      const activation = (navigator as Navigator & { userActivation?: { isActive?: boolean } }).userActivation;
      return runInstallRequest({
        userActivation: activation ? !!activation.isActive : null,
        lastRequestAt: installRequestedAtRef,
        perform: async () => {
          if (appHostRef.current) return installSelf();
          await resolveRemoteInstallRef.current();
          const origin = remoteHostRef.current;
          return origin ? installRemote(origin) : 'unavailable';
        },
      });
    });

    // --- share.request (host.requestShare) -----------------------------------
    // SAFE share assist: a widget can ASK the host to open the system Share
    // dialog, but can NEVER grant access itself — access control is mutated only
    // via the owner-gated grant routes, reached only from the trusted system
    // dialog (the human owner clicks Save), which also re-gates on ownership
    // (`assertActorCanManage`).
    //
    // Default target is the widget's OWN project (`contentIdRef`). A widget that
    // holds the `files` grant already reads/writes the viewer's library, so it
    // may also SHARE the items it has access to: we honour the requested
    // `contentId` / `contentIds`, confined to what the bridge actually served
    // (`filesServed`, enforced in scoped mode). Without the files grant the ids
    // are ignored and we fall back to the own-project target — the original
    // guard against a random widget popping share dialogs for arbitrary content.
    // (`filesGranted` / `filesScopeRootId` / `filesServed` are consts declared
    // further down in THIS effect; this async handler only runs on a widget
    // message, long after the effect body has initialised them.)
    reg.register('share.request', validateShareRequestArgs, async (args) => {
      if (ephemeral) return { ok: false };
      const requested = args.contentIds?.length
        ? args.contentIds
        : args.contentId
          ? [args.contentId]
          : [];
      let targets: string[] = [];
      if (filesGranted && requested.length) {
        targets = requested.filter((id) => !filesScopeRootId || filesServed.has(id));
      }
      if (!targets.length && shareableContentRef.current) {
        targets = [shareableContentRef.current];
      }
      // `reason: 'unsaved'` lets the app say "save it first" instead of
      // silently opening a share dialog for the wrong thing.
      if (!targets.length) {
        return { ok: false, reason: shareableContentRef.current ? 'unavailable' : 'unsaved' };
      }
      window.dispatchEvent(
        new CustomEvent('widget-request-share', {
          // linkTypes: app-declared TYPED links ('player', …) the dialog offers
          // alongside the default link. Display metadata only — the dialog
          // mints them itself on the owner's session.
          detail: { contentIds: targets, linkTypes: args.linkTypes },
        }),
      );
      return { ok: true };
    });

    // --- project.open (host.requestProject) -----------------------------------
    // The app ASKS for the portal's Project dialog (CASFS-UI, components/casfs/)
    // over its OWN project: the bridge resolves the project folder itself and
    // sends only a validated tab and path. The dialog is trusted chrome; every
    // person-only decision (a weaker policy, a repository link, a folder on
    // the computer) is made there by the person, never by the app. The app's
    // name in the dialog is the window title as the portal knows it, and the
    // `itemId` lets the dialog ask THIS app to compare two versions of a file
    // (`files.diff`) over the command bus, nothing more.
    reg.register('project.open', validateProjectOpenArgs, async (args) => {
      if (ephemeral) return { ok: false as const, reason: 'unavailable' as const };
      const projectId = projectFolderIdNow();
      if (!projectId) return { ok: false as const, reason: 'unsaved' as const };
      window.dispatchEvent(
        new CustomEvent('widget-request-project', {
          detail: { projectId, tab: args.tab, path: args.path, appName: title ?? null, itemId },
        }),
      );
      return { ok: true as const };
    });

    // --- share.link (host.shareLink) ------------------------------------------
    // The VIEWER's personal share link for this widget's project — the invite
    // loop for in-app share buttons (game lobbies, collab boards, docs). The
    // bridge runs on the apex with the viewer's session, so the mint attributes
    // the VIEWER as the sharer (referral credit goes to whoever shares, not the
    // app owner). Target is always the bridge's own project base (`contentIdRef`
    // — the project FOLDER for project widgets, so recipient links carry the
    // assets); widget-supplied ids are ignored. Signed-out viewers (or private
    // content) fall back to the canonical `/w/{id}` URL, which the landing-page
    // capture attributes to the content owner. Works in the ephemeral share
    // viewer too — a recipient can immediately re-share with THEIR link.
    reg.register('share.link', validateShareLinkArgs, async (args) => {
      // Same rule as share.request: an unsaved draft has no shareable content
      // (see shareableContentRef) — return null rather than a link to the app.
      const target = shareableContentRef.current;
      if (!target) return null;
      let url = `${window.location.origin}/w/${target}`;
      let personal = false;
      try {
        const r = await fetch(`/api/content/${encodeURIComponent(target)}/share-link`, {
          method: 'POST',
          credentials: 'same-origin',
          // Typed link ('player', …) — the mint returns a per-(sharer, type)
          // short code whose type the /w mount hands back to the widget.
          ...(args.type
            ? { headers: { 'content-type': 'application/json' }, body: JSON.stringify({ type: args.type }) }
            : {}),
        });
        if (r.ok) {
          const data = (await r.json()) as { shareToken?: string; url?: string };
          if (data.url) {
            url = data.url;
            personal = true;
          } else if (data.shareToken) {
            url = `${window.location.origin}/w/${data.shareToken}`;
            personal = true;
          }
        }
      } catch {
        // Mint unreachable — canonical URL still shares fine (owner-attributed).
      }
      // Compose the widget's current deep-link (host.workspace.setDeeplink) so the
      // shared URL restores THIS view (a slide / cell / game room), not just the doc.
      if (deeplinkRef.current) url += (url.includes('#') ? '&' : '#') + deeplinkRef.current;
      // Best-effort host-side share sheet / clipboard. User activation rarely
      // survives the iframe→host hop, so widgets should render the returned
      // URL themselves (copy box / QR) and treat `open` as a bonus.
      if (args.open) void openShareSheet({ title, url });
      return { url, personal };
    });

    // workspace.set-deeplink — the widget publishes its current view as URL-hash
    // params; the node composes it into the fullscreen/share link so maximizing
    // (or copying a link) restores the exact view. Purely additive.
    reg.register(
      'workspace.set-deeplink',
      (raw: unknown): { deeplink: string | null } => {
        const o = (raw ?? {}) as { deeplink?: unknown };
        return { deeplink: o.deeplink == null ? null : String(o.deeplink).slice(0, 1024) };
      },
      async (args) => {
        deeplinkRef.current = args.deeplink;
        onDeeplinkChange?.(args.deeplink);
      },
    );

    // ui.drive-with — the widget asks the host to open the "Drive with"
    // dialog carrying a task the APP wrote.
    //
    // An app knows things the generic drive prompt cannot: which channel is
    // half-connected, which record is stuck, what the person was looking at.
    // Letting it supply that sentence turns "here is a tool, good luck" into
    // "help me finish connecting email", while the dialog still shows the
    // prompt and the person still picks the agent and presses go. The app
    // never gets to send anything on its own - it can only PROPOSE the words.
    reg.register(
      'ui.drive-with',
      (raw: unknown): { task: string | null } => {
        const o = (raw ?? {}) as { task?: unknown };
        // Bounded: this becomes prompt text a person is about to read — and
        // hand to an agent. Cut on a word boundary, never mid-word.
        const t = o.task == null ? null : clampDriveTask(String(o.task));
        return { task: t || null };
      },
      async (args) => {
        if (!onDriveWith) return { opened: false, reason: 'not_available_here' };
        onDriveWith(args.task);
        return { opened: true };
      },
    );

    // workspace.set-title — the widget publishes its open DOCUMENT's name so
    // the window chrome tracks it (the NodeFrame title otherwise shows the
    // widget's catalog name forever — "Vector Design" over an open "Seed
    // Deck"). Persisted in item state so it survives reload; null/empty
    // clears back to the widget name. Cosmetic only — never an access
    // surface. The standalone share view's header is server-owned, so the
    // op no-ops there.
    reg.register(
      'workspace.set-title',
      (raw: unknown): { title: string | null } => {
        const o = (raw ?? {}) as { title?: unknown };
        const t = o.title == null ? null : String(o.title).trim().slice(0, 120);
        return { title: t || null };
      },
      async (args) => {
        if (ephemeral) return;
        patchItemState(itemId, { docTitle: args.title });
      },
    );

    // --- documents.* family (host.documents — user-owned, shareable docs) ----
    // A widget saves the user's OWN file (e.g. a board "Save to my files") and
    // lists them for an opener. Distinct from content.* (per-instance, confined)
    // — routed through `/api/widgets/documents`, which creates a TOP-LEVEL row
    // under the caller + seeds the doc's `<docId>:MAIN` collab room.
    reg.register('documents.save', validateDocumentsSaveArgs, async (args) => {
      // CHECKPOINT (args.docId set) is allowed on the standalone `/w` surface;
      // only CREATE is not. Blocking both is what left share-only documents with
      // ZERO version history: an invited editor's live-room edits persist in the
      // CRDT room but never checkpoint, so a cold reopen re-seeds from a
      // snapshot that was never written, and there is nothing to restore when a
      // room loses content. The route authorizes this path itself (owner OR
      // canModifyContent) and needs no workspace/item context to do it — it is
      // CREATE that requires a real canvas instance, to adopt that instance's
      // project folder (ADR-0022) rather than mint a detached one.
      if (ephemeral && !args.docId) {
        throw new Error('documents.save (create) unavailable in share view — open the app on a workspace to save a new document');
      }
      // Inject canvas context (ADR-0022): on CREATE the route ADOPTS this
      // instance's project folder (so the doc joins any ephemeral media already
      // uploaded via host.content) instead of minting a detached one.
      const r = await fetchGated('/api/widgets/documents', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json', 'x-widget-origin': originTag },
        // When this save runs inside an agent-driven command (widget awaits it
        // before replying), credit the acting client on the version row
        // ("Claude Code (for <user>)" in history) — same attribution comments get.
        body: JSON.stringify({
          ...args,
          workspaceId,
          itemId,
          ...(agentCmdActorRef.current ? { actorName: agentCmdActorRef.current.name } : {}),
        }),
      });
      if (!r.ok) throw new Error(`documents.save ${r.status}`);
      const saved = (await r.json()) as { id: string; renamedId?: unknown };
      // A save whose `name` renamed the document tells this tab's workspace
      // labels, the way the tile settings, the Content panel and the Manage
      // drawer do after theirs (item-label.ts, 2026-09-13). Collab-kit apps
      // rename a saved document from their own title field through exactly
      // this op; the route renamed the project folder row but nothing here
      // said so, and windows opened on that folder kept the old name in the
      // rail, the Stage and the screen menus until a reload while the app's
      // own header already showed the new one. The route echoes the row it
      // renamed as `renamedId` (not `args.docId`: a body child resolves up to
      // its folder). The echo is host bookkeeping, so the app is handed the
      // result shape it always had.
      const renamedId = saved.renamedId;
      delete saved.renamedId;
      if (typeof renamedId === 'string') announceContentRenamed(renamedId, args.name);
      // Persist the doc binding onto the Yjs item so it survives reload — the
      // route no longer writes workspace_items.state. There is no canvas item to
      // bind on the standalone surface (the ids there are synthetic). The ref
      // is what the re-assert effect restores from when addItem's spawn-time
      // write drops this patch, and what `contentIdRef` prefers meanwhile.
      // `rebind: false` says the caller does not want this instance moved:
      // an app that mints documents as content (a notebook minting a page)
      // was rebound by every save and had to spend a second round trip
      // binding itself back, on the click a person is waiting through.
      if (saved.id && !ephemeral && args.rebind !== false) {
        // A REBINDING save moves the tile, and that is all it takes for the
        // next content op to address the saved project (P-20): the read rule
        // reuses a remembered folder only under the binding it was remembered
        // with, so moving the binding retires it with no shared write, no
        // scrub and nothing for another open client to answer.
        boundProjectRef.current = saved.id;
        patchItemState(itemId, { projectFolderId: saved.id });
        pushWorkspaceContext();
      }
      return saved;
    });

    // Save As → open the HOST "Save to…" dialog (destination folder + name). Portal
    // chrome over the canvas; the widget only learns the confirmed { parentId, name }.
    // Default destination = this workspace's content folder (a workspace IS a folder).
    reg.register('documents.saveDialog', validateDocumentsSaveDialogArgs, async (args) => {
      if (ephemeral) throw new Error('documents.saveDialog unavailable in share view');
      return await new Promise<{ parentId: string | null; name: string } | null>((resolve) => {
        window.dispatchEvent(
          new CustomEvent('widget-save-dialog', {
            detail: {
              defaultName: args.defaultName,
              defaultFolderId: workspaceId ?? null,
              title: args.defaultName ? `Save “${args.defaultName}”` : 'Save a copy',
              resolve,
            },
          }),
        );
      });
    });

    reg.register('documents.revisions', validateDocumentsRevisionsArgs, async (args) => {
      // READ op — no ephemeral short-circuit: the `/w` standalone view mounts
      // with `ephemeral` too, and the owner opening Version history there got
      // a hardcoded "No saved versions yet" for a document with real history.
      // The route enforces access (401/403 for anon/strangers → the kit's
      // catch shows the empty state), so passing through is safe; only the
      // WRITE ops (restore/rename/save) stay ephemeral-gated.
      const r = await fetch(`/api/widgets/documents/${encodeURIComponent(args.docId)}/revisions`, { method: 'GET', credentials: 'same-origin' });
      if (!r.ok) throw new Error(`documents.revisions ${r.status}`);
      const data = (await r.json()) as { items?: unknown[] };
      return data.items ?? [];
    });

    reg.register('documents.getRevision', validateDocumentsGetRevisionArgs, async (args) => {
      const r = await fetch(
        `/api/widgets/documents/${encodeURIComponent(args.docId)}/revisions?version=${args.version}`,
        { method: 'GET', credentials: 'same-origin' },
      );
      if (!r.ok) throw new Error(`documents.getRevision ${r.status}`);
      return (await r.json()) as { version: number; snapshot: string; createdAt: string };
    });

    reg.register('documents.restore', validateDocumentsRestoreArgs, async (args) => {
      // No ephemeral gate: the route is OWNER-ONLY server-side, and the owner
      // legitimately restores from the `/w` standalone view too (the gate made
      // the History menu's Restore a silent no-op there).
      const r = await fetchGated(`/api/widgets/documents/${encodeURIComponent(args.docId)}/revisions`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json', 'x-widget-origin': originTag },
        body: JSON.stringify({ version: args.version }),
      });
      if (!r.ok) throw new Error(`documents.restore ${r.status}`);
      return (await r.json()) as { version: number };
    });

    // documents.renameRevision — rename a checkpoint (history label only; the
    // route enforces write access: owner or write grant). No ephemeral gate —
    // invited editors curate history from the `/w` standalone view too, and a
    // read-only viewer is rejected by the route (403), not the bridge.
    reg.register('documents.renameRevision', validateDocumentsRenameRevisionArgs, async (args) => {
      const r = await fetchGated(`/api/widgets/documents/${encodeURIComponent(args.docId)}/revisions`, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json', 'x-widget-origin': originTag },
        body: JSON.stringify({ version: args.version, label: args.label ?? null }),
      });
      if (!r.ok) throw new Error(`documents.renameRevision ${r.status}`);
      return (await r.json()) as { version: number; label: string | null };
    });

    reg.register('documents.info', validateDocumentsInfoArgs, async (args) => {
      // READ — allowed in the ephemeral share view too. Hard-returning
      // { isDocument: false } here made every share-link viewer's widget
      // believe it wasn't looking at a document, which killed doc-name
      // adoption AND the viewer analytics tracker (which exists precisely
      // for share viewers). The API authorizes the read (canAccessContent);
      // writes (bind / restore / save) stay blocked in ephemeral mode.
      const r = await fetch(`/api/widgets/documents/${encodeURIComponent(args.docId)}`, { method: 'GET', credentials: 'same-origin' });
      if (!r.ok) {
        if (ephemeral) return { isDocument: false }; // no access → boot as a plain view, don't crash
        throw new Error(`documents.info ${r.status}`);
      }
      return (await r.json()) as { isDocument: boolean; name?: string; mime?: string; owned?: boolean; canWrite?: boolean };
    });

    // documents.read — BYTES with documents.info's breadth (viewer-accessible
    // content, session on the apex). Widget frames run on the SANDBOX origin
    // in production, so a widget's own fetch('/api/content/…') carries no
    // apex session there and fails for anything non-public — while passing on
    // dev's single origin. This op does that read HERE, on the apex, with the
    // user's session; the content route's own ACL stays the authority.
    // Soft-nulls (not-ok / oversize) so callers keep their legacy fallback.
    reg.register('documents.read', validateDocumentsReadArgs, async (args) => {
      const r = await fetch(`/api/content/${encodeURIComponent(args.docId)}`, {
        method: 'GET',
        credentials: 'same-origin',
      });
      if (!r.ok) return null;
      const buf = await r.arrayBuffer();
      if (buf.byteLength > 64 * 1024 * 1024) return null; // postMessage sanity cap
      const bytes = new Uint8Array(buf);
      let bin = '';
      const CHUNK = 0x8000;
      for (let i = 0; i < bytes.length; i += CHUNK) {
        bin += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + CHUNK)));
      }
      return { data: btoa(bin), mime: r.headers.get('content-type') ?? 'application/octet-stream' };
    });

    // documents.bind — scope THIS instance's host.content to a project FOLDER
    // (ADR-0022). Save binds on its own; OPEN calls this so the opener's media
    // hits the shared folder. Persists server-side AND updates the live refs so
    // host.content is correctly scoped immediately (no reload needed).
    reg.register('documents.bind', validateDocumentsBindArgs, async (args) => {
      if (ephemeral) return { ok: false, projectFolderId: null };
      const r = await fetchGated('/api/widgets/documents/bind', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json', 'x-widget-origin': originTag },
        body: JSON.stringify({ id: args.id, workspaceId, itemId }),
      });
      if (r.status === 404 && args.id) {
        // Dangling binding: the remembered folder is GONE (hard-deleted, or
        // minted mid-outage and never persisted — 2026-08-31). The id lives
        // in the item's Yjs state, so without this it re-404s on EVERY
        // mount. not_found is permanent (soft-deleted folders still
        // resolve), so unbind and run unbound; the next Save mints a fresh
        // project folder. Same shape the ephemeral branch returns. The media
        // folder remembered under the dead binding retires itself: the read
        // rule stops reusing it the moment the binding changes (P-20).
        boundProjectRef.current = null;
        patchItemState(itemId, { projectFolderId: undefined });
        pushWorkspaceContext();
        return { ok: false, projectFolderId: null };
      }
      if (!r.ok) throw new Error(`documents.bind ${r.status}`);
      const res = (await r.json()) as { ok: boolean; projectFolderId: string | null };
      if (res.ok) {
        // Bind and unbind both retire the remembered media folder (P-20)
        // without touching shared state: from here every content op follows
        // the new binding, or, unbound, the tile's own folder, never the
        // folder it uploaded into under the previous binding. The kit's unbind
        // on "New document" is the flow that needs it. NOTE what unbind
        // cannot do: for a project minted by a plain canvas create the server
        // still recovers that folder from its own `metadata.widgetItemId`
        // stamp (`findProjectFolderByWidgetItem`), which no `?project=` from
        // here overrides, so the blank document's uploads land there until the
        // app creates detached (P-06).
        boundProjectRef.current = res.projectFolderId;
        // Ref, not the memo's stale closure: `widgetSpec` resolves after the
        // registry is built (P-20).
        if (isProjectWidgetRef.current) contentIdRef.current = res.projectFolderId ?? contentIdRef.current;
        // Persist onto the Yjs item (route no longer writes workspace_items.state)
        // so the binding resolves on reload. undefined = unbind (the store
        // normalises undefined → null for the Yjs write).
        patchItemState(itemId, { projectFolderId: res.projectFolderId ?? undefined });
        pushWorkspaceContext();
      }
      return res;
    });

    // notifications.mention — ping @mentioned collaborators (in-app + web
    // push via the platform notification service). DEFAULT-ON per instance
    // (mentions are a core collab primitive), revocable via a
    // `!notifications` entry in state.permissions; the route is the real
    // gate (every target must share the doc/workspace, per-recipient rate
    // limits + sender muting apply). Scope + actor are injected HERE — the
    // widget only supplies targets and the comment snippet.
    reg.register('notifications.mention', validateNotifyMentionArgs, async (args) => {
      if (ephemeral) throw new Error('notifications unavailable in share view');
      if (deniedPermissions.includes('notifications')) {
        throw new Error('notifications are disabled for this app');
      }
      const r = await fetchGated('/api/widgets/notifications/mention', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json', 'x-widget-origin': originTag },
        body: JSON.stringify({
          targets: args.targets,
          snippet: args.snippet,
          docTitle: args.docTitle,
          threadId: args.threadId,
          workspaceId,
          itemId,
          projectId: contentIdRef.current ?? undefined,
          // Agent-driven mentions credit the acting client (same convention
          // as comments + version history).
          actorName: agentCmdActorRef.current?.name,
        }),
      });
      if (!r.ok) throw new Error(`notifications.mention ${r.status}`);
      return (await r.json()) as { ok: boolean; delivered: number };
    });

    // notifications.candidates — mentionable people/agents from the version
    // history (persistence tracks every editor), merged widget-side with
    // live awareness peers. Read-only; same scope injection as mention.
    reg.register('notifications.candidates', validateNotifyCandidatesArgs, async () => {
      if (ephemeral) return { users: [], agents: [] };
      const scope = contentIdRef.current ?? workspaceId;
      if (!scope) return { users: [], agents: [] };
      const q = contentIdRef.current
        ? `projectId=${encodeURIComponent(contentIdRef.current)}`
        : `workspaceId=${encodeURIComponent(workspaceId as string)}`;
      const r = await fetch(`/api/widgets/notifications/candidates?${q}`, { credentials: 'same-origin' });
      if (!r.ok) return { users: [], agents: [] };
      const res = (await r.json()) as { users?: Array<{ id: string; name: string }>; agents?: Array<{ name: string }> };
      return { users: res.users ?? [], agents: res.agents ?? [] };
    });

    // widget.event — host-side fan-out into the workspace.bus. The
    // wrapper API publishes a `widget` frame so other tabs / dock
    // tiles can react. Args are echoed verbatim; the bridge does
    // not introspect the payload (widgets define their own event
    // shapes).
    reg.register(
      'widget.event',
      (raw) => {
        if (raw === null || typeof raw !== 'object') {
          throw new Error('args must be an object');
        }
        return raw as { payload?: unknown };
      },
      async (args) => {
        // Ephemeral mount (share viewer): no workspace bus to fan out to,
        // so drop the event locally rather than POST to a synthetic
        // workspace (which would 500 on the non-UUID lookup).
        if (ephemeral) return;
        // A command result this bridge has already handed to its caller
        // (`locallyAnsweredRef`) is not relayed: no one else waits for it.
        const reply = args.payload as { kind?: unknown; replyId?: unknown } | null | undefined;
        if (
          reply && typeof reply === 'object' && reply.kind === 'cmd.result' &&
          typeof reply.replyId === 'string' && locallyAnsweredRef.current.delete(reply.replyId)
        ) {
          return;
        }
        // Plain `fetch` (NOT the signup-gated wrapper). `widget.event`
        // is a best-effort fan-out relay that fires on widget mount
        // (e.g. an agent greeting), so an anon visitor's 401 here must
        // be swallowed silently — routing it through `fetchGated`
        // would pop the signup dialog over the onboarding workspace
        // for a background event the user never triggered.
        await fetch(`/api/workspaces/${workspaceId}/items/${itemId}/events`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'content-type': 'application/json',
            'x-widget-origin': originTag,
          },
          body: JSON.stringify({ payload: args.payload ?? null }),
        }).catch(() => {
          /* events are fire-and-forget; the widget itself doesn't
           * await acknowledgement and the workspace.bus relay is
           * best-effort. */
        });
      },
    );

    // --- live.* family (host.live SDK primitive) --------------------
    // Each handler thin-wraps the matching POST endpoint under
    // /api/widgets/live/*. The endpoints handle authz, ACL,
    // cap-counting, and Redis I/O — the bridge just forwards the
    // validated args + cookie session and returns the JSON body
    // verbatim. Errors come back as `{ ok: false, error: { code,
    // message } }` and we re-throw with a typed message so the
    // OpRegistry surface ships the same shape that the SDK promises
    // in the wire contract.
    const liveJson = async (path: string, payload: unknown): Promise<unknown> => {
      const r = await fetchGated(path, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      let body: { ok?: boolean; error?: { code?: string; message?: string }; [k: string]: unknown };
      try {
        body = (await r.json()) as typeof body;
      } catch {
        throw new Error(`live op ${path}: invalid response (${r.status})`);
      }
      if (body.ok === false || !r.ok) {
        const err = body.error || { code: 'internal', message: `live op failed (${r.status})` };
        const message = err.message || err.code || 'internal';
        const e = new Error(`${err.code ?? 'internal'}: ${message}`) as Error & { status?: number; code?: string };
        e.status = r.status;
        e.code = err.code;
        throw e;
      }
      return body;
    };

    // Minting a collab token is IDEMPOTENT and sits on the critical open path:
    // if it fails, the app does not open at all. The room-join limiter is per
    // user and modest (20/window), and a reload storm or reconnect burst blows
    // through it easily — a page refresh, a widget __reload and the provider's
    // own retry all mint. Failing hard there turned a transient limit into
    // "Could not open: rate_limited: too many room joins; slow down" with no way
    // forward but waiting. Back off and retry instead; jitter keeps several tabs
    // from re-colliding in lockstep.
    const RETRYABLE = new Set([429, 502, 503, 504]);
    const liveJsonRetry = async (path: string, payload: unknown, attempts = 4): Promise<unknown> => {
      let lastErr: unknown;
      for (let i = 0; i < attempts; i++) {
        try {
          return await liveJson(path, payload);
        } catch (e) {
          lastErr = e;
          const status = (e as { status?: number }).status;
          if (!status || !RETRYABLE.has(status) || i === attempts - 1) throw e;
          const backoff = Math.min(500 * 2 ** i, 4000) * (0.7 + Math.random() * 0.6);
          await new Promise((res) => setTimeout(res, backoff));
        }
      }
      throw lastErr;
    };

    reg.register('live.publish', validateLivePublishArgs, async (args) => {
      const body = (await liveJson('/api/widgets/live/publish', args)) as { ok: true };
      return body;
    });
    reg.register('live.unpublish', validateLiveUnpublishArgs, async (args) => {
      const body = (await liveJson('/api/widgets/live/unpublish', args)) as { ok: true };
      return body;
    });
    reg.register('live.attach', validateLiveAttachArgs, async (args) => {
      return (await liveJson('/api/widgets/live/attach', args)) as {
        ok: true;
        attachId: string;
        transport: import('@/lib/uamp/types').LiveTransport;
        widgetId?: string;
        expiresAt: string;
      };
    });
    reg.register('live.release', validateLiveReleaseArgs, async (args) => {
      return (await liveJson('/api/widgets/live/release', args)) as { ok: true };
    });
    reg.register('live.url.resolve', validateLiveResolveArgs, async (args) => {
      return (await liveJson('/api/widgets/live/resolve', args)) as {
        ok: true;
        transport: import('@/lib/uamp/types').LiveTransport;
        widgetId?: string;
        expiresAt?: string;
      };
    });
    reg.register('live.heartbeat', validateLiveHeartbeatArgs, async (args) => {
      return (await liveJson('/api/widgets/live/heartbeat', args)) as {
        ok: true;
        expiresAt: string;
      };
    });

    // --- collab.* family (host.collab SDK primitive) ----------------
    // collab.get-token returns a short-lived Hocuspocus JWT plus ws-url +
    // TURN creds + the caller's participant identity. The widget connects
    // directly to the collab pod; the bridge does not proxy the websocket.
    //
    // INVARIANT: this op MUST stay LIVE in `ephemeral` (the `/w/<id>` share
    // path) — unlike kv/content/widget.event it has no `if (ephemeral) return`
    // short-circuit, because it never touches the synthetic share itemId (it
    // POSTs {scope,scopeId} to a cookie-authed route). Shared-link realtime
    // (collab-board / robongus lobbies) depends on this. Do not gate it.
    reg.register(
      'collab.get-token',
      (raw) => {
        if (!raw || typeof raw !== 'object') throw new Error('args must be an object');
        const o = raw as Record<string, unknown>;
        if (o.scope !== 'workspace' && o.scope !== 'item' && o.scope !== 'room') {
          throw new Error("scope must be 'workspace', 'item', or 'room'");
        }
        if (typeof o.scopeId !== 'string' || o.scopeId.length === 0 || o.scopeId.length > 128) {
          throw new Error('scopeId must be a non-empty string ≤ 128 chars');
        }
        return { scope: o.scope, scopeId: o.scopeId };
      },
      async (args) => {
        type Minted = {
          ok: true;
          token: string;
          wsUrl: string;
          expiresAt: string;
          roomId: string;
          identity: import('@/lib/collab/identity').ParticipantIdentity;
        };
        // ANONYMOUS SHARE VIEWER: `/w` mints a guest for a logged-out visitor
        // (ViewerGuestMint) while this widget boots in parallel — and the boot
        // usually wins, so the very first token request went out with no
        // session and came back 401. `liveJsonRetry` does not retry a 401 (it
        // is normally a real answer), and the widget treats it as fatal, so a
        // valid share link dead-ended on "Could not open: permission:
        // Unauthorized" one tick before the session existed. Wait for a mint
        // that has already been announced; a 401 after that is genuine.
        const waited = await awaitGuestMint();
        try {
          return (await liveJsonRetry('/api/widgets/collab/get-token', args)) as Minted;
        } catch (e) {
          // Nothing was in flight when we asked, but a mint may have been
          // announced a tick later — give that exactly one more chance.
          if ((e as { status?: number }).status === 401 && !waited && (await awaitGuestMint())) {
            return (await liveJsonRetry('/api/widgets/collab/get-token', args)) as Minted;
          }
          throw e;
        }
      },
    );

    // collab.turn-credentials — LAZY TURN/ICE creds, minted ONLY when a widget
    // actually starts a WebRTC call (host.collab.getTurnCredentials). Kept off
    // get-token so the TURN provider round-trip never lands on the per-mount
    // path of non-call collab widgets. Same LIVE-in-ephemeral invariant as
    // get-token (cookie-authed route, no synthetic itemId — do not gate).
    reg.register(
      'collab.turn-credentials',
      () => ({}),
      async () => {
        return (await liveJson('/api/widgets/collab/turn-credentials', {})) as {
          ok: true;
          turn: import('@/lib/collab/jwt').CollabTurnCredentials | null;
        };
      },
    );

    // --- rooms.* family (host.rooms, ADR-0030) ------------------------
    // Endpoint discovery for the game-rooms service PLUS the app identity,
    // answered by the HOST so the SDK never dials an app-named endpoint and
    // the room service is asked for THIS widget's declared rooms, not an
    // arbitrary `app` string (room.ts takes options.app verbatim; the token
    // carries no app claim). Same-origin fetch from the portal document, so
    // the apps-apex trap in app/api/widgets/rooms/config/route.ts never
    // applies here. Public route, no cookie needed (`credentials: 'omit'`).
    //
    // Answers { url, ops, app } with nulls, never an error, when the route is
    // down: the SDK turns that into join() === null ("multiplayer
    // unavailable") and does not cache it. Same LIVE-in-ephemeral posture as
    // collab.get-token: no synthetic itemId involved, so `/w/<id>` share
    // mounts get the same answer. This is not containment (the kit stays
    // publicly importable and an app can dial the service with a host.collab
    // token itself); it closes the accidental credential-to-app-chosen-
    // destination path and lets the host, not the app, say which app this is.
    reg.register('rooms.config', validateRoomsConfigArgs, async () => {
      if (!roomsEndpointsRef.current) {
        roomsEndpointsRef.current = (async () => {
          // An https host document can never dial ws:// (mixed content). A
          // proxy-blinded route may still hand back plain schemes, so harden
          // them here the way the kit's own discovery always did.
          const secure = typeof window !== 'undefined' && window.location.protocol === 'https:';
          const harden = (u: unknown): string | null => {
            if (typeof u !== 'string' || !u) return null;
            return secure ? u.replace(/^ws:\/\//, 'wss://').replace(/^http:\/\//, 'https://') : u;
          };
          try {
            const r = await fetch('/api/widgets/rooms/config', {
              credentials: 'omit',
              signal: AbortSignal.timeout(4000),
            });
            if (!r.ok) throw new Error(`rooms config ${r.status}`);
            const j = (await r.json()) as { url?: unknown; ops?: unknown };
            return { url: harden(j.url), ops: harden(j.ops) };
          } catch {
            roomsEndpointsRef.current = null; // transient: let the next call retry
            return { url: null, ops: null };
          }
        })();
      }
      const ep = await roomsEndpointsRef.current;
      return { url: ep.url, ops: ep.ops, app: widgetTypeRef.current ?? null };
    });

    // --- agent.* family (host.agents / host.get('agent', …)) ---------
    // Connected-agent surface: UAMP turns (SSE-proxied to the canonical
    // endpoint), chat history reads, non-secret config + capabilities.
    // Each handler gates on ctx.grantedResources AND runs as the viewer
    // against a server route that authorizes ownership/participation.
    registerAgentOps(reg);

    // --- fn.invoke (host.fn) ---------------------------------------
    // Invoke a custom function through the WIDGET-scoped route (never
    // /api/agents directly): the route resolves this instance → its
    // bundle-host agent + owner, gates the target agent, attributes
    // billing/quota to the OWNER (consumerId) while auth stays the
    // viewer, and reuses the dispatcher/executor core. `ref` is a bare
    // endpoint name (own host agent) or "<agent>/<endpoint>" (system
    // robutler / granted only — the route enforces). Routed through
    // `fetchGated` so a `signup_required` 401 pops the login dialog.
    reg.register('fn.invoke', validateFnInvokeArgs, async (args) => {
      const slash = args.ref.indexOf('/');
      const agent = slash >= 0 ? args.ref.slice(0, slash) : null;
      const endpoint = slash >= 0 ? args.ref.slice(slash + 1) : args.ref;
      const r = await fetchGated(
        `/api/widgets/${encodeURIComponent(itemId)}/fn/${encodeURIComponent(endpoint)}`,
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(agent ? { args: args.args ?? {}, agent } : { args: args.args ?? {} }),
        },
      );
      if (!r.ok) {
        const code =
          r.status === 404
            ? 'not_found'
            : r.status === 401 || r.status === 403
              ? 'permission'
              : r.status === 429
                ? 'rate_limited'
                : r.status === 504
                  ? 'timeout'
                  : 'internal';
        // A function's own refusal is DATA the caller needs. The error frame
        // carries only { code, message }, so fold the body INTO the message
        // rather than dropping it: discarding it turned a perfectly clear
        // `{ ok:false, error:'no_folder' }` into a bare 400 that every caller
        // reported as a server fault, and sent us hunting an outage that was
        // really an app not yet bound to a project folder.
        // JSON only, and capped - an HTML error page or a giant body must not
        // become the message.
        const detail = await r
          .text()
          .then((t) => t.trim().slice(0, 400))
          .catch(() => '');
        const e = new Error(
          `fn.invoke ${args.ref} (${r.status})${detail.startsWith('{') ? ` ${detail}` : ''}`,
        ) as Error & { code?: string };
        e.code = code;
        throw e;
      }
      // Functions return their JSON body verbatim.
      return await r.json().catch(() => null);
    });

    // --- discover (host.discover) ----------------------------------
    // Platform discovery, MCP `search` parity. Reuses the existing
    // /api/discovery route (same-origin, viewer session) → executeDiscovery.
    reg.register('discover', validateDiscoverArgs, async (args) => {
      /* THE NAMESPACE IS NEVER STAMPED HERE (S-129, 2026-09-16). This used to
         stamp the verified widget type, so that an app searched its own
         namespace and never another's, and scoping enforced on the client is
         not scoping: `/api/discovery` could not tell a bridge call from any
         other signed-in caller. The field was then stripped, which closed the
         hole by closing the feature, until the route learned to gate a named
         namespace itself (ADR 0043). */
      /* PASSED THROUGH SINCE 2026-09-18, because the route is now the gate.
         `/api/discovery` checks a named namespace against the resolver the
         object's owner attached, for the person asking, and answers 403 or
         409 when that resolver has not admitted them
         (`checkNamespaceAccess`, ADR 0043). The strip above protected a route
         that could not tell who was asking; this one can, and asks. The
         validator admits only a lowercase uuid. Revent searches its week room
         this way. */
      const scoped = { ...(args as Record<string, unknown>) };
      const r = await fetchGated('/api/discovery', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(scoped),
      });
      if (!r.ok) {
        const code =
          r.status === 401 || r.status === 403
            ? 'permission'
            : r.status === 429
              ? 'rate_limited'
              : 'internal';
        const e = new Error(`discover (${r.status})`) as Error & { code?: string };
        e.code = code;
        throw e;
      }
      return await r.json();
    });

    /* host.agent.rail — attach this app's own HTTP rail, with a key the app
       just minted, to the PERSON'S OWN agent for this app, so that agent keeps
       working when the app is shut. The app names only the function and the
       key; the APP ITSELF is stamped here from the verified widget type, and
       the agent and the URL are resolved by the route. See
       lib/workspaces/widget-ops/agent-rail.ts for why each of those has to be
       out of the app's hands. Refused outright when the widget type is not
       known (a share view, a project folder): there is no app to be the agent
       OF, and guessing one would provision the wrong thing. */
    /* host.agent.mine — the person's own agent for THIS app (ADR 0042), minted
       on the first ask. The app is stamped here from the verified widget type;
       the person is the session. There is nothing for the app to name, which is
       why the op takes no arguments at all. */
    /* THE SERVER'S REASON REACHES THE CALLER (2026-09-16 browser pass). These
       handlers read only `r.status` and threw `agent.rail (400)`, so the one
       sentence that said what was actually wrong,
       `invalid_patch: Unrecognized key(s) in object: 'openapi'`, never left
       the network tab. A round of verification was spent rediscovering by
       hand what the response had already said. Bounded and best-effort: a body
       that is not JSON, or not there, costs nothing. */
    const reasonOf = async (r: Response): Promise<string> => {
      try {
        const body = (await r.clone().json()) as { error?: unknown; details?: unknown };
        const parts = [body?.error, body?.details].filter((x): x is string => typeof x === 'string' && !!x);
        return parts.length ? `: ${parts.join(' - ').slice(0, 200)}` : '';
      } catch { return ''; }
    };

    reg.register('agent.mine', validateAgentMineArgs, async () => {
      const widgetType = widgetTypeRef.current;
      if (!widgetType) {
        const e = new Error('agent.mine: this surface has no app type') as Error & { code?: string };
        e.code = 'not_found';
        throw e;
      }
      const r = await fetchGated('/api/widgets/agent', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ widgetType, contentId: appContentIdRef.current ?? null }),
      });
      if (!r.ok) {
        const code = r.status === 401 || r.status === 403 ? 'permission' : r.status === 404 ? 'not_found' : r.status === 429 ? 'rate_limited' : 'internal';
        const e = new Error(`agent.mine (${r.status})${await reasonOf(r)}`) as Error & { code?: string };
        e.code = code;
        throw e;
      }
      return await r.json();
    });

    /* One turn to the person's own agent for THIS app. The type and the frame's
       row are stamped here exactly as in `agent.mine`, and the route resolves
       the agent from them and the session: the app sends words and nothing
       else, so it has no way to reach any other agent. */
    reg.register('agent.mine.turn', validateAgentMineTurnArgs, async (args) => {
      const widgetType = widgetTypeRef.current;
      if (!widgetType) {
        const e = new Error('agent.mine.turn: this surface has no app type') as Error & { code?: string };
        e.code = 'not_found';
        throw e;
      }
      const r = await fetchGated('/api/widgets/agent/turn', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ widgetType, contentId: appContentIdRef.current ?? null, message: (args as { message: string }).message }),
      });
      if (!r.ok) {
        const code = r.status === 401 || r.status === 403 ? 'permission' : r.status === 404 ? 'not_found' : r.status === 429 ? 'rate_limited' : 'internal';
        const e = new Error(`agent.mine.turn (${r.status})${await reasonOf(r)}`) as Error & { code?: string };
        e.code = code;
        throw e;
      }
      return await r.json();
    });

    /* host.agent.intents — what this person is looking for INSIDE this app,
       published app-scoped so other people's agents for the same app can find
       it and the platform-wide index never sees it. The app is stamped here,
       as everywhere in this family, so an app cannot publish into another
       app's namespace. */
    reg.register('agent.intents', validateAgentIntentsArgs, async (args) => {
      const widgetType = widgetTypeRef.current;
      if (!widgetType) {
        const e = new Error('agent.intents: this surface has no app type') as Error & { code?: string };
        e.code = 'not_found';
        throw e;
      }
      const a = args as { goals: string[]; objectId?: string };
      const r = await fetchGated('/api/widgets/agent/intents', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ widgetType, contentId: appContentIdRef.current ?? null, goals: a.goals, objectId: a.objectId }),
      });
      if (!r.ok) {
        const code = r.status === 401 || r.status === 403 ? 'permission' : r.status === 404 ? 'not_found' : r.status === 429 ? 'rate_limited' : 'internal';
        const e = new Error(`agent.intents (${r.status})${await reasonOf(r)}`) as Error & { code?: string };
        e.code = code;
        throw e;
      }
      return await r.json();
    });

    /* The inbox inside an app (2026-09-24, Revent's in-app inbox;
       lib/workspaces/widget-ops/agent-inbox.ts, docs/internal/architecture/
       revent-inbox-build-plan.md section 6). Every op here acts on "this
       person's own agent for this app": the app is stamped from the verified
       widget type and the frame's row, exactly as `agent.mine` above, and the
       routes resolve the agent from those and the session. Nothing an app
       passes can point at another agent.

       THE HOST ASKS, NOT THE APP. Accepting, declining or blocking a sender,
       and sending any reply, each open `WidgetInboxConsent`, and the dialog
       is filled from the platform's own read of the item or the draft, never
       from the app's arguments. What the app learns is the outcome. */
    const inboxFail = async (op: string, r: Response) => {
      const e = new Error(`${op} (${r.status})${await reasonOf(r)}`) as Error & { code?: string };
      e.code = r.status === 400 ? 'invalid_args'
        : r.status === 401 || r.status === 403 ? 'permission'
          : r.status === 404 ? 'not_found'
            : r.status === 429 ? 'rate_limited'
              : 'internal';
      return e;
    };
    // Only the apps that have an inbox (lib/inbox/apps.ts). The routes check
    // this again; here it also keeps any other app from opening the dialog.
    const inboxApp = (op: string): { widgetType: string; contentId: string | null } => {
      const widgetType = widgetTypeRef.current;
      if (!widgetType || !INBOX_APPS.has(widgetType)) {
        const e = new Error(`${op}: this app has no inbox`) as Error & { code?: string };
        e.code = 'not_found';
        throw e;
      }
      return { widgetType, contentId: appContentIdRef.current ?? null };
    };
    const inboxQuery = (app: { widgetType: string; contentId: string | null }, extra: Record<string, string> = {}) => {
      const q = new URLSearchParams({ widgetType: app.widgetType, ...(app.contentId ? { contentId: app.contentId } : {}), ...extra });
      return q.toString();
    };
    const inboxPost = (url: string, body: Record<string, unknown>) =>
      fetchGated(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
    const askHost = (request: InboxConsentRequest) =>
      new Promise<boolean>((resolve) => {
        window.dispatchEvent(new CustomEvent(INBOX_CONSENT_EVENT, { detail: { ...request, resolve } }));
      });

    reg.register('agent.inbox.list', validateNoArgs, async () => {
      const app = inboxApp('agent.inbox.list');
      const r = await fetchGated(`/api/widgets/agent/inbox?${inboxQuery(app)}`, { credentials: 'same-origin' });
      if (!r.ok) throw await inboxFail('agent.inbox.list', r);
      return await r.json();
    });

    reg.register('agent.inbox.get', validateAgentInboxGetArgs, async (args) => {
      const app = inboxApp('agent.inbox.get');
      const r = await fetchGated(
        `/api/widgets/agent/inbox/item?${inboxQuery(app, { id: args.id, ...(args.reveal ? { reveal: '1' } : {}) })}`,
        { credentials: 'same-origin' },
      );
      if (!r.ok) throw await inboxFail('agent.inbox.get', r);
      const body = (await r.json()) as Record<string, unknown>;
      // The agent's name was fetched for the host's dialog; the app has its own.
      delete body.agent;
      return body;
    });

    reg.register('agent.inbox.decide', validateAgentInboxDecideArgs, async (args) => {
      const app = inboxApp('agent.inbox.decide');
      if (INBOX_DECISIONS_NEEDING_CONSENT.has(args.action)) {
        const got = await fetchGated(`/api/widgets/agent/inbox/item?${inboxQuery(app, { id: args.id })}`, { credentials: 'same-origin' });
        if (!got.ok) throw await inboxFail('agent.inbox.decide', got);
        const detail = (await got.json()) as {
          item: { kind: string; who: string; verified: boolean; subject: string | null };
          agent?: { displayName?: string };
        };
        // Only a first contact is accepted, declined or blocked; the route
        // refuses the rest, and a dialog for it would ask a question that
        // has no answer.
        if (detail.item.kind !== 'first_contact') return { ok: false, error: 'not_allowed' } as const;
        const confirmed = await askHost({
          kind: 'decide',
          action: args.action as 'accept' | 'decline' | 'block',
          appName: title ?? 'This app',
          agentName: detail.agent?.displayName || title || 'your agent',
          who: detail.item.who,
          verified: detail.item.verified,
          subject: detail.item.subject,
        });
        if (!confirmed) return { ok: false, error: 'declined' } as const;
      }
      const r = await inboxPost('/api/widgets/agent/inbox/act', { ...app, id: args.id, action: args.action });
      if (!r.ok) throw await inboxFail('agent.inbox.decide', r);
      return await r.json();
    });

    /* THE HOST HOLDS THE CHAT ID. The ask and thread routes answer the DM's
       chat id; it is kept here for the relay's filter and never read back
       from anything the app sends (the validators refuse a `chatId`). */
    const holdAskChat = (id: unknown) => {
      if (typeof id !== 'string' || !isUuidId(id)) return;
      askChatIdRef.current = id;
      askRelayRef.current?.setChatId(id);
    };

    /* THE HOST STAMPS THE SURFACE (2026-09-29, the round 8 dev run;
       revent-in-app-chat.md "Platform changes" 1). The workspace id (a canvas,
       or `share-<contentId>` on `/w/`), this tab's id and the item id ride the
       ask into the agent's run, so its `workspace.widgets.*` tools land on
       the tab the person is looking at. All three come from what this bridge
       knows; `validateAgentAskArgs` refuses them as arguments, so an app
       cannot aim its agent at a tab it does not own. `context` is the app's
       agent-facing words, capped, shown to the person as a chip. */
    reg.register('agent.ask', validateAgentAskArgs, async (args) => {
      const app = inboxApp('agent.ask');
      const r = await inboxPost('/api/widgets/agent/ask', {
        ...app,
        text: args.text,
        ...(args.about ? { about: args.about } : {}),
        ...(args.context ? { context: args.context } : {}),
        workspaceId,
        itemId,
        ...(readWorkspaceTabId() ? { workspaceTabId: readWorkspaceTabId() } : {}),
      });
      if (!r.ok) throw await inboxFail('agent.ask', r);
      const body = (await r.json()) as { chatId?: unknown };
      holdAskChat(body.chatId);
      return body;
    });

    reg.register('agent.ask.thread', validateAgentAskThreadArgs, async (args) => {
      const app = inboxApp('agent.ask.thread');
      const r = await fetchGated(`/api/widgets/agent/ask?${inboxQuery(app, args.before ? { before: args.before } : {})}`, { credentials: 'same-origin' });
      if (!r.ok) throw await inboxFail('agent.ask.thread', r);
      const body = (await r.json()) as { chatId?: unknown };
      holdAskChat(body.chatId);
      return body;
    });

    /* THE STREAM (revent-in-app-chat.md "Platform changes" 3). A watch is a
       push channel; the frames come from the UAMP effect below, which feeds
       this mount's `AskRelay` from the host page's own socket. The relay
       filters on the chat id held above and projects to four frame shapes:
       never a tool's arguments or result, thinking, media, a user id or a
       chat id. With no chat id held yet, the thread is read once so a watch
       opened before the first ask still receives the answer to it. */
    reg.register('agent.ask.watch', validateNoArgs, async () => {
      const app = inboxApp('agent.ask.watch');
      if (!askChatIdRef.current) {
        const r = await fetchGated(`/api/widgets/agent/ask?${inboxQuery(app)}`, { credentials: 'same-origin' });
        if (!r.ok) throw await inboxFail('agent.ask.watch', r);
        holdAskChat(((await r.json()) as { chatId?: unknown }).chatId);
      }
      const subId = `ask:${Date.now().toString(36)}:${Math.random().toString(36).slice(2, 10)}`;
      askWatchersRef.current.add(subId);
      return { subId };
    });

    reg.register('agent.ask.unwatch', validateAgentAskUnwatchArgs, (args) => {
      askWatchersRef.current.delete(args.subId);
      return { ok: true } as const;
    });

    /* STOP (revent-in-app-chat.md "Platform changes" 4): no argument; the
       route resolves the conversation from the session and the app. */
    reg.register('agent.ask.stop', validateNoArgs, async () => {
      const app = inboxApp('agent.ask.stop');
      const r = await inboxPost('/api/widgets/agent/ask/stop', { ...app });
      if (!r.ok) throw await inboxFail('agent.ask.stop', r);
      return await r.json();
    });

    /* A FRONT-DOOR CARD (2026-10-05, revent-agentic-front-door-plan.md lane
       L2): the app's own identity, the server-minted card id and one action
       word, nothing else; the route finds the card inside this person's own
       thread and refuses any other. Accept and decline of a connection never
       come through here (the person's tap in the app, or the `decide`
       dialog above). */
    reg.register('agent.ask.card', validateAgentAskCardArgs, async (args) => {
      const app = inboxApp('agent.ask.card');
      const r = await inboxPost('/api/widgets/agent/ask/card', { ...app, id: args.id, action: args.action });
      if (!r.ok) throw await inboxFail('agent.ask.card', r);
      return await r.json();
    });

    // One way out for any reply: the draft as the platform holds it, read
    // back here, shown in the host's dialog with the EXACT text, then sent
    // with that same string. `edit` is the person's edit when the app passed
    // one. Used by `sendDraft` (the agent wrote it) and `reply` (they did).
    const sendWithConsent = async (op: string, app: { widgetType: string; contentId: string | null }, draftId: string, edit?: string) => {
      const got = await fetchGated(`/api/widgets/agent/drafts?${inboxQuery(app, { draftId })}`, { credentials: 'same-origin' });
      if (!got.ok) throw await inboxFail(op, got);
      const { draft, agent } = (await got.json()) as {
        draft: { who: string; transport: 'portal' | 'smtp'; text: string };
        agent?: { displayName?: string };
      };
      const text = (typeof edit === 'string' && edit.trim() ? edit : draft.text).trim();
      if (!text) return { ok: false, error: 'empty' } as const;
      const confirmed = await askHost({
        kind: 'send',
        appName: title ?? 'This app',
        agentName: agent?.displayName || title || 'your agent',
        who: draft.who,
        transport: draft.transport,
        text,
      });
      if (!confirmed) return { ok: false, error: 'declined', draftId } as const;
      const r = await inboxPost('/api/widgets/agent/drafts', { ...app, draftId, op: 'send', text });
      if (r.status === 409) return { ok: false, error: 'no_turn', draftId } as const;
      if (!r.ok) throw await inboxFail(op, r);
      return { ok: true } as const;
    };

    reg.register('agent.inbox.sendDraft', validateAgentInboxSendDraftArgs, async (args) => {
      const app = inboxApp('agent.inbox.sendDraft');
      return sendWithConsent('agent.inbox.sendDraft', app, args.draftId, args.text);
    });

    /* The person's own reply, typed in the app. Held as a draft first so it
       leaves by the same door as one the agent wrote. A reply the person
       then turns down in the dialog is thrown away, not left in the list. */
    reg.register('agent.inbox.reply', validateAgentInboxReplyArgs, async (args) => {
      const app = inboxApp('agent.inbox.reply');
      const made = await inboxPost('/api/widgets/agent/drafts', { ...app, op: 'create', conversation: args.id, text: args.text });
      if (!made.ok) throw await inboxFail('agent.inbox.reply', made);
      const { draftId } = (await made.json()) as { draftId: string };
      const r = await sendWithConsent('agent.inbox.reply', app, draftId);
      if (!r.ok && r.error === 'declined') {
        await inboxPost('/api/widgets/agent/drafts', { ...app, draftId, op: 'discard' }).catch(() => null);
      }
      return r.ok ? r : { ok: false, error: r.error };
    });

    reg.register('agent.inbox.discardDraft', validateAgentInboxDiscardDraftArgs, async (args) => {
      const app = inboxApp('agent.inbox.discardDraft');
      const r = await inboxPost('/api/widgets/agent/drafts', { ...app, draftId: args.draftId, op: 'discard' });
      if (!r.ok) throw await inboxFail('agent.inbox.discardDraft', r);
      return { ok: true } as const;
    });

    /* A FIRST EMAIL (ADR 0050, 2026-10-04; lib/inbox/first-mail.ts). The
       platform's read says whether this person may write to this address
       now and from which name and address it would go; the app learns only
       `available`, never the From. `send` reads the same answer, draws it
       with the app's exact To, subject and text in the host's `letter`
       dialog, and posts THE SAME STRINGS only on the person's Send. The
       route matches `to` against the operator's list again and counts the
       cap; a 429 is the one failure the app is told by name. */
    const mailAvailability = async (op: string, app: { widgetType: string; contentId: string | null }, to: string) => {
      const r = await fetchGated(`/api/widgets/agent/mail?${inboxQuery(app, { to })}`, { credentials: 'same-origin' });
      if (!r.ok) throw await inboxFail(op, r);
      return (await r.json()) as { available?: unknown; from?: unknown; fromName?: unknown; reason?: unknown };
    };

    reg.register('agent.mail.check', validateAgentMailCheckArgs, async (args) => {
      const app = inboxApp('agent.mail.check');
      const a = await mailAvailability('agent.mail.check', app, args.to);
      // `from` and `fromName` are the host's; the app gets the bit and, on a
      // no, the platform's reason from a closed list (the owner, 2026-10-05:
      // the app words each one). Anything else the route says is no reason.
      if (a.available === true) return { available: true } as const;
      const reason = mailCheckReason(a.reason);
      return reason ? ({ available: false, reason } as const) : ({ available: false } as const);
    });

    reg.register('agent.mail.send', validateAgentMailSendArgs, async (args) => {
      const app = inboxApp('agent.mail.send');
      const a = await mailAvailability('agent.mail.send', app, args.to);
      if (a.available !== true || typeof a.from !== 'string' || !a.from) return { ok: false, error: 'not_available' } as const;
      const confirmed = await askHost({
        kind: 'letter',
        appName: title ?? 'This app',
        agentName: title ?? 'your agent',
        to: args.to,
        fromName: typeof a.fromName === 'string' ? a.fromName : '',
        fromAddress: a.from,
        subject: args.subject,
        text: args.text,
      });
      if (!confirmed) return { ok: false, error: 'declined' } as const;
      const r = await inboxPost('/api/widgets/agent/mail', { ...app, to: args.to, subject: args.subject, text: args.text });
      if (r.status === 429) return { ok: false, error: 'cap_reached' } as const;
      if (!r.ok) throw await inboxFail('agent.mail.send', r);
      return { ok: true } as const;
    });

    /* host.rpc('email.brief'): whether this app may email the person
       (lib/workspaces/widget-ops/app-email.ts, lib/email/app-mail.ts). The
       app is the APP ROW running in this frame, which the route checks is an
       official one (S-173). Turning mail ON opens the platform's own dialog first
       (components/workspace/widget-app-email.tsx) and writes nothing unless
       the person confirms there; the address is shown to them in it and never
       returned to the app. Off needs no dialog. */
    reg.register('email.brief', validateEmailBriefArgs, async (args) => {
      const widgetType = widgetTypeRef.current;
      const contentId = appContentIdRef.current;
      if (ephemeral || !widgetType || !contentId) return { ok: false, error: 'not_available_here' } as const;
      const failed = async (r: Response, what: string) => {
        const e = new Error(`email.brief ${what} (${r.status})${await reasonOf(r)}`) as Error & { code?: string };
        e.code = r.status === 401 || r.status === 403 ? 'permission' : r.status === 429 ? 'rate_limited' : 'internal';
        return e;
      };
      const q = `/api/widgets/email?widgetType=${encodeURIComponent(widgetType)}&contentId=${encodeURIComponent(contentId)}`;
      const got = await fetchGated(q, { credentials: 'same-origin' });
      if (!got.ok) throw await failed(got, 'read');
      const state = (await got.json()) as { allowed: boolean; on: boolean; canEmail: boolean; addressHint: string | null; appName?: string };
      if (!state.allowed) return { ok: false, error: 'not_allowed' } as const;
      if (args.on === undefined) return { ok: true, on: state.on, canEmail: state.canEmail } as const;
      if (args.on === true) {
        if (state.on) return { ok: true, on: true, canEmail: state.canEmail } as const;
        if (!state.canEmail) return { ok: false, error: 'no_verified_address' } as const;
        if (emailDeclinedRef.current) return { ok: false, error: 'declined' } as const;
        const confirmed = await new Promise<boolean>((resolve) => {
          window.dispatchEvent(
            new CustomEvent('widget-app-email', {
              detail: { appName: state.appName || 'This app', addressHint: state.addressHint || '', resolve },
            }),
          );
        });
        if (!confirmed) { emailDeclinedRef.current = true; return { ok: false, error: 'declined' } as const; }
      }
      const put = await fetchGated('/api/widgets/email', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ widgetType, contentId, on: args.on }),
      });
      if (put.status === 409) return { ok: false, error: 'no_verified_address' } as const;
      if (put.status === 403) return { ok: false, error: 'not_allowed' } as const;
      if (!put.ok) throw await failed(put, 'write');
      return { ok: true, on: args.on, canEmail: state.canEmail } as const;
    });

    reg.register('agent.rail', validateAgentRailArgs, async (args) => {
      const widgetType = widgetTypeRef.current;
      if (!widgetType) {
        const e = new Error('agent.rail: this surface has no app type') as Error & { code?: string };
        e.code = 'not_found';
        throw e;
      }
      const a = args as { fn: string; key: string };
      const r = await fetchGated('/api/widgets/agent/rail', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ widgetType, contentId: appContentIdRef.current ?? null, fn: a.fn, key: a.key }),
      });
      if (!r.ok) {
        const code =
          r.status === 401 || r.status === 403
            ? 'permission'
            : r.status === 404
              ? 'not_found'
              : r.status === 429
                ? 'rate_limited'
                : 'internal';
        const e = new Error(`agent.rail (${r.status})${await reasonOf(r)}`) as Error & { code?: string };
        e.code = code;
        throw e;
      }
      return await r.json();
    });

    /* The app offers to decide who may be found inside one of this person's
       own items. Same shape as the two ops above: the app names one of ITS OWN
       functions, the server resolves everything else, and `widgetType` is the
       host's knowledge of which iframe this is rather than anything the app
       said. */
    reg.register('namespace.resolver', validateNamespaceResolverArgs, async (args) => {
      const widgetType = widgetTypeRef.current;
      if (!widgetType) {
        const e = new Error('namespace.resolver: this surface has no app type') as Error & { code?: string };
        e.code = 'not_found';
        throw e;
      }
      const a = args as { objectId: string; fn?: string; clear?: boolean };
      const r = await fetchGated('/api/widgets/namespace/resolver', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ widgetType, contentId: appContentIdRef.current ?? null, objectId: a.objectId, fn: a.fn, clear: a.clear === true }),
      });
      if (!r.ok) {
        const code =
          r.status === 401 || r.status === 403 || r.status === 409
            ? 'permission'
            : r.status === 404
              ? 'not_found'
              : r.status === 429
                ? 'rate_limited'
                : 'internal';
        const e = new Error(`namespace.resolver (${r.status})${await reasonOf(r)}`) as Error & { code?: string };
        e.code = code;
        throw e;
      }
      return (await r.json()) as unknown;
    });

    // --- files.* family (host.files — the viewer's library as a filesystem) --
    // GRANT-GATED: spans the viewer's whole library, so it is never ambient
    // (that would be an exfiltration + destruction surface — the reason
    // content.pick exists). It rides the standard per-widget-instance
    // permission pipeline (lib/workspaces/widget-permissions.ts): the widget
    // DECLARES `files` in its manifest and ASKS via
    // `host.requestPermission('files')` (or `host.files.request()`), the
    // owner approves the notification, the grant lands in
    // `state.permissions`, and the host re-keys this bridge — exactly like
    // camera/microphone, except `files` is enforced HERE at the bridge, not
    // via the iframe Permissions-Policy. Any widget (first-party Files app,
    // its remixes, community apps) may request; nothing is granted without
    // the user approving that specific instance. NOTE: no `external` check —
    // folder bundles resolve as `iframe-external` (the sandbox subdomain is
    // a foreign origin by design), and the grant pipeline already treats
    // external iframes as grantable (camera works the same way); the user's
    // per-instance consent is the boundary, not the serving origin. Handlers
    // hit the SAME /api/content* REST the Content panel uses, as the
    // signed-in viewer — no server-side privilege beyond the standard ACL.
    // Denied in the ephemeral `/w/` share view (no item state to grant on).
    //
    // Grant tokens: `files` = entire library; `files:<folderId>` = ONE
    // folder subtree (picked in the host access dialog). Scoped mode is
    // confined by construction: the widget's "root" is remapped to the
    // granted folder, and every op is limited to ids the bridge itself has
    // SERVED from listings inside that subtree (`filesServed`, the
    // content.pick grant-set pattern) — a widget cannot reach an id it was
    // never shown. `grantTokens` derives from grantSig (stable string), so
    // this block — and the served-set — only rebuilds when grants change.
    const grantTokens = grantSig ? grantSig.split(',') : [];
    const filesGrantToken = grantTokens.find((g) => g === 'files' || g.startsWith('files:'));
    const filesScopeRootId =
      filesGrantToken && filesGrantToken.startsWith('files:')
        ? filesGrantToken.slice('files:'.length)
        : null;
    const filesGranted = !ephemeral && !!filesGrantToken;
    const filesServed = new Set<string>();
    if (filesScopeRootId) filesServed.add(filesScopeRootId);

    const filesDenied = () => {
      const e = new Error(
        "host.files requires the 'files' permission — request it with host.files.request() and have the user approve",
      ) as Error & { code?: string };
      e.code = 'permission';
      return e;
    };
    /** Scoped-grant confinement: op targets must have been served from inside the granted subtree. */
    const filesCheckId = (id: string) => {
      if (filesScopeRootId && !filesServed.has(id)) throw filesDenied();
    };
    /** Widget-root (`null`) remaps to the granted folder in scoped mode. */
    const filesResolveParent = (parentId: string | null): string | null => {
      if (!filesScopeRootId) return parentId;
      if (parentId === null) return filesScopeRootId;
      filesCheckId(parentId);
      return parentId;
    };
    const filesHttpError = (op: string, status: number) => {
      const e = new Error(`${op} ${status}`) as Error & { code?: string };
      e.code = status === 404 ? 'not_found' : status === 401 || status === 403 ? 'permission' : status === 413 ? 'quota_exceeded' : status === 429 ? 'rate_limited' : 'internal';
      return e;
    };
    /** Trim a /api/content row to the wire shape (lib/workspaces/widget-ops/files.ts). */
    const filesRow = (row: Record<string, unknown>): FilesEntry => {
      const md = (row.metadata ?? {}) as Record<string, unknown>;
      const bundle = md.bundle as Record<string, unknown> | undefined;
      const out: FilesEntry = {
        id: String(row.id),
        name: String(row.displayName ?? row.originalName ?? 'Untitled'),
        type: String(row.contentType ?? 'file'),
        mime: String(row.mimeType ?? 'application/octet-stream'),
        size: typeof row.size === 'number' ? row.size : Number(row.size ?? 0) || 0,
        parentId: (row.parentId as string | null) ?? null,
        createdAt: String(row.createdAt ?? ''),
        updatedAt: String(row.updatedAt ?? row.createdAt ?? ''),
      };
      if (typeof md.widgetType === 'string') out.widgetType = md.widgetType;
      if (typeof md.projectMime === 'string') out.projectMime = md.projectMime;
      if (bundle && bundle.kind === 'folder') out.bundle = true;
      if (md.hidden === 'true' || md.hidden === true) out.hidden = true;
      if (typeof row.previewUrl === 'string') out.previewUrl = row.previewUrl;
      if (typeof row.effectiveVisibility === 'string') out.visibility = row.effectiveVisibility;
      return out;
    };

    reg.register('files.available', validateFilesAvailableArgs, async () => {
      if (!filesGranted) return { ok: false, reason: ephemeral ? 'share_view' : 'not_granted' };
      if (!filesScopeRootId) return { ok: true, scope: null };
      // Scoped grant — resolve the folder's display name once (crumb label).
      if (filesScopeNameRef.current === null) {
        try {
          const r = await fetch(`/api/content/${encodeURIComponent(filesScopeRootId)}/info`, { credentials: 'same-origin' });
          if (r.ok) {
            const info = (await r.json()) as { displayName?: string };
            filesScopeNameRef.current = info.displayName ?? 'Folder';
          } else {
            filesScopeNameRef.current = 'Folder';
          }
        } catch {
          filesScopeNameRef.current = 'Folder';
        }
      }
      return { ok: true, scope: { rootId: filesScopeRootId, name: filesScopeNameRef.current } };
    });

    // Host access dialog (standard portal chrome, like save/pick): the user
    // allows the ENTIRE LIBRARY or picks ONE FOLDER. On allow the grant lands
    // in the per-user local store; the store re-projects and the host re-keys
    // this iframe, so the widget reboots granted.
    reg.register('files.requestAccess', validateFilesRequestAccessArgs, async () => {
      if (ephemeral) return { ok: false };
      if (filesGranted) return { ok: true };
      const decision = await new Promise<{ scope: 'all' | string } | null>((resolve) => {
        window.dispatchEvent(
          new CustomEvent('widget-files-access', {
            detail: { widgetName: title ?? 'This app', resolve },
          }),
        );
      });
      if (!decision) return { ok: false };
      addLocalWidgetGrant(
        workspaceId,
        itemId,
        decision.scope === 'all' ? 'files' : `files:${decision.scope}`,
      );
      return { ok: true };
    });


    reg.register('files.list', validateFilesListArgs, async (args) => {
      if (!filesGranted) throw filesDenied();
      const parent = filesResolveParent(args.parentId);
      // Ownership lens. `owner=me` was hardcoded here until 2026-09-21, which
      // made a whole class of content invisible to every app: a file or folder
      // SHARED with the viewer is not created by them, so it never matched,
      // and the Files app's "Shared with me" plus any navigation into a shared
      // folder listed empty. The caller picks now; `filesListOwner` owns the
      // defaults (and why they differ between a whole-library and a
      // folder-scoped grant).
      // `omitParent` is the shared ROOT: content shared with you sits in
      // somebody else's tree, so `parentId=null` there returns nothing rather
      // than the grant list. See `filesListScope` for both rules.
      const { owner, omitParent } = filesListScope(args.scope, filesScopeRootId, parent);
      // The route caps limit at 100 — page until short page or the 500-item
      // widget ceiling (report truncation so the UI can say so).
      const items: FilesEntry[] = [];
      let truncated = false;
      for (let page = 1; page <= 5; page++) {
        const p = new URLSearchParams({
          owner,
          limit: '100',
          page: String(page),
          sort: 'newest',
        });
        if (!omitParent) p.set('parentId', parent ?? 'null');
        // Default view hides `metadata.hidden` system rows (the flag's
        // always-on filter); the container-folder CTE never fires here
        // because parentId is always present. "Show hidden" lifts it.
        if (!args.showHidden) p.set('excludeBundleFiles', '1');
        const r = await fetch(`/api/content?${p}`, { credentials: 'same-origin' });
        if (!r.ok) throw filesHttpError('files.list', r.status);
        const data = (await r.json()) as { items?: Array<Record<string, unknown>> };
        const rows = data.items ?? [];
        for (const row of rows) items.push(filesRow(row));
        if (rows.length < 100) break;
        if (page === 5) truncated = true;
      }
      for (const it of items) filesServed.add(it.id);
      return { items, truncated };
    });

    reg.register('files.mkdir', validateFilesMkdirArgs, async (args) => {
      if (!filesGranted) throw filesDenied();
      const parent = filesResolveParent(args.parentId);
      const r = await fetchGated('/api/content/folder', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json', 'x-widget-origin': originTag },
        body: JSON.stringify({ displayName: args.name, parentId: parent ?? undefined }),
      });
      if (!r.ok) throw filesHttpError('files.mkdir', r.status);
      const folder = (await r.json()) as { id: string; displayName?: string };
      filesServed.add(folder.id);
      return { id: folder.id, name: folder.displayName ?? args.name };
    });

    reg.register('files.rename', validateFilesRenameArgs, async (args) => {
      if (!filesGranted) throw filesDenied();
      filesCheckId(args.id);
      const r = await fetchGated(`/api/content/${encodeURIComponent(args.id)}`, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json', 'x-widget-origin': originTag },
        body: JSON.stringify({ displayName: args.name }),
      });
      if (!r.ok) throw filesHttpError('files.rename', r.status);
      return { ok: true as const };
    });

    // Move/copy/delete fan out over the selection with a small concurrency
    // cap; per-id failures are counted, not thrown, so one 403 doesn't void
    // the rest of a multi-select operation.
    const filesBatch = async (ids: string[], perId: (id: string) => Promise<void>, width = 4) => {
      let ok = 0;
      let failed = 0;
      for (let i = 0; i < ids.length; i += width) {
        const settled = await Promise.allSettled(ids.slice(i, i + width).map(perId));
        for (const s of settled) (s.status === 'fulfilled' ? ok++ : failed++);
      }
      return { ok, failed };
    };

    reg.register('files.move', validateFilesMoveArgs, async (args) => {
      if (!filesGranted) throw filesDenied();
      const target = filesResolveParent(args.parentId);
      for (const id of args.ids) filesCheckId(id);
      const { ok, failed } = await filesBatch(args.ids, async (id) => {
        const r = await fetchGated(`/api/content/${encodeURIComponent(id)}`, {
          method: 'PATCH',
          credentials: 'same-origin',
          headers: { 'content-type': 'application/json', 'x-widget-origin': originTag },
          body: JSON.stringify({ parentId: target }),
        });
        if (!r.ok) throw filesHttpError('files.move', r.status);
      });
      return { moved: ok, failed };
    });

    reg.register('files.copy', validateFilesCopyArgs, async (args) => {
      if (!filesGranted) throw filesDenied();
      const copyTarget = filesResolveParent(args.parentId);
      for (const id of args.ids) filesCheckId(id);
      const { ok, failed } = await filesBatch(
        args.ids,
        async (id) => {
          const body: { parentId: string | null; newName?: string } = { parentId: copyTarget };
          if (args.newName && args.ids.length === 1) body.newName = args.newName;
          const r = await fetchGated(`/api/content/${encodeURIComponent(id)}/copy`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'content-type': 'application/json', 'x-widget-origin': originTag },
            body: JSON.stringify(body),
          });
          if (!r.ok) throw filesHttpError('files.copy', r.status);
        },
        2,
      );
      return { copied: ok, failed };
    });

    reg.register('files.delete', validateFilesDeleteArgs, async (args) => {
      if (!filesGranted) throw filesDenied();
      for (const id of args.ids) filesCheckId(id);
      // Sequential in caller order (widget sends folder children first), so a
      // folder's unlink→GC never races its own children's deletes.
      let deleted = 0;
      let failed = 0;
      for (const id of args.ids) {
        try {
          const r = await fetchGated(`/api/content/${encodeURIComponent(id)}`, {
            method: 'DELETE',
            credentials: 'same-origin',
            headers: { 'x-widget-origin': originTag },
          });
          if (!r.ok && r.status !== 404) throw filesHttpError('files.delete', r.status);
          deleted++;
        } catch {
          failed++;
        }
      }
      return { deleted, failed };
    });

    reg.register('files.upload', validateFilesUploadArgs, async (args) => {
      if (!filesGranted) throw filesDenied();
      const uploadParent = filesResolveParent(args.parentId);
      // Resumable, with progress in the portal's transfer tray (CASFS-UI PF-4).
      // The old multipart answer was a bare row or `{ items }`, never the
      // `{ results }` this handler read, so an app's upload came back with no
      // items; the resumable client answers each row itself.
      const rows: Array<Record<string, unknown>> = [];
      for (const f of args.files) {
        const bin = atob(f.data);
        const u8 = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
        try {
          const up = await trackUpload(new File([u8], f.name, { type: f.mime }), {
            filename: f.name,
            mimeType: f.mime,
            parentId: uploadParent ?? null,
            visibility: 'private',
            fetchImpl: fetchGated as typeof fetch,
            headers: { 'x-widget-origin': originTag },
          });
          rows.push({ ...up, parentId: uploadParent ?? null });
        } catch (e) {
          const code = (e as { code?: string })?.code;
          throw filesHttpError('files.upload', code === 'storage_quota_exceeded' || code === 'file_too_large' ? 413 : code === 'unauthorized' ? 401 : 500);
        }
      }
      const uploaded = rows.map(filesRow);
      for (const it of uploaded) filesServed.add(it.id);
      return { items: uploaded };
    });

    reg.register('files.read', validateFilesReadArgs, async (args) => {
      if (!filesGranted) throw filesDenied();
      filesCheckId(args.id);
      const r = await fetch(`/api/content/${encodeURIComponent(args.id)}`, {
        method: 'GET',
        credentials: 'same-origin',
      });
      if (r.status === 404 || r.status === 403) return null;
      if (!r.ok) throw filesHttpError('files.read', r.status);
      const buf = await r.arrayBuffer();
      const cap = args.maxBytes ?? FILES_READ_DEFAULT_MAX;
      const truncated = buf.byteLength > cap;
      const u8 = new Uint8Array(truncated ? buf.slice(0, cap) : buf);
      let bin = '';
      for (let i = 0; i < u8.length; i += 0x8000) bin += String.fromCharCode.apply(null, Array.from(u8.subarray(i, i + 0x8000)));
      return {
        data: btoa(bin),
        mime: r.headers.get('content-type') ?? 'application/octet-stream',
        name: '',
        size: buf.byteLength,
        truncated,
      };
    });

    reg.register('files.download', validateFilesDownloadArgs, async (args) => {
      if (!filesGranted) throw filesDenied();
      filesCheckId(args.id);
      // Host-side anchor: the download rides the host document's session
      // cookies (the sandboxed iframe has none) and the route's
      // Content-Disposition attachment path.
      const a = document.createElement('a');
      a.href = `/api/content/${encodeURIComponent(args.id)}?download=1`;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      return { ok: true as const };
    });

    reg.register('files.open', validateFilesOpenArgs, async (args) => {
      if (!filesGranted) throw filesDenied();
      filesCheckId(args.id);
      // Same shape the Content panel uses (my-content-manager focusOrAdd):
      // the cache fields let the node render before its meta fetch lands.
      await addItem({
        type: 'content',
        refId: args.id,
        state: {
          cache: {
            displayName: args.name,
            contentType: args.type,
            mimeType: args.mime,
          },
        },
      });
      return { ok: true };
    });

    // --- local.* family (host.local — a folder on the viewer's OWN computer) --
    // Client-side File System Access API. The native directory picker is the
    // consent (opened via host chrome on a real user gesture, since postMessage
    // does not carry the widget's activation). Ops only touch handles minted by
    // a pick in THIS instance's session (localMountsRef); nothing hits the
    // server. Denied in the ephemeral share view.
    const fsSupported = () =>
      typeof window !== 'undefined' && typeof (window as unknown as { showDirectoryPicker?: unknown }).showDirectoryPicker === 'function';
    const localDenied = (msg: string, code = 'permission') => {
      const e = new Error(msg) as Error & { code?: string };
      e.code = code;
      return e;
    };
    const getMount = (mountId: string): FileSystemDirectoryHandle => {
      const h = localMountsRef.current.get(mountId);
      if (!h) throw localDenied('local: unknown mount (re-connect the folder)', 'not_found');
      return h;
    };
    // Walk `path` (segment-name array) from a mount root to a DIRECTORY handle.
    // `create` makes missing dirs (mkdir/write). Rejects nothing beyond what the
    // FS API rejects — traversal is impossible (segments are plain names).
    const walkDir = async (mountId: string, path: string[], create = false): Promise<FileSystemDirectoryHandle> => {
      let dir = getMount(mountId);
      for (const seg of path) dir = await dir.getDirectoryHandle(seg, { create });
      return dir;
    };

    reg.register('local.available', validateLocalAvailableArgs, async () => {
      return { ok: !ephemeral && fsSupported(), supported: fsSupported() };
    });

    // The widget's click cannot lend its user-activation to the apex, so the
    // apex renders a "Connect a folder" prompt whose button (host activation)
    // calls showDirectoryPicker. The dialog resolves the chosen handle back.
    reg.register('local.pick', validateLocalPickArgs, async () => {
      if (ephemeral || !fsSupported()) return null;
      const handle = await new Promise<FileSystemDirectoryHandle | null>((resolve) => {
        window.dispatchEvent(
          new CustomEvent('widget-local-pick', {
            detail: { widgetName: title ?? 'This app', resolve },
          }),
        );
      });
      if (!handle) return null;
      const mountId = (crypto?.randomUUID?.() ?? `m-${Date.now()}-${Math.random().toString(36).slice(2)}`);
      localMountsRef.current.set(mountId, handle);
      return { mountId, name: handle.name || 'Folder' };
    });

    reg.register('local.list', validateLocalListArgs, async (args) => {
      if (ephemeral) throw localDenied('local unavailable in share view');
      const dir = await walkDir(args.mountId, args.path);
      const entries: LocalEntry[] = [];
      // FileSystemDirectoryHandle is async-iterable over [name, handle].
      for await (const [name, h] of (dir as unknown as AsyncIterable<[string, FileSystemHandle]>)) {
        if (h.kind === 'directory') {
          entries.push({ name, kind: 'directory', size: 0 });
        } else {
          let size = 0;
          let modified: number | undefined;
          try {
            const f = await (h as FileSystemFileHandle).getFile();
            size = f.size;
            modified = f.lastModified;
          } catch {
            /* unreadable entry — list it with size 0 */
          }
          entries.push({ name, kind: 'file', size, ...(modified ? { modified } : {}) });
        }
        if (entries.length >= 5000) break; // sanity cap for a single directory
      }
      return { entries };
    });

    reg.register('local.read', validateLocalReadArgs, async (args) => {
      if (ephemeral) throw localDenied('local unavailable in share view');
      const name = args.path[args.path.length - 1];
      const dir = await walkDir(args.mountId, args.path.slice(0, -1));
      const fh = await dir.getFileHandle(name);
      const file = await fh.getFile();
      const cap = args.maxBytes ?? LOCAL_READ_DEFAULT_MAX;
      const truncated = file.size > cap;
      const buf = await (truncated ? file.slice(0, cap) : file).arrayBuffer();
      const u8 = new Uint8Array(buf);
      let bin = '';
      for (let i = 0; i < u8.length; i += 0x8000) bin += String.fromCharCode.apply(null, Array.from(u8.subarray(i, i + 0x8000)));
      return { data: btoa(bin), mime: file.type || guessLocalMime(name) || 'application/octet-stream', size: file.size, truncated };
    });

    reg.register('local.write', validateLocalWriteArgs, async (args) => {
      if (ephemeral) throw localDenied('local unavailable in share view');
      const dir = await walkDir(args.mountId, args.path, true);
      const fh = await dir.getFileHandle(args.name, { create: true });
      const bin = atob(args.data);
      const u8 = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
      const w = await (fh as FileSystemFileHandle & { createWritable(): Promise<FileSystemWritableFileStream> }).createWritable();
      try {
        await w.write(u8);
      } finally {
        await w.close();
      }
      return { ok: true as const };
    });

    reg.register('local.mkdir', validateLocalMkdirArgs, async (args) => {
      if (ephemeral) throw localDenied('local unavailable in share view');
      const dir = await walkDir(args.mountId, args.path, true);
      await dir.getDirectoryHandle(args.name, { create: true });
      return { ok: true as const };
    });

    reg.register('local.rename', validateLocalRenameArgs, async (args) => {
      if (ephemeral) throw localDenied('local unavailable in share view');
      const parent = await walkDir(args.mountId, args.path.slice(0, -1));
      const oldName = args.path[args.path.length - 1];
      let handle: FileSystemHandle;
      try {
        handle = await parent.getFileHandle(oldName);
      } catch {
        handle = await parent.getDirectoryHandle(oldName);
      }
      const movable = handle as FileSystemHandle & { move?: (name: string) => Promise<void> };
      if (typeof movable.move !== 'function') {
        throw localDenied('renaming local items needs a newer browser', 'internal');
      }
      await movable.move(args.name);
      return { ok: true as const };
    });

    reg.register('local.delete', validateLocalDeleteArgs, async (args) => {
      if (ephemeral) throw localDenied('local unavailable in share view');
      const parent = await walkDir(args.mountId, args.path.slice(0, -1));
      await parent.removeEntry(args.path[args.path.length - 1], { recursive: !!args.recursive });
      return { ok: true as const };
    });

    // --- cas.* family (host.cas - project data as a CASFS tree, ADR 0063) ---
    // The project is the one THIS instance is bound to, read by the host at
    // call time; the app passes no project id. Named `Cas:*` refusals keep
    // their code in the message (`Cas:HeadMoved: ...`) under the sealed wire
    // vocabulary; a moved head on commit is a RESULT so the app's core can
    // merge by path and retry.
    const casHost = () => (casHostRef.current ??= new CasHost({ openCache: true }));
    const casWire = (e: unknown): Error => {
      if (!(e instanceof CasError)) return e as Error;
      const wire =
        e.code === 'Cas:NotFound' ? 'not_found'
        : e.code === 'Cas:QuotaExceeded' || e.code === 'Cas:LocalQuota' || e.code === 'Cas:TooLarge' ? 'quota_exceeded'
        : e.code === 'Cas:Forbidden' || e.code === 'Cas:GrantScope' || e.code === 'Cas:LocalOnly' || e.code === 'Cas:PolicyWeaken' || e.code === 'Cas:MountReadOnly' || e.code === 'Cas:MountSource' ? 'permission'
        : 'invalid_args';
      const out = new Error(e.message) as Error & { code?: string };
      out.code = wire;
      return out;
    };
    const casDenied = () => localDenied('this link opens the project to read: to change it, open the project from a workspace');
    // [SAVE-7] (2026-11-04, review B2: an invite link was a dead end, "project files are unavailable in the share view") THE SHARED PROJECT ITSELF OPENS READ ONLY UNDER /w/.
    // The project is the page's own share target (itemId, set by the server page, never the app: documents.bind refuses under ephemeral), and only a folder row. Reads go through the server under the
    // PERSON's session, so CASFS's own check decides: a stranger or a signed-out visitor gets its refusal. Every write op stays refused here whatever the role. It never makes a folder a CASFS project (ifExists).
    const casShareTarget = () => ephemeral && itemId.startsWith('share-') && shareContentType === 'folder';
    // [REQ-BUILD] requirements connectors: the EDA app only. The shell makes the call under the person's
    // session, so the frame never holds a credential and no other app reaches this person's tools.
    const redaOnly = () => { if (ephemeral || widgetTypeRef.current !== 'eda') { const e = new Error('this op belongs to another app') as Error & { code?: string }; e.code = 'permission'; throw e; } };
    const redaCall = async (path: string, init?: RequestInit) => {
      const res = await fetch(path, { ...init, credentials: 'same-origin', headers: { 'Content-Type': 'application/json' } }), j = await res.json().catch(() => ({}));
      if (!res.ok) { const e = new Error(String(j.error || 'refused')) as Error & { code?: string; syncCode?: string }; e.code = res.status === 401 || res.status === 403 ? 'permission' : 'internal'; e.syncCode = String(j.code || ''); throw e; }
      return j;
    };
    // ---- kit.* (ADR-0066): the board on the person's desk, held by THIS page. The frame holds no USB and sends no transfer: kit-session.ts refuses anything but read, pins and a load a person confirms in portal chrome. ----
    const kitInstance = workspaceId + ':' + itemId, kitApp = () => title ?? 'This app';
    reg.register('kit.available', validateKitAvailableArgs, () => kitOp(() => pageKitSession().available(kitInstance, !!ephemeral)));
    reg.register('kit.requestAccess', validateKitRequestAccessArgs, () => kitOp(() => { if (ephemeral) return Promise.resolve({ ok: false }); return pageKitSession().requestAccess(kitInstance, kitApp()); }));
    reg.register('kit.connect', validateKitConnectArgs, () => kitOp(() => pageKitSession().connect(kitInstance)));
    reg.register('kit.read', validateKitReadArgs, () => kitOp(() => pageKitSession().read(kitInstance)));
    reg.register('kit.pins', validateKitPinsArgs, (a) => kitOp(() => pageKitSession().pins(kitInstance, a.samples, a.everyMs)));
    reg.register('kit.load', validateKitLoadArgs, (a) => kitOp(() => pageKitSession().load(kitInstance, kitApp(), a.bytes)));
    reg.register('kit.forget', validateKitForgetArgs, () => kitOp(() => pageKitSession().forget(kitInstance)));
    reg.register('reda.relay', validateRedaRelayArgs, async (a) => { redaOnly(); return redaCall('/api/reda/connectors/relay', { method: 'POST', body: JSON.stringify(a) }); });
    reg.register('reda.connections', validateRedaConnectionsArgs, async () => { redaOnly(); return { ...(await redaCall('/api/reda/connectors')), appBase: window.location.origin }; });
    reg.register('reda.signature', validateRedaSignatureArgs, async (a) => {
      redaOnly();
      // the published keys are public by definition: the app checks signatures itself with them (req-seal.mjs), so a teammate's screen and an auditor run the same check
      if (a.keys) { const r = await fetch('/.well-known/jwks.json', { credentials: 'omit' }); if (!r.ok) throw new Error('The published keys could not be read.'); return r.json(); }
      if (a.present) return redaCall('/api/reda/sign', { method: 'POST', body: JSON.stringify({ action: 'present', ...a.present }) });
      if (a.grant) { const { op, ...g } = a.grant; return redaCall('/api/reda/sign', { method: 'POST', body: JSON.stringify({ action: op, ...g }) }); }
      if (a.log) return redaCall('/api/reda/sign', { method: 'POST', body: JSON.stringify({ action: 'log', projectId: a.log }) });
      if (a.roles) return redaCall('/api/reda/sign', { method: 'POST', body: JSON.stringify({ action: 'roles', projectId: a.roles }) });
      return a.receipt ? redaCall('/api/reda/sign', { method: 'POST', body: JSON.stringify({ action: 'check', receipt: a.receipt }) }) : redaCall('/api/reda/sign?hash=' + encodeURIComponent(a.hash ?? '') + '&project=' + encodeURIComponent(a.projectId ?? ''));
    });
    // [MOSIS-DEV-2] the signing page is opened by the shell, never by the app's frame: under COOP same-origin a pop-up from the
    // cross-origin frame has no handle (lib/workspaces/widget-ops/reda.ts). The app only ever names a window id this map minted and a
    // token of the server's form; the address is always this origin's /reda/sign. At most four open at once.
    const signWins = new Map<string, Window>();
    reg.register('reda.signWindow', validateRedaSignWindowArgs, async (a) => {
      redaOnly();
      if ('open' in a) {
        const w = window.open('', '_blank');
        if (!w) { const e = new Error('Allow pop-ups for this site to sign') as Error & { code?: string }; e.code = 'permission'; throw e; }
        try { w.document.title = 'Robutler · Signing'; w.document.body.style.cssText = 'margin:0;font:14px/1.5 system-ui,-apple-system,Segoe UI,sans-serif;color:#555;display:grid;place-items:center;height:100vh'; w.document.body.textContent = 'Preparing the signing page. The server is reading what you sign.'; } catch { /* the window opened; the words are a courtesy */ }
        for (const [k, v] of signWins) if (v.closed) signWins.delete(k);
        while (signWins.size >= 4) { const [k0, v0] = signWins.entries().next().value as [string, Window]; try { v0.close(); } catch { /* already gone */ } signWins.delete(k0); }
        const id = crypto.randomUUID();
        signWins.set(id, w);
        return { id };
      }
      if ('go' in a) {
        const w = signWins.get(a.go.id);
        if (!w || w.closed) return { ok: false, closed: true };
        w.location.replace(window.location.origin + '/reda/sign?' + new URLSearchParams({ t: a.go.t }));
        return { ok: true };
      }
      if ('close' in a) { const w = signWins.get(a.close); try { w?.close(); } catch { /* already gone */ } signWins.delete(a.close); return { ok: true }; }
      const w = signWins.get(a.state);
      return { closed: !w || w.closed };
    });
    reg.register('cas.open', validateCasOpenArgs, async (a) => {
      if (ephemeral && !casShareTarget()) throw casDenied();
      try {
        return await casHost().open(projectFolderIdNow(), { ifExists: ephemeral ? true : a.ifExists });
      } catch (e) {
        throw casWire(e);
      }
    });
    reg.register('cas.head', validateCasHeadArgs, async (a) => {
      try { return await casHost().head(a.handle); } catch (e) { throw casWire(e); }
    });
    reg.register('cas.has', validateCasHasArgs, async (a) => {
      try { return await casHost().has(a.handle, a.sha, a.target); } catch (e) { throw casWire(e); }
    });
    reg.register('cas.blob', validateCasBlobArgs, async (a) => {
      try { return await casHost().blob(a.handle, a.sha, a.offset, a.length, a.target); } catch (e) { throw casWire(e); }
    });
    reg.register('cas.put', validateCasPutArgs, async (a) => {
      if (ephemeral) throw casDenied();
      try { return await casHost().put(a.handle, a); } catch (e) { throw casWire(e); }
    });
    reg.register('cas.commit', validateCasCommitArgs, async (a) => {
      if (ephemeral) throw casDenied();
      try {
        return { ok: true as const, ...(await casHost().commit(a.handle, a.base, a.root, a.message) as object) };
      } catch (e) {
        if (e instanceof CasError && e.code === 'Cas:HeadMoved') return { ok: false as const, code: 'Cas:HeadMoved', current: (e.args.head as string | null) ?? null };
        throw casWire(e);
      }
    });
    reg.register('cas.history', validateCasHistoryArgs, async (a) => {
      try { return await casHost().history(a.handle, a.limit); } catch (e) { throw casWire(e); }
    });
    // Mounts (ADR 0063 section 17). Changes go through the server's one
    // implementation under the person's session; reading a source needs a
    // read-only handle the host mints only for a mount the project names.
    reg.register('cas.mounts', validateCasMountsArgs, async (a) => {
      try { return await casHost().mounts(a.handle, a.root); } catch (e) { throw casWire(e); }
    });
    reg.register('cas.mount', validateCasMountArgs, async (a) => {
      if (ephemeral) throw casDenied();
      try { return await casHost().mount(a.handle, a.path, a.source, a.root); } catch (e) { throw casWire(e); }
    });
    reg.register('cas.unmount', validateCasUnmountArgs, async (a) => {
      if (ephemeral) throw casDenied();
      try { return await casHost().unmount(a.handle, a.path); } catch (e) { throw casWire(e); }
    });
    reg.register('cas.updateMount', validateCasUpdateMountArgs, async (a) => {
      if (ephemeral) throw casDenied();
      try { return await casHost().updateMount(a.handle, a.path, a.root); } catch (e) { throw casWire(e); }
    });
    reg.register('cas.ensurePdkMount', validateCasEnsurePdkMountArgs, async (a) => {
      if (ephemeral) throw casDenied();
      try { return await casHost().ensurePdkMount(a.handle, a); } catch (e) { throw casWire(e); }
    });
    reg.register('cas.openMount', validateCasOpenMountArgs, async (a) => {
      try { return await casHost().openMount(a.handle, a.source, a.root); } catch (e) { throw casWire(e); }
    });
    // Evidence (ADR 0063 section 18): a reader of the project's head; the server decides what is in the store.
    reg.register('cas.importBundle', validateCasImportBundleArgs, async (a) => {
      if (ephemeral) throw casDenied();
      try { return await casHost().importBundle(a.handle, a); } catch (e) { throw casWire(e); }
    });
    reg.register('cas.pack', validateCasPackArgs, async (a) => {
      try { return await casHost().pack(a.handle, a); } catch (e) { throw casWire(e); }
    });
    reg.register('cas.evidence', validateCasEvidenceArgs, async (a) => {
      if (ephemeral) throw casDenied(); // [SAVE-7] it can keep a version: a write
      try { return await casHost().evidence(a.handle, a); } catch (e) { throw casWire(e); }
    });

    return reg;
    // The registry is keyed on workspaceId / itemId / originTag /
    // fetchGated / ephemeral (+ `grantSig`, a STABLE sorted-string of the
    // granted permissions, so a mid-session `files` grant/revoke re-gates
    // the files.* handlers — but ordinary item-state churn like drags never
    // rebuilds the registry); nothing else needs to invalidate it. KV op
    // handlers close over these inputs to keep the URL / origin pinned,
    // and swap the gated fetch when `requireSignup` flips (e.g. the user
    // signs up mid-session).
  }, [workspaceId, itemId, legacyKvWorkspaceId, legacyKvItemId, originTag, fetchGated, ephemeral, grantSig, title, addItem]);

  // -----------------------------------------------------------------
  // Widget-command-bus registration for iframe widgets.
  //
  // Translates `bus.invoke(itemId, name, args)` into a `cmd` push
  // frame addressed to this iframe and joins on the SDK's matching
  // `cmd.result` reply. Event subscriptions are kept in a local map;
  // `widget.event` payloads emitted by the iframe fan out to bus
  // subscribers (without going through the workspace.bus relay).
  //
  // The cmd / cmd.result protocol is owned by the SDK at
  // `public/widgets/sdk.v1.js` (see `host.commands.handle`).
  // -----------------------------------------------------------------
  const bridgeInvoke = useMemo(
    () =>
      async function bridgeInvoke(
        name: string,
        args: unknown,
        ctx: {
          timeoutMs?: number;
          signal?: AbortSignal;
          agentId?: string | null;
          actor?: { kind?: string; id?: string; name?: string; avatar?: string } | null;
        },
      ): Promise<unknown> {
        const iframe = iframeRef.current;
        if (!iframe?.contentWindow) {
          throw new BusError('not_found', 'iframe not mounted');
        }
        // `__reload` reloads THIS widget's iframe from the host — so an
        // agent that just edited the bundle (widget_put_files) can see the
        // change without the user refreshing the tab. The sandboxed iframe
        // is cross-origin (no `contentWindow.location` access), so we
        // re-navigate by reassigning `src` with a cache-bust on the ENTRY
        // url. The bundle's own `?v=` query strings still gate its
        // sub-assets (app.js / docs.css), so bump those when you change them.
        if (name === '__reload') {
          // The iframe is about to reboot — commands posted before the fresh
          // SDK's `__ready__` handshake would be lost, so close the gate and
          // let subsequent invokes park on it (see sdkReadyRef).
          sdkReadyRef.current = false;
          try {
            const u = new URL(iframe.src, window.location.href);
            u.searchParams.set('_r', Date.now().toString(36));
            iframe.src = u.toString();
          } catch {
            // eslint-disable-next-line no-self-assign
            iframe.src = iframe.src;
          }
          return { reloaded: true };
        }
        // Host-side Region Capture (taint-free): grab the widget's iframe
        // region from the compositor and save it to the PUBLIC capture sink
        // (the sandboxed editor has an opaque origin / no cookie, so it can't
        // fetch `/api/content/<id>` but CAN `importUrl` a `/captures/*` file).
        // Shared by `__capture` and the `__screenshot` taint fallback.
        const hostRegionCapture = async (a: {
          durationMs?: number;
          fps?: number;
          mime?: string;
        }): Promise<{
          url: string;
          id: string;
          mime: string;
          size: number;
          durationMs: number;
          data?: string;
        }> => {
          let blob: Blob;
          try {
            blob = await captureElement(iframe, { durationMs: a.durationMs, fps: a.fps, mime: a.mime });
          } catch (err) {
            throw new BusError('internal', err instanceof Error ? err.message : 'capture failed');
          }
          const bytes = new Uint8Array(await blob.arrayBuffer());
          let bin = '';
          const CHUNK = 0x8000;
          for (let i = 0; i < bytes.length; i += CHUNK) {
            bin += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + CHUNK)));
          }
          const b64 = btoa(bin);
          const r = await fetchGated('/api/captures', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'content-type': 'application/json', 'x-widget-origin': originTag },
            body: JSON.stringify({ data: b64, mime: blob.type }),
          });
          if (!r.ok) throw new BusError('internal', `capture save failed (${r.status})`);
          const ref = (await r.json()) as { id: string; url: string };
          // Inline a STILL frame's bytes (<~5MB) so the MCP layer can hand the
          // agent an image content block — it must SEE the capture, not just a
          // URL. Clips (durationMs) and oversized frames are ref-only.
          const inlineData =
            !a.durationMs && b64.length <= 5_000_000 ? b64 : undefined;
          return {
            url: ref.url,
            id: ref.id,
            mime: blob.type,
            size: blob.size,
            durationMs: a.durationMs || 0,
            ...(inlineData ? { data: inlineData } : {}),
          };
        };

        // Relay a command into the sandboxed iframe and await its reply.
        const relayToWidget = (n: string, a: unknown): Promise<unknown> => {
          const replyId = `rep-${Date.now().toString(36)}-${Math.random()
            .toString(36)
            .slice(2, 8)}`;
          const timeoutMs = Math.max(50, Math.min(60_000, ctx.timeoutMs ?? 10_000));
          // Mark this actor in-flight for the duration of the relay so ops the
          // widget fires while handling the command (checkpoint saves) can
          // credit the acting client. Settled exactly once on reply, timeout
          // or abort; depth-counted so overlapping commands don't clear early.
          const cmdActorName =
            typeof ctx.actor?.name === 'string' && ctx.actor.name ? ctx.actor.name : null;
          if (cmdActorName) {
            const cur = agentCmdActorRef.current;
            agentCmdActorRef.current = { name: cmdActorName, depth: (cur?.depth ?? 0) + 1 };
          }
          let actorSettled = false;
          const settleActor = () => {
            if (actorSettled || !cmdActorName) return;
            actorSettled = true;
            const cur = agentCmdActorRef.current;
            if (cur) agentCmdActorRef.current = cur.depth > 1 ? { ...cur, depth: cur.depth - 1 } : null;
          };
          return new Promise<unknown>((resolve, reject) => {
            const timer = setTimeout(() => {
              settleActor();
              pendingCmdsRef.current.delete(replyId);
              reject(new BusError('timeout', `command '${n}' timed out`));
            }, timeoutMs);
            pendingCmdsRef.current.set(replyId, {
              resolve: (v) => { settleActor(); resolve(v); },
              reject: (e) => { settleActor(); reject(e); },
              timer,
            });
            if (ctx.signal) {
              ctx.signal.addEventListener(
                'abort',
                () => {
                  const pending = pendingCmdsRef.current.get(replyId);
                  if (!pending) return;
                  pendingCmdsRef.current.delete(replyId);
                  clearTimeout(pending.timer);
                  settleActor();
                  reject(new BusError('internal', 'aborted'));
                },
                { once: true },
              );
            }
            iframe.contentWindow!.postMessage(
              {
                id: '__broadcast__',
                // `actor` (the calling agent) lets the widget attribute the edit +
                // show the agent-activity indicator (the command-interface analogue
                // of a collab cursor) + log it to the doc. Prefer the rich actor
                // resolved upstream ({kind,id,name,avatar}); fall back to the bare
                // agentId. Absent for human/direct invokes.
                push: {
                  kind: 'cmd',
                  name: n,
                  args: a ?? {},
                  replyId,
                  target: itemId,
                  ...(ctx.actor ? { actor: ctx.actor } : ctx.agentId ? { actor: { id: ctx.agentId } } : {}),
                },
              },
              '*',
            );
          });
        };

        // Pixel-true capture is HOST-side Region Capture (taint-free real
        // pixels), NOT the in-iframe foreignObject rasteriser (which taints the
        // moment a widget shows any cross-origin content). Both `__screenshot`
        // (still frame) and `__capture` (still or a WebM clip) read the
        // COMPOSITOR via a one-time screen-share grant on the tab.
        //
        // An agent driving over MCP has no user gesture, so it can never START
        // the grant (getDisplayMedia needs transient activation). On a missing
        // grant we surface the in-app "share this tab" prompt for the user and
        // hand the AGENT a structured explanation to relay — not a raw error —
        // so it can ask the user to approve, then retry. After the one grant,
        // every capture in the session is silent.
        if (name === '__screenshot' || name === '__capture') {
          // PROMPTLESS BY DEFAULT (2026-09-22). The app answers first.
          //
          // Until today every `__screenshot` went straight to the compositor,
          // which needs a screen-share grant, which needs transient user
          // activation, which an agent over MCP can never have. So the common
          // case was: the agent asks for a screenshot, the USER gets a prompt
          // mid-task, and the agent gets nothing until they click it. The
          // compositor is still the only thing that cannot be forged, but it
          // is the wrong DEFAULT for "show me what this app looks like".
          //
          // The app's own `__screenshot` handler is asked first. Every app has
          // one for free (the SDK auto-registers a DOM rasteriser) and an app
          // that owns its rendering can override it with
          // `host.commands.handle('__screenshot', fn)` and answer with its own
          // pixels, which is both faithful and taint-free. Our `/api/content`
          // is same-origin on the app's own origin, so it does not taint the
          // rasteriser either; what still taints is third-party pixels, and
          // under COEP an asset with no CORP now fails to load rather than
          // tainting (measured 2026-09-22).
          //
          // PROVENANCE IS PART OF THE ANSWER, not a nicety. Apps are untrusted
          // user space: an app-supplied image is whatever the app chose to
          // draw, so anything that needs ground truth (moderation, "what does
          // the user actually SEE") must ask for `authoritative: true` and get
          // compositor pixels. Every result says which it is.
          const wantsAuthoritative =
            !!(args as { authoritative?: boolean } | undefined)?.authoritative;
          let frameHasNoSize = false;
          if (name === '__screenshot' && !wantsAuthoritative) {
            try {
              const shot = (await relayToWidget('__screenshot', args ?? {})) as Record<
                string,
                unknown
              > | null;
              // A frame with no size rasterises to a 1x1 canvas scaled by the
              // device pixel ratio, and that came back to an MCP client as a
              // 2x2 PNG with `ok: true` (Connectors Directory review,
              // 2026-10-26). A picture of nothing is not a screenshot: treat it
              // as no answer, so the compositor gets its turn or the caller
              // gets a plain failure.
              //
              // The frame's own box is the real test, measured here because an
              // app's numbers are its own: a forced 0x0 frame still answered
              // 42x56 from its content. The size check catches the app that
              // draws nothing in a frame that does have a box.
              const box = iframeRef.current?.getBoundingClientRect();
              frameHasNoSize = !box || box.width < 2 || box.height < 2;
              const nothingDrawn =
                frameHasNoSize ||
                (typeof shot?.width === 'number' &&
                  typeof shot?.height === 'number' &&
                  Math.min(shot.width, shot.height) <= 4);
              if (shot && typeof shot === 'object' && (shot.dataUrl || shot.content || shot.url) && !nothingDrawn) {
                return {
                  ...shot,
                  ok: true,
                  authoritative: false,
                  via: typeof shot.via === 'string' ? shot.via : 'app',
                };
              }
            } catch {
              // No usable handler, a timeout, or a tainted canvas. The
              // compositor is the fallback, not the first move.
            }
          }
          if (!hasCaptureSession()) {
            // Only the compositor is left, and it costs the user a prompt.
            // Ask for one ONLY when the caller actually needs unforgeable
            // pixels, or for `__capture`, which records a clip an app cannot
            // produce of itself. A plain `__screenshot` never prompts.
            if (name === '__screenshot' && !wantsAuthoritative) {
              return {
                ok: false,
                status: 'capture_unavailable',
                message: frameHasNoSize
                  ? "This app has no size on screen right now, so there is nothing to capture. Its window may be minimized or hidden: ask the user to open it, then call __screenshot again."
                  : "This app could not screenshot itself (no usable handler, or its canvas is tainted by third-party pixels). Nothing was shown to the user. If you need the image, call __screenshot again with { authoritative: true }: that asks them once for a screen-share grant and then returns real compositor pixels, silently, for the rest of the session.",
              };
            }
            if (captureGrantDismissedForever()) {
              return {
                ok: false,
                status: 'capture_disabled',
                message:
                  'Pixel-true screenshots need a one-time screen-share grant, which the user turned off for this tab ("do not ask again"). Ask them to re-enable capture from the app AI menu, then call ' +
                  name +
                  ' again.',
              };
            }
            void requestCaptureGrant(name === '__capture' ? 'capture' : 'screenshot');
            return {
              ok: false,
              status: 'grant_required',
              message:
                'Pixel-true capture needs a one-time screen-share grant. I just asked the user to enable it (a "Share this tab" prompt appeared on their screen). Ask them to click "Enable capture" and approve the browser prompt, then call ' +
                name +
                ' again. After that one grant, capture is silent for the rest of the session.',
            };
          }
          const out = await hostRegionCapture(
            (args ?? {}) as { durationMs?: number; fps?: number; mime?: string },
          );
          return { ...out, ok: true, via: 'region-capture', authoritative: true };
        }

        // SDK-boot gate: a cmd posted while the iframe is still booting
        // (initial load, or right after `__reload`) is silently lost — the
        // SDK's listener doesn't exist yet — so the invoke would dangle to
        // its timeout. Park until the `__ready__` handshake, bounded well
        // inside the command's own timeout so a widget that never boots
        // still yields the normal timeout error.
        if (!sdkReadyRef.current) {
          const graceMs = Math.min(
            15_000,
            Math.max((ctx.timeoutMs ?? 30_000) - 5_000, 1_000),
          );
          await new Promise<void>((resolve) => {
            const timer = setTimeout(() => {
              sdkReadyWaitersRef.current.delete(wake);
              resolve();
            }, graceMs);
            const wake = () => {
              clearTimeout(timer);
              resolve();
            };
            sdkReadyWaitersRef.current.add(wake);
            // Re-check after subscribing — the flip may have raced us.
            if (sdkReadyRef.current) wake();
          });
        }

        return relayToWidget(name, args);
      },
    [itemId],
  );

  const bridgeSubscribe = useMemo(
    () =>
      function bridgeSubscribe(
        eventName: string,
        onPush: (payload: unknown) => void,
      ): () => void {
        let set = eventSubsRef.current.get(eventName);
        if (!set) {
          set = new Set();
          eventSubsRef.current.set(eventName, set);
        }
        set.add(onPush);
        return () => {
          const s = eventSubsRef.current.get(eventName);
          if (!s) return;
          s.delete(onPush);
          if (s.size === 0) eventSubsRef.current.delete(eventName);
        };
      },
    [],
  );

  const registration = useMemo(() => {
    // Register every iframe widget that has a spec + item, even when it
    // declares no command `interface`. Reserved `__` builtins
    // (`__capture` / `__screenshot` / `__getState` / `__describe`) must
    // work on ANY open widget — the bus skips the declared-command gate
    // for reserved names — so an interface-less widget (collab-board,
    // vector-editor, …) still needs a bus registration to be reachable.
    // It gets an empty command surface; non-builtin commands stay
    // rejected by the bus precheck.
    if (!widgetSpec || !item) return null;
    return {
      itemId,
      widgetType: 'iframe',
      spec: widgetSpec,
      interface: widgetSpec.interface ?? {
        description: 'Builtin host commands only (no declared agent surface).',
        commands: {},
      },
      title,
      item,
      invoke: (name: string, args: unknown, ctx: { timeoutMs?: number; signal?: AbortSignal }) =>
        bridgeInvoke(name, args, ctx),
      subscribe: (eventName: string, onPush: (payload: unknown) => void) =>
        bridgeSubscribe(eventName, onPush),
    };
  }, [widgetSpec, item, itemId, title, bridgeInvoke, bridgeSubscribe]);
  useRegisterWidget(registration);

  // -----------------------------------------------------------------
  // postMessage terminator. Verifies `event.source === iframe.contentWindow`
  // before doing anything — sandbox=allow-scripts iframes emit
  // `event.origin === 'null'`, so source identity is the only line
  // of defence against forged frames from other tabs.
  // -----------------------------------------------------------------
  useEffect(() => {
    const handle = async (event: MessageEvent) => {
      const iframe = iframeRef.current;
      if (!iframe || event.source !== iframe.contentWindow) return;
      const data = event.data;

      // SDK ready handshake — `{ id: '__ready__', op: '__ready__' }`
      // arrives once the widget has bound its message listener. Reply
      // with a push frame carrying the workspace context so
      // `host.workspace` populates synchronously.
      if (
        data &&
        typeof data === 'object' &&
        data.id === '__ready__' &&
        data.op === '__ready__'
      ) {
        setWidgetLoadState(itemId, 'ready');
        // Release cmd relays parked behind the SDK boot (see sdkReadyRef).
        sdkReadyRef.current = true;
        for (const wake of [...sdkReadyWaitersRef.current]) wake();
        sdkReadyWaitersRef.current.clear();
        iframe.contentWindow?.postMessage(
          { id: '__ready__', push: buildReadyPushRef.current() },
          '*',
        );
        return;
      }

      // The folder shell asking for a sandbox grant (ADR-0044): its entry
      // fetch was refused on the app's own origin. Answered from the shell's
      // origin only; see `answerSandboxGrant`.
      if (
        data &&
        typeof data === 'object' &&
        data.method === 'ui/notifications/sandbox-grant-needed'
      ) {
        void answerSandboxGrant(iframe, data.params);
        return;
      }

      // ISOLATION SELF-REPORT (ADR-0044). The shell says, about itself,
      // whether it became cross-origin isolated and whether SharedArrayBuffer
      // constructs. Nothing in THIS document can read that: the app frame is
      // cross-origin on every cloud environment, so the read throws, and the
      // four ways around it were each tried and each blocked (see
      // lib/widgets/sandbox-isolation.ts). The value lands in the app log
      // rail, so `workspace_widgets_logs` answers it on every engine.
      //
      // `false` is the interesting case and the reason for the `warn`: the
      // chain has three links, and any one of them breaking shows up here and
      // nowhere else. The page's COOP+COEP, the shell's own COEP, and the
      // `cross-origin-isolated` delegation on the iframe `allow`. An engine
      // that ignores the delegation reports false SILENTLY, which is exactly
      // the failure this exists to surface.
      //
      // Deliberately does NOT return: the ready message keeps whatever
      // downstream handling it had, so this is additive and cannot change the
      // mount path.
      if (
        data &&
        typeof data === 'object' &&
        typeof data.method === 'string' &&
        data.method.indexOf('ui/notifications/sandbox-') === 0 &&
        data.params &&
        typeof data.params === 'object' &&
        (data.params as { isolation?: unknown }).isolation
      ) {
        const iso = (data.params as { isolation: Record<string, unknown> }).isolation;
        const coi = iso.coi === true;
        // `false` is a fault wherever it appears: every page that hosts apps
        // is isolated (ADR-0044), so every link of the chain (the page's
        // COOP+COEP, the shell's COEP, the delegation on the iframe `allow`)
        // should have made the frame isolated too, and this is the one place
        // a broken link shows. `page=` is this document's own answer, so the
        // line tells a page fault (a custom domain missing the pair) from a
        // frame fault (a missing delegation, an engine that ignores it). For
        // one day (2026-09-22) the warning was gated on the page being
        // isolated, because a plain canvas was then the designed answer;
        // that day is over and the gate went with it.
        const pageIsolated = typeof window !== 'undefined' && window.crossOriginIsolated === true;
        appendWidgetLog(itemId, {
          level: coi ? 'log' : 'warn',
          message:
            `[isolation] crossOriginIsolated=${coi} sharedArrayBuffer=${iso.sab === true}` +
            ` origin=${typeof iso.origin === 'string' && iso.origin ? iso.origin : '(opaque)'}` +
            ` page=${pageIsolated}`,
          ts: Date.now(),
        });
        if (!coi) {
          console.warn(
            `[robutler] app frame is NOT cross-origin isolated (item ${itemId}). ` +
              'SharedArrayBuffer and threaded WASM are unavailable in it. Check the page COOP/COEP, ' +
              "the shell's COEP, and the cross-origin-isolated delegation on the iframe allow.",
          );
        }
      }

      // Diagnostics channel: console/error lines forwarded by the SDK's
      // log forwarder (mirrors the agent-HTML `injectLogForwarder`
      // path). Buffer per item so `workspace_widgets_logs` can read them.
      if (data && typeof data === 'object' && data.source === 'robutler-widget-log') {
        appendWidgetLog(itemId, {
          level: typeof data.level === 'string' ? data.level : 'log',
          message:
            typeof data.message === 'string' ? data.message : String(data.message ?? ''),
          ts: typeof data.ts === 'number' ? data.ts : Date.now(),
        });
        // A captured Permissions-Policy violation tags the frame with
        // the gated feature → auto-raise a grant request (the PlayCanvas
        // `xr-spatial-tracking` case becomes a user prompt).
        if (typeof data.featureId === 'string' && data.featureId) {
          requestFeaturePermission(data.featureId);
        }
        return;
      }

      // Explicit `host.requestPermission(feature)` from the widget.
      if (
        data &&
        typeof data === 'object' &&
        data.source === 'robutler-widget-permission-request'
      ) {
        if (typeof data.feature === 'string') {
          requestFeaturePermission(data.feature);
        }
        return;
      }

      // Interaction ping — a (throttled) pointerdown inside the widget.
      // Re-emitted for the canvas so a click anywhere in an app raises
      // it above overlapping siblings; the window-blur fallback in
      // workspace-canvas only catches the FIRST host→iframe focus
      // transfer, never iframe→iframe.
      if (
        data &&
        typeof data === 'object' &&
        data.source === 'robutler-widget-focus'
      ) {
        window.dispatchEvent(
          new CustomEvent('workspace:widget-focus', { detail: { itemId } }),
        );
        return;
      }

      // Host-reserved shortcut relay. Key events never cross the iframe
      // boundary on their own, so the SDK forwards a small allowlist of
      // chords pressed inside the widget (Cmd/Ctrl+Shift + K / E / ←→ /
      // 1-9, see the forwarder in sdk.v2.js) as one-way frames. Re-emit as a
      // window CustomEvent consumed by the rail chord handler and the
      // app switcher — the bridge stays dumb about what each chord does.
      // The allowlist is re-checked HERE because event.source is the
      // trust boundary, not the frame contents.
      //
      // Escape rides the same channel as key PHASES (down/up, never
      // preventDefaulted in the widget) so the fullscreen chrome can time
      // the Esc-HOLD header reveal — see fullscreen-chrome.tsx. A bare
      // Escape without a valid phase is dropped.
      if (
        data &&
        typeof data === 'object' &&
        data.source === 'robutler-widget-hostkey'
      ) {
        const key = typeof data.key === 'string' ? data.key : '';
        const code = typeof data.code === 'string' ? data.code : '';
        const phase =
          data.phase === 'down' || data.phase === 'up'
            ? (data.phase as 'down' | 'up')
            : undefined;
        if (
          (key === 'Escape' && phase) ||
          key === 'k' ||
          key === 'K' ||
          key === 'e' ||
          key === 'E' ||
          code === 'KeyE' ||
          key === 'ArrowLeft' ||
          key === 'ArrowRight' ||
          /^Digit[1-9]$/.test(code)
        ) {
          window.dispatchEvent(
            new CustomEvent('workspace:host-key', {
              detail: { key, code, itemId, phase },
            }),
          );
        }
        return;
      }

      // Attribution signal relay — one-way frames from the SDK's
      // `host.attribution` batcher. Validate (untrusted iframe input, like
      // everything else on this listener), buffer, and POST on the bridge's
      // own rate-limited cadence. The server re-clamps every magnitude.
      if (
        data &&
        typeof data === 'object' &&
        (data as { source?: unknown }).source === ATTRIBUTION_SIGNALS_SOURCE
      ) {
        if (isAttributionSignalsFrame(data)) enqueueAttributionSignals(data.signals);
        return;
      }

      if (!isWidgetRequest(data)) return;

      // First state-mutating widget message → one-shot funnel event.
      trackFirstMeaningfulEdit(data.op, data.args);

      // Intercept widget.event payloads that carry the agent-command
      // result convention (`{ kind: 'cmd.result', replyId, ... }`)
      // so the bus invoke resolves locally without round-tripping
      // through the server. Same for declared event broadcasts — we
      // fan them out to bus subscribers AND let the existing
      // widget.event handler post to /events for the workspace.bus
      // relay so OTHER tabs see them.
      if (data.op === 'widget.event' && data.args && typeof data.args === 'object') {
        const payload = (data.args as { payload?: unknown }).payload;
        if (payload && typeof payload === 'object') {
          const p = payload as Record<string, unknown>;
          if (p.kind === 'cmd.result' && typeof p.replyId === 'string') {
            const pending = pendingCmdsRef.current.get(p.replyId);
            if (pending) {
              pendingCmdsRef.current.delete(p.replyId);
              // Bounded: an entry normally lives until the handler below
              // reads it, a moment later.
              if (locallyAnsweredRef.current.size > 256) locallyAnsweredRef.current.clear();
              locallyAnsweredRef.current.add(p.replyId);
              clearTimeout(pending.timer);
              if (p.error) {
                const err = p.error as { code?: string; message?: string };
                pending.reject(
                  new BusError(
                    (err.code as never) ?? 'internal',
                    err.message ?? 'widget command failed',
                  ),
                );
              } else {
                pending.resolve(p.result ?? null);
              }
            }
          } else if (typeof p.event === 'string') {
            // Convention: widgets broadcast declared events by emitting
            // `{ event: '<name>', data: ... }`. The bus subscribers
            // receive the inner `data` payload verbatim.
            const subs = eventSubsRef.current.get(p.event);
            if (subs) {
              for (const cb of subs) {
                try {
                  cb(p.data ?? null);
                } catch {
                  // Subscriber threw — drop the frame for that one.
                }
              }
            }
          }
        }
      }

      const response: WidgetResponse = await registry.dispatch(data, {
        agentId: null,
        userId: null,
        itemId,
        grantedResources: grantedResourcesRef.current,
        pushSink: (frame) => {
          iframe.contentWindow?.postMessage(frame, '*');
        },
      });
      iframe.contentWindow?.postMessage(response, '*');
    };

    window.addEventListener('message', handle);
    return () => {
      window.removeEventListener('message', handle);
      activeSubsRef.current.clear();
      // NOT the ask watchers (the e2e run of 2026-09-30, bug 1, high): this
      // cleanup also runs when the effect merely re-runs, and `currentUser`
      // used to get a new identity on every return to the tab (the auth
      // provider re-reads the session on `visibilitychange`). Clearing the
      // watchers here made the host forget which chats were listening, so
      // after one tab switch a streamed answer never reached the app and
      // showed only at the next poll or a Refresh. The app still holds its
      // subscription; the set is emptied on unmount, below.
      eventSubsRef.current.clear();
    };
  }, [registry, workspaceId, itemId, currentUser, requestFeaturePermission, enqueueAttributionSignals, trackFirstMeaningfulEdit, answerSandboxGrant]);

  // In-flight commands live as long as this bridge does, like the ask
  // watchers below: the iframe that will answer them does not go away when
  // the message effect merely re-runs, and the new listener resolves them
  // from the same ref (AGT-A7, 2026-11-01). Rejecting them on every re-run
  // told the agent "bridge remounted" 7 times in 24 prompts with no remount.
  useEffect(() => {
    const pending = pendingCmdsRef.current;
    return () => {
      // These commands were already POSTED to the widget, so the outcome is
      // genuinely UNKNOWN, not failed: a command that rebinds the document
      // (EDA `fileNew` / `fileOpenId`) remounts this bridge as its own side
      // effect and lands here having succeeded. Say what happened.
      for (const p of pending.values()) {
        clearTimeout(p.timer);
        p.reject(
          new BusError(
            'internal',
            'bridge remounted while the command was in flight — it was delivered to the widget and MAY have completed. Do not retry blindly: re-read state (__getState, or the widget\'s own status command) to see whether it took effect.',
          ),
        );
      }
      pending.clear();
    };
  }, []);

  // The ask watchers live as long as this bridge does (see the note in the
  // message effect's cleanup): emptied on unmount only.
  useEffect(() => {
    const watchers = askWatchersRef.current;
    return () => {
      watchers.clear();
    };
  }, []);

  // -----------------------------------------------------------------
  // Portal theme → iframe. The portal toggles dark mode by flipping the
  // `.dark` class on <html>; widgets can't observe the parent document,
  // so relay changes as a `theme` broadcast push (the SDK fans it out via
  // `host.onMessage`). Initial value rides the `__ready__` push.
  // -----------------------------------------------------------------
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    let last = hostTheme();
    const mo = new MutationObserver(() => {
      const next = hostTheme();
      if (next === last) return;
      last = next;
      iframeRef.current?.contentWindow?.postMessage(
        { id: '__broadcast__', push: { kind: 'theme', theme: next } },
        '*',
      );
    });
    mo.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => mo.disconnect();
  }, []);

  // -----------------------------------------------------------------
  // `arrival` (the Revent e2e run of 2026-09-26/27): a link that finds
  // THIS window already open is raised instead of spawning a twin, and
  // the fragment it carried (`#e=…&f=…`) has to reach the app, which read
  // `host.workspace.params` once at boot. The `spawn` URL action
  // dispatches `ws:item-arrival` naming the raised item; every bridge
  // hears it, only the named one acts, and it re-validates the params
  // against the arrival-fragment grammar before posting one
  // `{ kind: 'arrival', params }` broadcast into ITS OWN frame. A window
  // event is not a trusted channel and the frame is untrusted user-space
  // code, hence both the item check and the second validation
  // (lib/workspaces/arrival-push.ts holds the rules and the contract).
  // -----------------------------------------------------------------
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onArrival = (e: Event) => {
      let pushed: Record<string, string> | null = null;
      handleItemArrival((e as CustomEvent).detail, itemId, (frame) => {
        iframeRef.current?.contentWindow?.postMessage(frame, '*');
        pushed = frame.push.params as Record<string, string>;
      });
      // The raised window has the fragment now. When it runs the app the
      // page's link named, the hand-over is remembered for its later context
      // pushes and the address bar loses the fragment (S-378); on a page
      // whose link names no app the hash stays, as it always did.
      if (pushed) {
        captureFragmentTarget(window.location.search);
        if (fragmentHandedByArrival(fragmentIds(), pushed)) clearFragment(window);
      }
    };
    window.addEventListener(ITEM_ARRIVAL_EVENT, onArrival);
    return () => window.removeEventListener(ITEM_ARRIVAL_EVENT, onArrival);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  // -----------------------------------------------------------------
  // `agent.inbox.changed` (2026-09-24, the inbox inside Revent): a NUDGE,
  // not a feed. When a notice arrives for this person whose link opens THIS
  // app's row (lib/inbox/app-links.ts builds `/w/<catalog row>#inbox=...` for
  // every notice an app agent raises), tell the frame to re-read. Nothing of
  // the notice is passed on: the app asks `agent.inbox.list`, which the route
  // authorises afresh, so the count moves without polling and without the
  // bridge ever handing an app a notification body.
  // -----------------------------------------------------------------
  const { onUAMPEvent, on: onUAMP, isConnected: uampConnected } = useUAMP();
  const hasInbox = !!widgetType && INBOX_APPS.has(widgetType);
  useEffect(() => {
    if (!hasInbox) return;
    return onUAMPEvent((event: unknown) => {
      const e = event as { type?: unknown; notification?: { data?: unknown } } | null;
      if (!e || e.type !== 'notification.new') return;
      const data = (e.notification?.data ?? null) as { url?: unknown; appLink?: unknown } | null;
      const link = typeof data?.appLink === 'string' ? data.appLink : typeof data?.url === 'string' ? data.url : '';
      const row = appContentIdRef.current;
      // A notice about one of the app's OWN items opens the person's own
      // app through its entry link (`/?app=<id>#inbox=...`, lib/inbox/
      // app-links.ts, 2026-10-01), so that shape counts as this app's too.
      const entry = appEntryPath(widgetType);
      const ownRow = !!row && (link === `/w/${row}` || link.startsWith(`/w/${row}#`) || link.startsWith(`/w/${row}?`));
      const ownEntry = !!entry && (link === entry || link.startsWith(`${entry}#`));
      if (!ownRow && !ownEntry) return;
      iframeRef.current?.contentWindow?.postMessage({ id: '__broadcast__', push: { kind: 'agent.inbox.changed' } }, '*');
    });
  }, [hasInbox, onUAMPEvent, widgetType]);

  // -----------------------------------------------------------------
  // `agent.ask.watch` (2026-09-29, the round 8 dev run): the RELAY. The host
  // page's socket already receives every delta of the person's direct
  // message with their app agent (`broadcastToChat` sends to every
  // participant's connections). One `AskRelay` per mount turns those six
  // events into the four allowed frame shapes and fans them out to every
  // open watch. It filters on the chat id the routes answered, never on
  // anything the app said. Re-subscribes when the socket connects (`on` is a
  // no-op with no client), and tears the listeners down on unmount; the
  // watchers themselves are cleared with the other subscriptions below.
  // -----------------------------------------------------------------
  useEffect(() => {
    if (!hasInbox) return;
    const relay = new AskRelay((frame: AskPushFrame) => {
      const target = iframeRef.current?.contentWindow;
      if (!target) return;
      for (const subId of askWatchersRef.current) {
        try {
          target.postMessage({ id: subId, push: frame }, '*');
        } catch {
          /* one closed watcher must not stop the rest */
        }
      }
    });
    relay.setChatId(askChatIdRef.current);
    askRelayRef.current = relay;
    const offs = [
      onUAMP('response.delta', (ev) => relay.onDelta(ev)),
      onUAMP('response.done', (ev) => relay.onDone(ev)),
      onUAMP('response.cancelled', (ev) => relay.onCancelled(ev)),
      onUAMP('response.error', (ev) => relay.onError(ev)),
      onUAMP('chat:message', (m) => relay.onMessage(m)),
      onUAMP('chat:message_updated', (m) => relay.onMessageUpdated(m)),
    ];
    return () => {
      for (const off of offs) off();
      relay.dispose();
      if (askRelayRef.current === relay) askRelayRef.current = null;
    };
  }, [hasInbox, onUAMP, uampConnected]);

  // -----------------------------------------------------------------
  // Workspace bus → iframe.
  //
  //   - `kv` frames: dispatched to subscribed kv keys (legacy path).
  //   - `widget.event` / `widget` frames: forwarded into the iframe
  //     as `__broadcast__` push frames so widgets can react to
  //     events emitted by OTHER widgets (or by agents via
  //     `workspace_widgets_invoke`). Also delivers the response
  //     side of the agent-command convention: the broadcast push
  //     carries `{ kind: 'cmd', name, args, replyId, target }` /
  //     `{ kind: 'cmd.result', replyId, result, error }` and the
  //     SDK dispatches it via `host.onMessage` / `host.commands.handle`.
  //
  // Echo suppression: skip frames whose `origin` matches our own
  // tag (we already saw our own write locally).
  // -----------------------------------------------------------------
  useWorkspaceBus(
    {
      workspaceId,
      itemId,
      kinds: ['kv', 'widget.event', 'widget'],
      originTag,
    },
    (frame: WorkspaceBusFrame) => {
      // Always allow targeted broadcasts addressed to this item to
      // pass — even if the bus also delivers cross-item frames, the
      // `itemId` filter in `useWorkspaceBus` keeps them scoped here.

      if (frame.kind === 'kv') {
        const key = frame.key;
        if (typeof key !== 'string') return;
        const subs = activeSubsRef.current;
        subs.forEach((entry) => {
          if (entry.key === key) entry.push({ value: frame.value });
        });
        return;
      }

      // `widget.event` / `widget` — forward verbatim into the iframe
      // as a broadcast push. The SDK fans these out to its
      // `host.onMessage` listeners + `host.commands.handle` registry.
      const iframe = iframeRef.current;
      if (!iframe) return;
      iframe.contentWindow?.postMessage(
        {
          id: '__broadcast__',
          push: {
            kind: frame.kind === 'widget' ? 'widget.event' : frame.kind,
            payload: frame.payload ?? null,
            origin: frame.origin ?? null,
            sourceItemId: frame.itemId,
          },
        },
        '*',
      );
    },
  );

  // First-party (in-house widgets): minimal sandbox + opaque origin.
  // External (third-party iframe-embeds): broader sandbox so YouTube
  // / PlayCanvas / Figma / CodePen / etc. can do their normal thing
  // on THEIR own origin. `allow-same-origin` here is safe because
  // the iframe's src points at a different origin from ours — it
  // grants the embed access to ITS OWN cookies / storage / IndexedDB,
  // not ours. Without it, YouTube's player init fails (it expects
  // storage access for player state), Figma's session check
  // 302-redirects to a login wall (no cookies → no session), and
  // many WebGL apps (PlayCanvas, Three.js) fail to bootstrap because
  // their loaders write to localStorage.
  //
  // The danger pattern is `allow-same-origin` + `srcdoc` (which
  // inherits the embedder's origin); we never use that combination
  // here — `srcdoc` paths render under the sandbox-host origin in
  // the double-iframe shell (see widget-sandbox routes).
  // Capability widgets (mic/camera) need a NON-opaque origin: `getUserMedia`
  // refuses opaque origins with "Invalid security origin" even when the
  // Permissions-Policy `allow=` grants the modality. So a first-party widget
  // that declares a device capability via the registry `allow` (set ONLY on
  // platform/system widgets — never on community widgets, whose allow is
  // parsed from their own meta tag) gets `allow-same-origin`.
  //
  // Multi-tenant-safe deployment serves these from `SANDBOX_ORIGIN` (a
  // different eTLD+1, so same-origin grants the widget access to the SANDBOX
  // origin's storage, not the apex's). When no sandbox origin is configured
  // (local dev), the widget runs same-origin on the apex — acceptable only
  // because these are first-party, in-house widgets (our own code), gated
  // strictly on `widgetSpec.allow`.
  // First-party widgets that need a NON-OPAQUE origin:
  //   - capability widgets (mic/camera — `getUserMedia` refuses opaque origins);
  //   - COLLAB widgets (`widgetSpec.collab` — collab-board / robongus). Their
  //     CDN-loaded runtimes (React + @xyflow/react, Yjs) and interactivity expect
  //     real storage + a defined origin; an opaque sandbox also can't be driven
  //     (synthetic input / cross-frame access) and trips libs that touch
  //     localStorage. Same first-party trust basis as capability widgets: in the
  //     multi-tenant deploy they're served from SANDBOX_ORIGIN (isolated from the
  //     apex); in local dev (no SANDBOX_ORIGIN) they run same-origin on the apex.
  const needsNonOpaqueOrigin = !external && (!!widgetSpec?.allow || !!widgetSpec?.collab);
  // `allow-downloads` is granted to EVERY tier: widgets legitimately export
  // files (vector-editor PDFs/PNGs, board exports) via blob-URL anchors, and
  // without the token the click is SILENTLY dropped. A download still goes
  // through the browser's normal download UI/prompts — no script capability.
  // `allow-pointer-lock` is what actually enables mouselook — pointer lock in a
  // sandboxed iframe is gated by THIS token, not by Permissions-Policy (there is
  // no `pointer-lock` allow= directive), so a game declaring it in its manifest
  // still gets `requestPointerLock()` rejected without it. Granted to the trusted
  // tiers only; the browser still requires a user gesture and Esc always releases.
  // NOTE on window.print()/confirm() for bundle-served FIRST-PARTY widgets:
  // this external tier deliberately has NO `allow-modals` (third-party embeds
  // could phish with alert/confirm — see tests/security/widget-iframe-sandbox),
  // and nested sandboxes intersect, so the inner sandbox-route grant alone
  // cannot enable them. First-party print handles this with a POPUP fallback
  // (docs printFlow: a blocked print() fires no beforeprint → print from a
  // window.open'd document; popups DO escape via the tokens below).
  const sandboxAttr = external
    ? 'allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-forms allow-downloads allow-pointer-lock'
    : needsNonOpaqueOrigin
      // First-party capability/collab widgets (our own code) may open external links in a
      // new tab — anchor target=_blank / window.open need allow-popups(-to-escape-sandbox),
      // since the bridge's ui/open-link RPC has no host-side handler. Same popup grant
      // the `external` branch already gets; these are trusted in-house widgets.
      // `allow-modals` lets them use window.confirm/alert/prompt (e.g. the editors'
      // "New / discard changes?" guards) — without it the browser silently IGNORES
      // the call and the confirm-gated action never runs.
      ? 'allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-downloads allow-modals allow-pointer-lock'
      : 'allow-scripts allow-downloads';

  // The workspace theme, handed to the sandbox shell in the mount URL's
  // fragment so the widget's document can paint in the right colours on its
  // FIRST frame. The `__ready__` push carries the same value, but it lands
  // after the widget's head has run, so an app that guessed from the OS
  // scheme showed one dark frame in a light workspace before correcting
  // itself. Captured once at mount and never recomputed: a later toggle
  // rides the live `theme` broadcast, and changing this would reload the
  // iframe under the person. Only our own sandbox/bundle mounts get it;
  // third-party embed URLs are left exactly as given.
  const mountTheme = useRef<'light' | 'dark' | null>(null);
  if (mountTheme.current === null) mountTheme.current = hostTheme();
  // A genuinely foreign document: an `iframe-external` mount that is NOT one
  // of our sandbox shells (see the `credentialless` note on the iframe).
  const thirdPartyFrame = external && !/\/widget-sandbox\//.test(src);
  const srcWithTheme = useMemo(() => {
    if (!/\/widget-sandbox\/|^\/widgets\//.test(src)) return src;
    // Nothing but the theme is added to a mount URL. For one day (2026-09-22)
    // this appended an isolation parameter when the page was isolated,
    // because the shells then sent COEP only on request; the shells send it
    // on every response again (ADR-0044) and read nothing from their query
    // string, so the mount URL is the entry as resolved, and the `/w` page,
    // which server-renders this iframe, hydrates to the same `src` for free.
    return `${src}${src.includes('#') ? '&' : '#'}theme=${mountTheme.current}`;
  }, [src]);

  // The iframe is rendered once; the bridge state is keyed on its
  // contentWindow identity so the only way to "reset" is to remount
  // the component (typically by changing `src`).
  return (
    <iframe
      // Permissions-Policy is load-time, so re-key on the grant
      // signature: granting / revoking a feature remounts the iframe
      // with the new `allow=` (the widget then retries the feature).
      // Also re-key on the connected chat so switching the agent node's
      // current chat REMOUNTS the widget onto the new thread (the voice
      // session reads currentChatId only at mount).
      key={`${src}::${grantSig}::${connectedChatId ?? ''}`}
      ref={iframeRef}
      src={srcWithTheme}
      title={title ?? 'Widget'}
      sandbox={sandboxAttr}
      // Permissions-Policy: baseline (fullscreen / autoplay /
      // clipboard-write / encrypted-media / picture-in-picture — always
      // delegated so YouTube full-screen, PlayCanvas autoplay, Figma
      // copy-paste work without plumbing) ∪ first-party registry
      // `allow` ∪ the user-granted sensitive features for THIS instance
      // (`state.permissions`). Sensitive features (camera, mic, geo,
      // xr-spatial-tracking, sensors, …) are default-OFF and only ever
      // appear here after the user grants them — see
      // lib/workspaces/widget-permissions.ts.
      allow={allowValue}
      // A THIRD-PARTY document under the isolated canvas (ADR-0044; every
      // page that hosts apps is one). The page is `require-corp`, and a
      // third-party origin never sends COEP, so the frame would be refused
      // outright. A note here once said the attribute was gone for good
      // because the workspace had dropped COEP to keep signed-in SaaS embeds;
      // ADR-0044 brought both back. `credentialless` is the one escape
      // hatch the platform offers: the document loads in a fresh, cookie-less
      // partition, so it renders but signed out (a private Figma file or a
      // signed-in Notion page will not authenticate). Chromium only; other
      // engines ignore the attribute and refuse the frame.
      //
      // NEVER ON OUR OWN SHELLS, and `external` alone cannot tell them apart:
      // every folder bundle resolves as `iframe-external` because the sandbox
      // origin is a different origin. A credentialless subtree gets a FRESH
      // storage partition per top-level document (measured 2026-09-21: the
      // shell's localStorage stamp changed on every reload with the attribute
      // and persisted without it), which would reset every app's device-local
      // state on each reload. The shells send COEP themselves and need no
      // escape hatch. React passes the unknown attribute through; the spread
      // keeps the JSX typings happy.
      {...(thirdPartyFrame ? CREDENTIALLESS_ATTR : {})}
      data-external={external ? 'true' : undefined}
      onLoad={() => {
        // Proactively hand the widget its workspace context on load,
        // in ADDITION to replying to the SDK's `__ready__` request. The
        // request/reply handshake can race (the SDK posts `__ready__`
        // before our message listener is attached, or while the iframe
        // ref is momentarily null on a remount) — a lost reply hangs the
        // widget's `await host.ready()` forever and it renders blank. The
        // SDK consumes this push through its `__ready__` subscription
        // (set at SDK init, before this fires), so pushing here closes
        // the race even for an older cached SDK that lacks retry.
        const win = iframeRef.current?.contentWindow;
        if (!win) return;
        setWidgetLoadState(itemId, 'ready');
        setAppUnsaved(unsavedInstance.current, false); // [AUTOSAVE-2] a frame that loaded again holds nothing yet
        win.postMessage({ id: '__ready__', push: buildReadyPushRef.current() }, '*');
      }}
      className="nowheel h-full w-full border-0 bg-background"
    />
  );
}
